/* eslint-disable no-restricted-globals */
/* global email, approver, nominatorRole, name */
// src/pages/OnboardingEmpSupplierAdmin.jsx
//import React, { useEffect, useState } from "react";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Onboarding.css";
import DatePicker from "react-datepicker";
import { offset, flip, shift } from "@floating-ui/dom";
import "react-datepicker/dist/react-datepicker.css";
import { Country, State, City } from "country-state-city";
import countries from "i18n-iso-countries";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ENV, tbGetJson, tbPostJson, getJson } from "../api/client";
import { setDashboardToast } from "../utils/dashboardToast";
import {
  getCpErrorMessages,
  hasApiValidationFailure as hasCpValidationFailure,
} from "../utils/cpValidationErrors";
import { validateSecurityIdMmdd8 } from "../utils/securityId";
import {
  getLookupSecurityExistenceFromPayload,
  normalizeLookupSecurityExistence,
} from "../utils/lookupSecurity";

import {
  blockingLoadsForContinue,
  dataLoadToastId,
  loadFailMessage,
  loadOngoingMessage,
  loadTimeoutMessage,
} from "../utils/onboardingDataLoadGate";
import { useDataLoadTimeoutToasts } from "../hooks/useDataLoadTimeoutToasts";
import { useDataLoadErrorToasts } from "../hooks/useDataLoadErrorToasts";

const truthy = (v) =>
  v === true || v === 1 || v === "1" || v === "true" || v === "t" || v === "T";

const generateTbFallbackDtvid = () => {
  const cryptoObj = typeof window !== "undefined" ? window.crypto : null;
  if (cryptoObj?.getRandomValues) {
    const values = new Uint32Array(1);
    cryptoObj.getRandomValues(values);
    return `TB-${String(values[0] % 10000).padStart(4, "0")}`;
  }
  return `TB-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`;
};

const FIRST_LAST_NAME_REGEX = /^(?=[a-zA-Z])[a-zA-Z.\-' ]{2,50}$/;

/* ------------------------------------------------------------------ */
/*  SESSION HELPERS (fresh reads + guard)                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  LEGACY SHIMS (to avoid undefined vars in older JSX usage)          */
/*  NOTE: For display-only usage. For actions, use getFreshSession().  */
/* ------------------------------------------------------------------ */
(function () {
  try {
    const __SS = typeof sessionStorage !== "undefined" ? sessionStorage : null;
    const __name = __SS ? __SS.getItem("tb_name") || "" : "";
    const __email = __SS ? __SS.getItem("tb_email") || "" : "";
    const __role = __SS ? __SS.getItem("tb_role") || "" : "";
    const __approver = __SS ? __SS.getItem("approver") || "" : "";
    // Expose legacy vars for display-only usage
    // eslint-disable-next-line no-unused-vars
    var name = __name;
    // eslint-disable-next-line no-unused-vars
    var email = __email;
    // eslint-disable-next-line no-unused-vars
    var nominatorRole = __role;
    // eslint-disable-next-line no-unused-vars
    var approver = __approver;
    // Also set window-scoped globals for legacy references
    if (typeof window !== "undefined") {
      window.name = __name;
      window.email = __email;
      window.nominatorRole = __role;
      window.approver = __approver;
    }
  } catch (e) {
    /* ignore */
  }
})();

const SS = sessionStorage;
function formatRoleLabel(roleRaw) {
  const raw = String(roleRaw ?? "").trim();
  if (!raw) return "Employee Supplier Admin";

  const key = raw
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .trim();

  const map = {
    "employee supplier admin": "Employee Supplier Admin",
    "employee sponsor": "Employee Sponsor",
    "cp channel delegate": "CP Channel Delegate",
    "fte nominator": "FTE Nominator",
    "dtv ops admin": "DTV Ops Admin",
    nominator: "Nominator",
  };
  if (map[key]) return map[key];

  return key
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
function getFreshSession() {
  const name = SS.getItem("tb_name") || "";
  const email = SS.getItem("tb_email") || "";
  const role = SS.getItem("tb_role") || "";
  const dtvid = SS.getItem("tb_dtvid") || "";
  const submitterId = email;
  const approver = SS.getItem("approver") || "";
  return { name, email, dtvid, role, submitterId, approver };
}
function ensureSessionOrReauth() {
  const { name, role, submitterId } = getFreshSession();
  if (!name || !role || !submitterId) {
    try {
      [
        "tb_login_kicked",
        "tb_name",
        "tb_email",
        "tb_role",
        "tb_vendorId",
        "tb_dtvid",
        "tb_personType",
        "tb_dtvRoleName",
        "tb_supplierName",
        "tb_directReports",
        "tb_isFte",
        "tb_hinge_person_list",
        "tb_login_at",
        "tb_last_activity",
        "tb_lookup",
        "tb_lookup_at",
        "tb_lookup_ver",
      ].forEach((k) => SS.removeItem(k));
    } catch {}
    if (typeof window !== "undefined") {
      // hard-redirect; RequireAuth will pick up and drive SSO
      window.location.href = "/unauthorized";
    }
    return false;
  }
  return true;
}

/* ------------------------------------------------------------------ */
/*  USER SESSION                                                       */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  BASE URL HELPERS (ENV ONLY)                                       */
/* ------------------------------------------------------------------ */

const TB_BASE = (ENV.TB_BASE || "").replace(/\/+$/, "");

// Onboarding create / edit
const ONBOARDING_NEW_URL =
  ENV.ONBOARDING_NEW_URL ||
  (TB_BASE && `${TB_BASE}/api/npw-onboarding-new`) ||
  "";

const ONBOARDING_EDIT_URL =
  ENV.ONBOARDING_EDIT_URL ||
  (TB_BASE && `${TB_BASE}/api/npw-onboarding-edit`) ||
  "";
const CP_VALIDATE_URL =
  ENV.CP_VALIDATE_URL ||
  ENV.CINGLEPOINT_URL ||
  ENV.NPW_CP_VALIDATE_URL ||
  ENV.ONBOARDING_CP_VALIDATE_URL ||
  (TB_BASE && `${TB_BASE}/api/v1/cinglepoint`) ||
  "" ||
  "";

// Approval details (load by npw_id – draft)
const APPROVAL_DETAILS_URL =
  ENV.APPROVAL_DETAILS_URL ||
  (TB_BASE && `${TB_BASE}/api/npw-approval-details`) ||
  "";

// TB search endpoint (used for npwId editLoadNewOnboard)
const TB_SEARCH_URL =
  ENV.TB_SEARCH_URL || (TB_BASE && `${TB_BASE}/api/search`) || "";

// TB ↔ Hinge Search (used for Security ID existence check; same as Search page)
const TB_HINGE_SEARCH_URL = (
  ENV.TB_HINGE_SEARCH_URL ||
  ENV.TB_HINGE_PERSON_SEARCH_URL ||
  ENV.TB_HINGE_SEARCH_PROXY_URL ||
  ""
).replace(/\/+$/, "");

// CP Country of Citizenship list (from DB via cp-countrylist lambda)
const CP_COUNTRYLIST_URL = (
  ENV.CP_COUNTRYLIST_URL ||
  ENV.CP_COUNTRYLIST_API_URL ||
  "https://gbc0othivl.execute-api.us-west-2.amazonaws.com/test/api/cp-countrylist"
).replace(/\/+$/, "");

// S2P Supplier validation endpoint
const SUPPLIER_VALIDATION_URL = (
  ENV.SUPPLIER_VALIDATION_URL ||
  "https://4ax3gccsbj.execute-api.us-west-2.amazonaws.com/dev/api/npw-s2p-supplier-validation"
).replace(/\/+$/, "");

// ---- small helper to coerce env flags into booleans ----
const boolFromEnv = (v) =>
  v === true || v === "true" || v === "1" || v === 1 || v === "Y" || v === "y";

// Hinge validation + roles (all flag driven)
const ENABLE_SPONSOR_VALIDATION = boolFromEnv(ENV.ENABLE_SPONSOR_VALIDATION);
const ENABLE_WEBPHONE_VALIDATION = boolFromEnv(ENV.ENABLE_WEBPHONE_VALIDATION);
const ENABLE_HINGE_ROLE_DROPDOWN = boolFromEnv(ENV.ENABLE_HINGE_ROLE_DROPDOWN);
const IS_VALIDATE_CP_NEEDED =
  ENV.IS_VALIDATE_CP_NEEDED === undefined ||
  ENV.IS_VALIDATE_CP_NEEDED === null ||
  ENV.IS_VALIDATE_CP_NEEDED === ""
    ? true
    : boolFromEnv(ENV.IS_VALIDATE_CP_NEEDED);

// Role catalog (Hinge)
const HINGE_ROLE_LIST_URL = (
  ENV.HINGE_ROLE_LIST_URL ||
  "https://api-hingedev-directv.com/api/v1/roleCatalog"
).replace(/\/+$/, "");

// Sponsor & Webphone validation (Hinge)
const HINGE_SPONSOR_VALIDATE_URL = (
  ENV.HINGE_SPONSOR_VALIDATE_URL ||
  "https://api-hingedev-directv.com/api/v1/validation"
).replace(/\/+$/, "");
const HINGE_WEBPHONE_VALIDATE_URL = (
  ENV.HINGE_WEBPHONE_VALIDATE_URL ||
  "https://api-hingedev-directv.com/api/v1/validation"
).replace(/\/+$/, "");

// --- TB-Hinge proxy endpoints (prefer TB_* env; fall back to direct Hinge) ---
const TB_HINGE_ROLE_LIST_URL = (
  ENV.TB_HINGE_ROLE_LIST_URL ||
  ENV.TB_ROLE_CATALOG_URL ||
  ENV.TB_HINGE_ROLE_CATALOG_URL ||
  HINGE_ROLE_LIST_URL
).replace(/\/+$/, "");

const TB_HINGE_SPONSOR_VALIDATE_URL = (
  ENV.TB_HINGE_SPONSOR_VALIDATE_URL ||
  ENV.TB_HINGE_VALIDATE_URL ||
  HINGE_SPONSOR_VALIDATE_URL
).replace(/\/+$/, "");

const TB_HINGE_WEBPHONE_VALIDATE_URL = (
  ENV.TB_HINGE_WEBPHONE_VALIDATE_URL ||
  ENV.TB_HINGE_VALIDATE_URL ||
  HINGE_WEBPHONE_VALIDATE_URL
).replace(/\/+$/, "");
const WEBPHONE_MANAGER_CACHE_KEY = "tb_webphone_manager_validation_v1";
const normalizeManagerCacheKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const getManagerValidationPayload = (resp) => {
  if (!resp || typeof resp !== "object") return resp;
  return resp.response && typeof resp.response === "object"
    ? resp.response
    : resp.data && typeof resp.data === "object"
      ? resp.data
      : resp;
};
const getManagerAttuidFromResp = (resp) => {
  const r = getManagerValidationPayload(resp);
  return String(r?.attuid ?? r?.attUid ?? r?.attUID ?? "").trim();
};
const getManagerIsValidFromResp = (resp) => {
  const r = getManagerValidationPayload(resp);
  const raw = r?.isValid ?? r?.isvalid ?? r?.valid;
  const normalized = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (
    raw === true ||
    raw === 1 ||
    ["true", "t", "yes", "y", "1"].includes(normalized)
  );
};
const getManagerAttuidStatusRawFromResp = (resp) => {
  const r = getManagerValidationPayload(resp);
  return (
    r?.attuidStatus ??
    r?.attuidstatus ??
    r?.attuid_status ??
    r?.attUidStatus ??
    r?.attUIDStatus ??
    r?.attuidActive ??
    r?.attuid_active ??
    ""
  );
};
const isManagerAttuidActiveForAttuidFlow = (resp) => {
  const attuid = getManagerAttuidFromResp(resp);
  if (!attuid || ["false", "null", "undefined"].includes(attuid.toLowerCase()))
    return false;
  const rawStatus = getManagerAttuidStatusRawFromResp(resp);
  if (rawStatus === true || rawStatus === 1) return true;
  const status = String(rawStatus ?? "")
    .trim()
    .toLowerCase();
  return ["active", "true", "t", "yes", "y", "1"].includes(status);
};
const WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG =
  "Webphone Manager ATTUID is Inactive for ATTUID flows, please check";
const readWebphoneManagerCache = (managerId) => {
  const key = normalizeManagerCacheKey(managerId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed?.[key] || null;
  } catch {
    return null;
  }
};
const writeWebphoneManagerCache = (managerId, response) => {
  const key = normalizeManagerCacheKey(managerId);
  if (!key) return;
  try {
    const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === "object" ? parsed : {};
    next[key] = { response };
    sessionStorage.setItem(WEBPHONE_MANAGER_CACHE_KEY, JSON.stringify(next));
  } catch {
    // ignore cache failures
  }
};

// Hinge: pre-load by DTV ID (for new onboarding)
const ENABLE_DTV_PREFILL = boolFromEnv(ENV.ENABLE_DTV_PREFILL);
const HINGE_DTV_LOOKUP_URL = (ENV.TB_HINGE_LOOKUP_URL || "").replace(
  /\/+$/,
  "",
);

const TB_HINGE_DTV_LOOKUP_URL = (
  ENV.TB_HINGE_LOOKUP_URL ||
  ENV.TB_HINGE_PERSON_LOOKUP_URL ||
  HINGE_DTV_LOOKUP_URL
).replace(/\/+$/, "");

/* ------------------------------------------------------------------ */
/*  STATIC DROPDOWNS (no /onboarding/dropdowns API)                   */
/* ------------------------------------------------------------------ */

const STATIC_DROPDOWNS = {
  validateSecurity: ["Yes", "No"],
  nonBillable: ["Yes", "No"],
  occupancyCode: ["virtual", "office"],
  workerTypeTax: ["W-2", "1099", "International"], //["W2 / 1099", "International"],
  workerPrimaryContact: ["Father", "Mother", "Spouse"],
  roles: ["Please Wait ... "],
  //roles: ["Developer", "Analyst", "Project Manager", "QA Engineer"],
  sites: ["Dallas", "Los Angeles", "New York", "Remote"],
  locations: ["TX-DAL-01", "CA-LA-03", "NY-NYC-02", "Remote"],
  npwTypes: ["Contractor", "Consultant", "Intern", "Vendor Staff"],
};

/* ------------------------------------------------------------------ */
/*  TIME ZONES                                                        */
/* ------------------------------------------------------------------ */

const getTimeZones = () => {
  const timeZones = Intl.supportedValuesOf("timeZone");
  const now = new Date();
  return timeZones.map((tz) => {
    const offset = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
      hour: "2-digit",
      minute: "2-digit",
    })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value;
    return { label: `(UTC${offset}) ${tz}`, value: tz };
  });
};
const timeZoneOptions = getTimeZones();

/* Non-mandatory fields list (for *) */
const NonMandatory = [
  "displayWorkforceToBuyer",
  "upload",
  "workstation",
  "billRate",
  "boltApproval",
  "username",
  "supplierReference",
  "autoRegister",
  "autoInvoiceAll",
  "timeSheetType",
];

const FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Email",
  phone: "Phone",
  citizenship: "Country of Citizenship",
  sponsor: "DTV Sponsor DTVUID (Owns portal record)",
  webphoneManagerId: "Webphone Manager DTVUID (HR line manager)",
  role: "Role",
  country: "Country",
  state: "State",
  city: "City",
  timeZone: "Time Zone",
  occupancyCode: "Occupancy",
  securityId: "Security ID",
  validateSecurity: "Confirm Security ID",
  startDate: "Start Date",
  endDate: "End Date",
  workerTypeTax: "W2 / 1099 or International",
  nonBillable: "Billable",
  attRoleKey: "ATT Role",
  vendor: "Vendor Name",
  supplierReference: "Supplier Reference",
};
const requiredError = (name) => {
  const label = FIELD_LABELS[name] || name;
  return `Please enter ${label}`;
};

const invalidDropdownValueError = (name) => {
  const label = FIELD_LABELS[name] || name;
  return `Please select a valid ${label}`;
};

const DTVUID_MAX_LEN = 10;
const CONTRACT_FIELDS_MAX_LEN = 50;

/* ------------------------------------------------------------------ */
/*  API HELPERS (TB with auth headers)                               */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  OPTIONAL: wrap handlers (if used)                                  */
/* ------------------------------------------------------------------ */
function guardThen(fn) {
  return (...args) => {
    if (!ensureSessionOrReauth()) return;
    const { name, role, submitterId } = getFreshSession();
    try {
      return fn(...args, { name, role, submitterId });
    } catch (e) {
      throw e;
    }
  };
}

async function apiSaveDraft(payload, editDataOrIsEdit) {
  if (!ensureSessionOrReauth()) throw new Error("reauth-required");
  const { name, role, submitterId, dtvid } = getFreshSession();
  payload = payload || {};

  // Ensure identity fields are always present
  if (!payload.submitter_name) payload.submitter_name = name;
  if (!payload.submitter_id) payload.submitter_id = submitterId;
  if (!payload.submitterDtvid) payload.submitterDtvid = dtvid;
  if (!payload.loggedInUserRole) payload.loggedInUserRole = role;

  // Treat as EDIT if caller indicates so OR if payload has an npw id
  const hasNpw =
    !!editDataOrIsEdit ||
    !!payload?.npw_id ||
    !!payload?.npwId ||
    !!payload?.form1?.npw_id ||
    !!payload?.form1?.npwId;

  const url = hasNpw ? ONBOARDING_EDIT_URL : ONBOARDING_NEW_URL;
  if (!url) throw new Error("ONBOARDING URL not configured");
  const extra = hasNpw ? { fetchInit: { method: "POST" } } : {}; // EDIT = PUT/POST, NEW = POST
  return tbPostJson(url, payload, extra);
}
async function apiSubmit(payload, editDataOrIsEdit) {
  if (!ensureSessionOrReauth()) throw new Error("reauth-required");
  const { name, role, submitterId, dtvid } = getFreshSession();
  payload = payload || {};

  // Ensure identity fields are always present
  if (!payload.submitter_name) payload.submitter_name = name;
  if (!payload.submitter_id) payload.submitter_id = submitterId;
  if (!payload.submitterDtvid) payload.submitterDtvid = dtvid;
  if (!payload.loggedInUserRole) payload.loggedInUserRole = role;

  // Treat as EDIT if caller indicates so OR if payload has an npw id
  const hasNpw =
    !!editDataOrIsEdit ||
    !!payload?.npw_id ||
    !!payload?.npwId ||
    !!payload?.form1?.npw_id ||
    !!payload?.form1?.npwId;

  const url = hasNpw ? ONBOARDING_EDIT_URL : ONBOARDING_NEW_URL;
  if (!url) throw new Error("ONBOARDING URL not configured");
  const extra = hasNpw ? { fetchInit: { method: "POST" } } : {}; // EDIT = PUT/POST, NEW = POST
  return tbPostJson(url, payload, extra);
}
async function apiValidateCp(payload) {
  if (!ensureSessionOrReauth()) throw new Error("reauth-required");
  if (!CP_VALIDATE_URL) throw new Error("CP_VALIDATE_URL not configured");
  return tbPostJson(CP_VALIDATE_URL, payload, {
    fetchInit: { method: "POST" },
  });
}

async function apiFetchOnboardingById(id) {
  /*TODO:pentest*/ // Build v query
  const dtvidRaw = sessionStorage.getItem("tb_dtvid") || "";
  const roleRaw =
    sessionStorage.getItem("tb_role") ||
    sessionStorage.getItem("tb_role_raw") ||
    "";
  const ts = Date.now();
  const vRaw = `${dtvidRaw}_*v*_${ts}`;
  const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);

  if (!APPROVAL_DETAILS_URL)
    throw new Error("APPROVAL_DETAILS_URL not configured");
  const base = APPROVAL_DETAILS_URL;
  const sep = base.includes("?") ? "&" : "?";
  /*TODO:pentest*/ const url = `${base}${sep}npw_id=${encodeURIComponent(id)}&role=${encodeURIComponent(roleRaw)}&v=${encodeURIComponent(vParam)}&_=${Date.now()}`;
  // const url = `${base}${sep}npw_id=${encodeURIComponent(id)}`;
  return tbGetJson(url);
}

/* ------------------------------------------------------------------ */
/*  MAPPING HELPERS                                                   */
/* ------------------------------------------------------------------ */

const fmtDate = (date) => {
  let d = date instanceof Date ? date : new Date(date);
  return d instanceof Date && !isNaN(+d)
    ? d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : "";
};

// Serialize Date → YYYY-MM-DD (local date, no timezone jump)
const serializeDate = (d) => {
  if (!d) return null;
  const parsed = d instanceof Date ? d : new Date(d);
  if (!(parsed instanceof Date) || Number.isNaN(+parsed)) return null;
  const dt = new Date(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
  );
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const collectApiMessages = (value, out = [], depth = 0) => {
  if (depth > 4 || value == null) return out;
  if (typeof value === "string") {
    const msg = value.trim();
    if (msg) out.push(msg);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectApiMessages(item, out, depth + 1));
    return out;
  }
  if (typeof value === "object") {
    const keys = [
      "message",
      "error",
      "errorMessage",
      "detail",
      "reason",
      "errors",
      "validationErrors",
      "fieldErrors",
      "results",
      "body",
      "agentBulkResults",
      "messages",
    ];
    keys.forEach((k) => collectApiMessages(value?.[k], out, depth + 1));
  }
  return out;
};

const hasApiValidationFailure = (resp) => {
  return hasCpValidationFailure(resp);
};

const firstNonEmpty = (...vals) => {
  for (const val of vals) {
    const text = String(val ?? "").trim();
    if (
      text &&
      text.toLowerCase() !== "null" &&
      text.toLowerCase() !== "undefined"
    )
      return text;
  }
  return "";
};

const HINGE_SSN_MIN_DIGITS = Number(ENV.HINGE_SSN_MIN_DIGITS || 8);
const HINGE_SSN_DEBOUNCE_MS = Number(ENV.HINGE_SSN_DEBOUNCE_MS || 2000);

/* =========================
   SECURITY ID MASKING RULES
   ========================= */

const EyeIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const USA_CITIZENSHIP_LABEL = "United States of America (USA) Citizen";

const isUnitedStatesSelected = (countryLabel = "") => {
  const c = String(countryLabel || "")
    .trim()
    .toLowerCase();
  return (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c === USA_CITIZENSHIP_LABEL.toLowerCase()
  );
};

const COUNTRY_CODE_ALIASES = { UK: "GB" };

// Common city-name abbreviations (e.g. API sends "MT DORA", dataset has "Mount Dora").
const CITY_ABBREVIATIONS = {
  mt: "mount",
  mtn: "mountain",
  ft: "fort",
  st: "saint",
  "n.": "north",
  "s.": "south",
  "e.": "east",
  "w.": "west",
};

const canonicalizeCountryText = (value = "") =>
  String(value || "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toLowerCase();

const getParentheticalCountryCode = (value = "") => {
  const match = String(value || "").match(/\(([A-Za-z]{2,3})\)/);
  return match ? match[1].toUpperCase() : "";
};

const getTrailingCountryCode = (value = "") => {
  const match = String(value || "").match(/[-_\s]([A-Za-z]{2,3})$/);
  return match ? match[1].toUpperCase() : "";
};

const normalizeCountryName = (country = "") => {
  const rawCountry = String(country || "").trim();
  if (!rawCountry) return "";
  if (isUnitedStatesSelected(rawCountry)) return "United States";
  if (rawCountry.toLowerCase() === USA_CITIZENSHIP_LABEL.toLowerCase())
    return "United States";

  const upper = rawCountry.toUpperCase();
  const parenCode = getParentheticalCountryCode(rawCountry);
  const trailingCode = getTrailingCountryCode(rawCountry);
  const countryCode =
    COUNTRY_CODE_ALIASES[parenCode] ||
    countries.alpha3ToAlpha2(parenCode) ||
    parenCode ||
    COUNTRY_CODE_ALIASES[trailingCode] ||
    countries.alpha3ToAlpha2(trailingCode) ||
    trailingCode ||
    COUNTRY_CODE_ALIASES[upper] ||
    countries.alpha3ToAlpha2(upper) ||
    upper;
  const canonical = canonicalizeCountryText(rawCountry);
  const matchedCountry =
    Country.getCountryByCode(countryCode) ||
    Country.getAllCountries().find(
      (item) => item.name.toLowerCase() === rawCountry.toLowerCase(),
    ) ||
    Country.getAllCountries().find(
      (item) => canonicalizeCountryText(item.name) === canonical,
    );

  return matchedCountry?.name || "";
};

const normalizeWorkingCountryName = normalizeCountryName;

const getCountryOption = (options, country) => {
  const countryName = normalizeCountryName(country);
  const countryCode = String(country || "")
    .trim()
    .toUpperCase();
  return (options || []).find(
    (option) =>
      option.label.toLowerCase() === countryName.toLowerCase() ||
      option.value.toUpperCase() === countryCode,
  );
};

const getCitizenshipOption = (options, citizenship) => {
  const rawCitizenship = String(citizenship || "").trim();
  if (!rawCitizenship) return undefined;
  const countryName = normalizeCountryName(rawCitizenship);
  const exact = (options || []).find((option) => {
    const label = String(option.label || "")
      .trim()
      .toLowerCase();
    const value = String(option.value || "")
      .trim()
      .toLowerCase();
    return (
      label === rawCitizenship.toLowerCase() ||
      value === rawCitizenship.toLowerCase() ||
      (countryName &&
        (label === countryName.toLowerCase() ||
          value === countryName.toLowerCase()))
    );
  });
  if (exact) return exact;

  const rawCanonical = canonicalizeCountryText(rawCitizenship);
  const countryCanonical = canonicalizeCountryText(countryName);
  return (options || []).find((option) => {
    const label = canonicalizeCountryText(option.label);
    const value = canonicalizeCountryText(option.value);
    return (
      (rawCanonical && (label === rawCanonical || value === rawCanonical)) ||
      (countryCanonical &&
        (label === countryCanonical || value === countryCanonical))
    );
  });
};

const CP_CITIZENSHIP_LABELS = [
  USA_CITIZENSHIP_LABEL,
  "United States of America (USA) Lawful Permanent Resident",
  "United States of America (USA) Legally Admitted Asylee or Refugee",
];

const resolveCitizenshipDisplay = (citizenship, citizenshipOpts) => {
  const raw = String(citizenship || "").trim();
  if (!raw) return "";
  if (raw.toUpperCase() === "USA") return USA_CITIZENSHIP_LABEL;
  const opt = getCitizenshipOption(citizenshipOpts, raw);
  if (opt) return opt.label || raw;
  const knownLabel = CP_CITIZENSHIP_LABELS.find(
    (label) => label.toLowerCase() === raw.toLowerCase(),
  );
  if (knownLabel) return knownLabel;
  return normalizeCountryName(raw);
};

// For US: ___-__-____ (9 digits)
const US_TEMPLATE = "   -  -    ";
const US_DIGIT_POS = [0, 1, 2, 4, 5, 7, 8, 9, 10];

// For Non-US: f-__-__-____ (8 digits)
const NONUS_TEMPLATE = "f-  -  -    ";
const NONUS_DIGIT_POS = [2, 3, 5, 6, 8, 9, 10, 11];

function templateFor(isUS) {
  return isUS ? US_TEMPLATE : NONUS_TEMPLATE;
}
function digitPosFor(isUS) {
  return isUS ? US_DIGIT_POS : NONUS_DIGIT_POS;
}
function maxDigitsFor(isUS) {
  return isUS ? 9 : 8;
}

function extractDigits(s, isUS) {
  const str = String(s || "");
  if (isUS) {
    // Only digits allowed for US
    return str.replace(/\D/g, "");
  }
  if (isUS === false) {
    // For non-US: first 4 must be digits, last 4 can be alphanumeric
    let out = "";
    for (let i = 0; i < str.length && out.length < 8; i++) {
      const ch = str[i];
      if (out.length < 4) {
        if (/\d/.test(ch)) {
          out += ch;
        }
      } else {
        if (/\w/.test(ch)) {
          out += ch;
        }
      }
    }
    return out?.toUpperCase();
  } else {
    return str.replace(/\D/g, "");
  }
}

// Prefer plaintext/decrypted Security ID fields when present.
// Many APIs return masked values in `security_id` (e.g., "***-**-____"), which contains no digits.
// Eye-toggle requires digits to be available in state to render unmasked format.
function resolveSecurityDigits(api) {
  if (!api) return "";
  const candidates = [
    api.security_id_plaintext,
    api.securityIdPlaintext,
    api.security_id_unmasked,
    api.securityIdUnmasked,
    api.security_id_value,
    api.securityIdValue,
    api.security_id_raw,
    api.securityIdRaw,
    api.security_id, // may be masked
    api.securityId,
    // Hinge DTV lookup can return only last-4 digits
    api.ssnLast4d,
    api.ssn_last4d,
    api.ssnLast4,
    api.ssn_last4,
    api.security_id_hash, // not usable for display but may contain digits in some legacy payloads
  ];
  for (const v of candidates) {
    const d = extractDigits(v ?? "");
    if (d) return d;
  }
  return "";
}

function clampDigitsToFormat(isUS, digits) {
  const max = maxDigitsFor(isUS);
  return String(digits || "").slice(0, max);
}

// Convert digits entered => masked display that keeps separators (and 'f' for non-US)
function buildMaskedDisplay(isUS, digits) {
  const tpl = templateFor(isUS).split("");
  const pos = digitPosFor(isUS);
  const n = Math.min(digits.length, pos.length);
  for (let i = 0; i < pos.length; i++) {
    tpl[pos[i]] = i < n ? "*" : "_";
  }
  return tpl.join("");
}

// Show only last4 in review, rest masked
function buildMaskedLast4(isUS, digits) {
  const tpl = templateFor(isUS).split("");
  const pos = digitPosFor(isUS);
  const last4 = String(digits || "").slice(-4);
  const total = maxDigitsFor(isUS);

  for (let i = 0; i < pos.length; i++) tpl[pos[i]] = "*";

  const start = Math.max(0, total - 4);
  for (let i = start; i < total; i++) {
    const idxInLast4 = i - start;
    tpl[pos[i]] = last4[idxInLast4] || "*";
  }
  return tpl.join("");
}

// Build "unmasked" formatted value for eye toggle
function buildUnmaskedFormatted(isUS, digits) {
  const d = clampDigitsToFormat(isUS, extractDigits(digits, isUS));
  if (!d) return "";
  if (isUS) {
    const a = d.slice(0, 3);
    const b = d.slice(3, 5);
    const c = d.slice(5, 9);
    return `${a}${d.length > 3 ? "-" : ""}${b}${d.length > 5 ? "-" : ""}${c}`.replace(
      /-$/,
      "",
    );
  }
  const a = d.slice(0, 2);
  const b = d.slice(2, 4);
  const c = d.slice(4, 8);
  return `f-${a}${d.length > 2 ? "-" : ""}${b}${d.length > 4 ? "-" : ""}${c}`.replace(
    /-$/,
    "",
  );
}

function setCaretToNextSlot(inputEl, isUS, digitsLen) {
  if (!inputEl) return;
  const pos = digitPosFor(isUS);
  const nextIndex =
    pos[Math.min(digitsLen, pos.length - 1)] ?? templateFor(isUS).length;
  const caret = digitsLen >= pos.length ? templateFor(isUS).length : nextIndex;
  try {
    inputEl.setSelectionRange(caret, caret);
  } catch {}
}

function setCaretToSlotIndex(inputEl, idx) {
  if (!inputEl) return;
  try {
    inputEl.setSelectionRange(idx, idx);
  } catch {}
}

// ====== map API → form1 shape (for edit / approval-details) ======
function mapApiToForm1(api) {
  if (!api) return null;

  const secDigits = resolveSecurityDigits(api);

  // booleans → "Yes"/"No"
  const nonBillable =
    api.non_billable === true ? "Yes" : api.non_billable === false ? "No" : "";
  const attuidRequired =
    api.attuid_required === true
      ? "Yes"
      : api.attuid_required === false
        ? "No"
        : "No";
  const autoRegister =
    api.auto_register === true
      ? "Yes"
      : api.auto_register === false
        ? "No"
        : "";
  const autoInvoiceAll =
    api.auto_invoice_all_characteristics === true
      ? "Yes"
      : api.auto_invoice_all_characteristics === false
        ? "No"
        : "";
  const displayWorkforceToBuyer =
    api.display_candidate_to_buyer === true
      ? "Yes"
      : api.display_candidate_to_buyer === false
        ? "No"
        : "";

  return {
    // status / meta
    status: api.status ?? "",
    isSecExist: normalizeLookupSecurityExistence(api.isSecExist),
    attuid: api.attuid ?? api.attUid ?? api.attUID ?? api.att_uid ?? "",

    // flags rendered in UI
    attuidRequired,
    site: api.site_id ?? "",
    location: api.location ?? "",

    // personal
    firstName: api.first_name ?? "",
    lastName: api.last_name ?? "",
    email: api.email ?? "",
    phone: api.phone ?? "",

    // security
    securityId: secDigits,
    validateSecurity: secDigits,

    // dates
    startDate: api.start_date ? new Date(api.start_date) : null,
    endDate: api.end_date ? new Date(api.end_date) : null,

    // general flags
    autoRegister,
    timeZone: api.time_zone ?? "",

    // org/department (still used in DB, just not shown on UI)
    department: api.department ?? "DIRECT (EGA003)",

    // manager / sponsor
    webphoneManagerId: api.webphone_manager ?? "",
    webphone_manager_attuid:
      api.webphone_manager_attuid ?? api.webphoneManagerAttuid ?? "",
    sponsor: api.sponsor ?? "",

    // worker types
    npwType:
      api.employment_type ??
      api.npw_type ??
      api?.personType ??
      "Non-staff Supp Worker",
    workerTypeTax: api.worker_type_tax ?? "",
    workerPrimaryContact: api.worker_primary_contact ?? "",
    nonBillable,

    // location / occupancy
    country: normalizeWorkingCountryName(
      firstNonEmpty(
        api.country,
        api.countryOfWorking,
        api.country_of_working,
        api.countryOfWork,
      ),
    ),
    state: api.state ?? api.stateOfWorking ?? api.state_of_working ?? "",
    city: api.city ?? api.cityOfWorking ?? api.city_of_working ?? "",
    occupancyCode: api.occupancy ?? api.occupation_indicator_code ?? "",
    workstation: api.floor_workstation_cubicle ?? "",

    // vendor / supplier / contract
    supplierReference: api.supplier_reference ?? "",
    vendor: api.vendor ?? "",
    contractPo: api.contract_po ?? api.contract ?? "",
    poNum: api.po_number ?? "",
    lineNum: api.line_number ?? "",
    billRate: api.bill_rate ?? "",
    boltApproval: api.bolt_approval_number ?? "",
    npwManagerCompany: api.npw_manager_company ?? "",

    // role
    role: api.role ?? "",

    // citizenship
    citizenship: resolveCitizenshipDisplay(
      firstNonEmpty(
        api.citizenship,
        api.countryOfCitizenship,
        api.country_of_citizenship,
      ),
      [],
    ),

    // misc flags
    autoInvoiceAll,
    username: api.username ?? "",
    displayWorkforceToBuyer,
    timeSheetType: api.timesheet_type ?? "No Time Sheets",

    // IDs (hidden on UI but kept in data)
    dtvId: api.dtv_id ?? "",

    // ATT Role fields
    attRoleKey: api.attRoleKey ?? api.att_role_key ?? "",
    attRoleDtvRoleId: api.attRoleDtvRoleId ?? api.att_role_dtv_role_id ?? "",
    attRoleDtvRoleName:
      api.attRoleDtvRoleName ?? api.att_role_dtv_role_name ?? "",
    attRoleCpRoleId: api.attRoleCpRoleId ?? api.att_role_cp_role_id ?? "",
    attRoleCpRoleDesc: api.attRoleCpRoleDesc ?? api.att_role_cp_role_desc ?? "",
    attRoleCpProfileId:
      api.attRoleCpProfileId ?? api.att_role_cp_profile_id ?? "",
    attRoleCpProfileDesc:
      api.attRoleCpProfileDesc ?? api.att_role_cp_profile_desc ?? "",

    // these are not on approval-details today, but we keep them in form shape
    npwTypeRaw: api.npw_type ?? null,
  };
}

/* ================================================================== */
/*  COMPONENT                                                         */
/* ================================================================== */

export default function OnboardingEmpSupplierAdmin({
  npwId = null,
  backToMain = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // onboarding draft id

  const roleLabelForTitle = useMemo(
    () => formatRoleLabel(getFreshSession().role),
    [],
  );

  /* ------------------------------------------------------------------ */
  /*  TOAST + NAV HELPERS                                               */
  /* ------------------------------------------------------------------ */
  const toastThenDashboard = (message, type = "success") => {
    const msg = String(message || "").trim();
    if (!msg) {
      navigate("/dashboard", { replace: true });
      return;
    }

    // Persist for the destination page.
    try {
      setDashboardToast(msg, type);
    } catch {
      /* non-blocking */
    }

    // IMPORTANT: ToastContainer is inside this page; navigating immediately can unmount it.
    const show =
      type === "success"
        ? toast.success
        : type === "warn"
          ? toast.warn
          : type === "info"
            ? toast.info
            : toast.error;

    show(msg, {
      autoClose: 1200,
      closeOnClick: true,
      pauseOnHover: true,
      onClose: () => navigate("/dashboard", { replace: true }),
    });
  };

  // Robust "Back" behavior for edit routes:
  // 1) use provided backToMain callback (if embedded)
  // 2) use location.state.from (if caller passed it)
  // 3) fall back to shared Search/Overview returnPath sessionStorage
  // 4) fall back to browser history
  // 5) finally fall back to dashboard
  const handleBackToCaller = () => {
    try {
      if (typeof backToMain === "function") {
        backToMain();
        return;
      }
    } catch {
      /* ignore */
    }

    const from =
      typeof location?.state?.from === "string" ? location.state.from : "";
    if (from) {
      navigate(from, { replace: true });
      return;
    }

    try {
      const host = (sessionStorage.getItem("npw.search.host") || "").trim();
      const ret = (
        sessionStorage.getItem("npw.search.returnPath") || ""
      ).trim();
      const target = host || ret;
      if (target) {
        navigate(target, { replace: true });
        return;
      }
    } catch {
      /* ignore */
    }

    try {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
    } catch {
      /* ignore */
    }

    navigate("/dashboard", { replace: true });
  };

  const hasExternalCallerTarget = (() => {
    const from =
      typeof location?.state?.from === "string"
        ? location.state.from.trim()
        : "";
    let host = "";
    let ret = "";
    try {
      host = (sessionStorage.getItem("npw.search.host") || "").trim();
      ret = (sessionStorage.getItem("npw.search.returnPath") || "").trim();
    } catch {
      /* ignore */
    }
    const target = (from || host || ret || "").trim();
    return Boolean(
      target && /^\/(approvals|manage-requests)(\b|\/|\?)/.test(target),
    );
  })();

  const [dd, setDd] = useState(STATIC_DROPDOWNS);
  const [workerType, setWorkerType] = useState("Non-Payroll Worker");
  const [step, setStep] = useState(1);
  const [editData, setEditData] = useState(null);

  // Security ID: in edit/prefill flows, inputs remain read-only until user clicks "Edit"
  const [securityEditEnabled, setSecurityEditEnabled] = useState(false);
  const [securityInitialDigits, setSecurityInitialDigits] = useState("");
  const securityInitCapturedRef = useRef(false);
  const securityInitialIsUSRef = useRef(null);

  // DTV-prefill: treat Active/Terminated people as an "edit-like" context for Security ID.
  const [dtvPrefillExistingRecord, setDtvPrefillExistingRecord] =
    useState(false);
  const [dtvPrefillPersonStatus, setDtvPrefillPersonStatus] = useState("");
  const [lookupAttuid, setLookupAttuid] = useState("");
  const [lookupSecExistFlag, setLookupSecExistFlag] = useState("");
  const normalizedLookupSecExistFlag =
    normalizeLookupSecurityExistence(lookupSecExistFlag);

  // Draft tracking (npw_id returned from "Draft" save) so a later "Submit" uses EDIT (PUT)
  const [draftNpwId, setDraftNpwId] = useState(() => {
    try {
      return SS.getItem("tb_draft_npw_id") || null;
    } catch {
      return null;
    }
  });

  // Worker Type should be immutable on Review page (Step 2) and also immutable when editing an existing request
  // Per UX: treat as edit ONLY when route has :id or an external npwId prop is present.
  const isEditRoute = !!(id || npwId);
  const isEditContext = isEditRoute;
  const isWorkerTypeLocked = step === 2 || isEditContext;
  const isEsaRole = useMemo(() => {
    const roleKey = String(getFreshSession()?.role || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, " ");
    return roleKey === "employee supplier admin";
  }, []);

  const editHeadingOverride = useMemo(() => {
    if (!isEditRoute) return "";
    const statusKey = (s) =>
      String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, "");
    const k = statusKey(
      editData?.personStatus ?? editData?.person_status ?? editData?.status,
    );
    if (k === "inactive") return "Rehire Onboarding";
    if (k === "active") return "Modify Onboarding";
    return "";
  }, [
    isEditRoute,
    editData?.status,
    editData?.personStatus,
    editData?.person_status,
  ]);

  const dtvPrefillHeadingOverride = useMemo(() => {
    if (isEditRoute) return "";
    if (!dtvPrefillExistingRecord) return "";
    const statusKey = (s) =>
      String(s || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]/g, "");
    const k = statusKey(dtvPrefillPersonStatus);
    if (k === "inactive") return "Rehire Onboarding";
    if (k === "active") return "Modify Onboarding";
    return "";
  }, [isEditRoute, dtvPrefillExistingRecord, dtvPrefillPersonStatus]);

  // Security ID must be locked (non-editable) when loading an existing record (npw_id)
  // OR when this page is opened via DTVID prefill (query/state).
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const isReactiveAttuidFlow = useMemo(() => {
    const flag = (
      searchParams.get("reactivate_attuid") ||
      searchParams.get("reactive_attuid") ||
      ""
    ).trim();
    const isFlagTruthy = (v) =>
      v === true ||
      v === 1 ||
      v === "1" ||
      v === "true" ||
      v === "t" ||
      v === "T";
    return (
      isFlagTruthy(flag) || isFlagTruthy(location?.state?.reactivateAttuid)
    );
  }, [searchParams, location.state]);
  const routeDtvid = useMemo(() => {
    const q = (
      searchParams.get("dtvId") ||
      searchParams.get("dtvid") ||
      ""
    ).trim();
    const s = (location.state?.dtvId || "").trim();
    let r = "";
    try {
      const raw = decodeURIComponent(String(id || "").trim());
      if (raw) {
        const m = /^(?:DTVID|DTV_ID|DTV)\s*=\s*(.+)$/i.exec(raw);
        if (m) r = String(m[1] || "").trim();
        else if (raw.toUpperCase().includes("DTV"))
          r = raw.replace(/^DTVID\s*:?/i, "").trim();
      }
    } catch {
      r = "";
    }
    return q || s || r || "";
  }, [searchParams, location.state, id]);
  const effectiveNpwId =
    draftNpwId ||
    (isEditRoute ? id || npwId : null) ||
    editData?.npwId ||
    editData?.npw_id ||
    null;
  const hasRealNpwId = /^\d+$/.test(String(effectiveNpwId || "").trim());
  const showDtvidAttuidPrefill = Boolean(
    routeDtvid && String(routeDtvid).trim() && !hasRealNpwId,
  );

  const isSecurityEditContext = Boolean(
    (effectiveNpwId && String(effectiveNpwId).trim()) ||
    (routeDtvid && String(routeDtvid).trim()),
  );

  useEffect(() => {
    if (!isSecurityEditContext) {
      securityInitCapturedRef.current = false;
      setSecurityEditEnabled(false);
      setSecurityInitialDigits("");
      securityInitialIsUSRef.current = null;
      setDtvPrefillExistingRecord(false);
      setDtvPrefillPersonStatus("");
      setLookupSecExistFlag("");
      return;
    }

    const lookupSecExistKnown =
      lookupSecExistFlag === "yes" || lookupSecExistFlag === "no";
    if (!lookupSecExistKnown) return;

    if (securityInitCapturedRef.current) return;
    if (lookupSecExistFlag !== "yes") {
      securityInitCapturedRef.current = true;
      setSecurityInitialDigits("");
      securityInitialIsUSRef.current = null;
      return;
    }
    if (!editData) return;

    securityInitCapturedRef.current = true;
    setSecurityInitialDigits(extractDigits(editData.securityId || ""));

    const editAttuidOn =
      editData.attuidRequired === "Yes" ||
      editData.attuidRequired === "true" ||
      editData.isAttuidRequested === "Yes" ||
      editData.isAttuidRequested === true;
    securityInitialIsUSRef.current =
      editAttuidOn && isUnitedStatesSelected(editData.country);
  }, [isSecurityEditContext, editData, lookupSecExistFlag]);

  const securityInitialHadValue = Boolean(securityInitialDigits);
  const hasLookupSecurity = lookupSecExistFlag === "yes";
  // Show Security ID edit controls ONLY when editing an existing record (not create / prefill).
  const isSecurityEditRecord = Boolean(
    (isEditRoute || dtvPrefillExistingRecord) && hasLookupSecurity,
  );
  const isSecurityLocked = isSecurityEditRecord && !securityEditEnabled;
  const showSecurityEditButton = isSecurityEditRecord && !securityEditEnabled;
  const showSecurityDisableEditButton =
    isSecurityEditRecord && securityEditEnabled;

  // When locked, clear Security ID validation errors (we do not validate in edit/prefill contexts).
  useEffect(() => {
    if (!isSecurityLocked) return;
    setFieldErrors((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));
  }, [isSecurityLocked]);

  // Hinge role catalog (for role + ATT Role)
  const [hingeRoleCatalog, setHingeRoleCatalog] = useState([]);

  /* geo dropdowns */
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // Country of Citizenship options (from cp-countrylist API)
  const [citizenshipOptions, setCitizenshipOptions] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [ctzn, setCitizen] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const hasStatesForSelectedCountry = useMemo(() => {
    const countryCode = selectedCountry?.value;
    if (!countryCode) return false;
    try {
      return State.getStatesOfCountry(countryCode).length > 0;
    } catch {
      return stateOptions.length > 0;
    }
  }, [selectedCountry?.value, stateOptions.length]);
  const isStateRequired = Boolean(
    selectedCountry && hasStatesForSelectedCountry,
  );
  // City is only required when the selected state actually has cities to pick from
  // (some states have no cities in the country-state-city dataset).
  const isCityRequired = Boolean(selectedState && cityOptions.length > 0);
  const [selectedTimeZone, SetSelectedTimeZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cpValidation, setCpValidation] = useState({
    status: "idle",
    message: "",
  });

  // When we programmatically set geo fields (e.g., Hinge Active prefill),
  // avoid the country/state cascades wiping state/city immediately after render.
  const suppressCountryCascadeRef = useRef(false);
  const suppressStateCascadeRef = useRef(false);

  // Sponsor / Webphone validation state (Hinge)
  const [sponsorValidation, setSponsorValidation] = useState({
    status: "idle",
    message: "",
  });
  const [webphoneValidation, setWebphoneValidation] = useState({
    status: "idle",
    message: "",
  });
  const [contractValidation, setContractValidation] = useState({
    status: "idle",
    message: "",
  });
  const [poValidation, setPoValidation] = useState({
    status: "idle",
    message: "",
  });
  const [lineValidation, setLineValidation] = useState({
    status: "idle",
    message: "",
  });

  // Security ID existence check (TB ↔ Hinge Search)
  const [securityIdExistence, setSecurityIdExistence] = useState({
    status: "idle",
    message: "",
  });
  const securityIdDebounceRef = useRef(null);
  const securityIdSeqRef = useRef(0);

  const [dataLoads, setDataLoads] = useState({
    roles: {
      field: "Role",
      status: "idle",
      startedAt: null,
    },
    vendorSupplier: {
      field: "Vendor/Supplier",
      status: "idle",
      startedAt: null,
    },
  });

  useDataLoadTimeoutToasts(dataLoads, { timeoutMs: 20000 });
  useDataLoadErrorToasts(dataLoads);

  useEffect(() => {
    if (!TB_HINGE_DTV_LOOKUP_URL) return;

    const targetDtvId = String(
      editData?.dtvId || editData?.dtv_id || routeDtvid || "",
    ).trim();
    const hasNumericNpwId = /^\d+$/.test(String(effectiveNpwId || "").trim());
    if (!hasNumericNpwId || !targetDtvId) return;

    let canceled = false;
    (async () => {
      try {
        const base = TB_HINGE_DTV_LOOKUP_URL;
        const sep = base.includes("?") ? "&" : "?";
        const resp = await tbGetJson(
          `${base}${sep}dtvId=${encodeURIComponent(targetDtvId)}`,
        );
        if (canceled) return;
        setLookupSecExistFlag(getLookupSecurityExistenceFromPayload(resp));
      } catch {
        if (!canceled) setLookupSecExistFlag("");
      }
    })();

    return () => {
      canceled = true;
    };
  }, [effectiveNpwId, editData?.dtvId, editData?.dtv_id, routeDtvid]);

  /* ------------------------------------------------------------------ */
  /*  COUNTRY OF CITIZENSHIP (CP list)                                  */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const url = String(CP_COUNTRYLIST_URL || "").trim();
    if (!url) return;

    const ac = new AbortController();
    let alive = true;

    (async () => {
      try {
        const resp = await tbGetJson(url, { fetchInit: { signal: ac.signal } });
        const payload =
          resp?.response && typeof resp.response === "object"
            ? resp.response
            : resp;

        const list = Array.isArray(payload?.citizenships)
          ? payload.citizenships
          : Array.isArray(payload?.rows)
            ? payload.rows
            : Array.isArray(payload)
              ? payload
              : [];

        const opts = (list || [])
          .map((item) =>
            item && typeof item === "object"
              ? item.citizenship_value ||
                item.citizenship ||
                item.value ||
                item.label
              : item,
          )
          .filter((value) => value != null && String(value).trim())
          .map((c) => {
            const v = String(c).trim();
            return { label: v, value: v };
          });

        if (alive && opts.length) setCitizenshipOptions(opts);
      } catch (e) {
        console.warn("CP country-of-citizenship load failed", e);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Vendor search state (S2P live search)
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorInput, setVendorInput] = useState("");

  /* ------- Step 1 fields ------- */
  const initialVal = {
    attuid: "",
    attuidRequired: "No",
    site: "",
    location: "",
    firstName: "",
    lastName: "",
    phone: "",
    role: "",
    securityId: "",
    validateSecurity: "",
    startDate: null,
    endDate: null,
    autoRegister: "Yes",
    timeZone: "",
    department: "DIRECT (EGA003)", // still used in DB, just not shown
    webphoneManagerId: "",
    webphone_manager_attuid: "",
    webphone_manager_email: "",
    webphoneManagerEmail: "",
    npwType: "Non-staff Supp Worker",
    workstation: "",
    occupancyCode: "",
    workerPrimaryContact: "",
    nonBillable: "",
    sponsor: "",
    sponsor_email: "",
    sponsorEmail: "",
    contractPo: "",
    billRate: "",
    boltApproval: "",
    npwManagerCompany: "",
    workerTypeTax: "",
    citizenship: "",
    country: "",
    city: "",
    state: "",
    autoInvoiceAll: "No",
    supplierReference: "",
    username: "",
    displayWorkforceToBuyer: "",
    timeSheetType: "No Time Sheets",
    vendor: "",
    dtvId: "",
    poNum: "",
    lineNum: "",
    email: "",
    // ATT Role fields
    attRoleKey: "",
    attRoleDtvRoleId: "",
    attRoleDtvRoleName: "",
    attRoleCpRoleId: "",
    attRoleCpRoleDesc: "",
    attRoleCpProfileId: "",
    attRoleCpProfileDesc: "",
  };
  const [form1, setForm1] = useState(initialVal);

  // Supplier reference must be present before user can proceed/submit.
  // If supplier lookup fails (API error) or user hasn't selected a valid supplier, block early.
  const [supplierGate, setSupplierGate] = useState({ blocked: false });

  useEffect(() => {
    const hasVendorName = !!String(form1.vendor || "").trim();
    const hasSupplierRef = !!String(form1.supplierReference || "").trim();

    // Reset when vendor cleared
    if (!hasVendorName && !hasSupplierRef) {
      if (supplierGate.blocked) setSupplierGate({ blocked: false });
      return;
    }

    // Explicit API error from vendor/supplier lookup
    if (dataLoads?.vendorSupplier?.status === "error") {
      if (!supplierGate.blocked) setSupplierGate({ blocked: true });
      toast.error(
        "SupplierValidation has failed (Supplier Reference not loaded). Please reload the page and try again.",
        { toastId: "supplier-validation-failed" },
      );
      return;
    }

    // If vendor was selected but supplier reference is missing, block immediately
    if (hasVendorName && !hasSupplierRef && !vendorLoading) {
      if (!supplierGate.blocked) setSupplierGate({ blocked: true });
      toast.error(
        "Supplier Reference is not loaded. Please select a valid Vendor (Supplier) before continuing.",
        { toastId: "supplier-reference-missing" },
      );
      return;
    }

    // Otherwise allow
    if (supplierGate.blocked) setSupplierGate({ blocked: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form1.vendor,
    form1.supplierReference,
    vendorLoading,
    dataLoads?.vendorSupplier?.status,
  ]);

  /* Attuid required? (drives Security ID validation rule) */
  const attuidRequiredOn = (() => {
    const attuidRequiredNorm = String(form1.attuidRequired || "")
      .trim()
      .toLowerCase();
    const requestedRaw = form1.isAttuidRequested;
    const requestedNorm = String(requestedRaw || "")
      .trim()
      .toLowerCase();
    return (
      attuidRequiredNorm === "yes" ||
      attuidRequiredNorm === "true" ||
      requestedRaw === true ||
      requestedNorm === "yes" ||
      requestedNorm === "true"
    );
  })();
  const requiresCpValidateBeforeSubmit =
    IS_VALIDATE_CP_NEEDED && attuidRequiredOn;

  useEffect(() => {
    setCpValidation((prev) =>
      prev.status === "idle" ? prev : { status: "idle", message: "" },
    );
  }, [attuidRequiredOn, form1]);
  const isSecurityOptionalEditMode = useMemo(() => {
    return (
      isEsaRole &&
      isEditContext &&
      !attuidRequiredOn &&
      normalizedLookupSecExistFlag !== "yes"
    );
  }, [
    isEsaRole,
    isEditContext,
    attuidRequiredOn,
    normalizedLookupSecExistFlag,
  ]);
  const isSecurityFieldRequired =
    !isSecurityLocked && !isSecurityOptionalEditMode;
  const isSecurityReadOnly =
    isSecurityLocked || (isSecurityOptionalEditMode && !securityEditEnabled);
  const showSecurityEditButtonUi =
    showSecurityEditButton ||
    (isSecurityOptionalEditMode && !securityEditEnabled);
  const showSecurityDisableEditButtonUi =
    showSecurityDisableEditButton ||
    (isSecurityOptionalEditMode && securityEditEnabled);
  //added bcos of gautham fix for loading details in edit issue
  //form1.attuidRequired === "Yes" || form1.attuidRequired === "true";

  useEffect(() => {
    if (!isSecurityOptionalEditMode) return;
    setSecurityEditEnabled(false);
    setShowSecurityId(false);
    setShowConfirmSecurityId(false);
    setFieldErrors((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));
  }, [isSecurityOptionalEditMode]);

  // When ATTUID Required is OFF:
  // - Hide Country of Citizenship + W2/1099
  // - Clear any previously selected values + related validation errors
  useEffect(() => {
    if (!attuidRequiredOn) {
      setCitizen(null);
      setForm1((prev) => ({
        ...prev,
        citizenship: "",
        workerTypeTax: "",
      }));
      setFieldErrors((prev) => ({
        ...prev,
        citizenship: "",
        workerTypeTax: "",
      }));
    }
  }, [attuidRequiredOn]);

  useEffect(() => {
    if (!attuidRequiredOn || !ctzn) return;
    const citizenship = String(ctzn.label || ctzn.value || "").trim();
    if (!citizenship) return;

    setForm1((prev) =>
      prev.citizenship === citizenship
        ? prev
        : { ...prev, citizenship },
    );
    setFieldErrors((prev) =>
      prev.citizenship ? { ...prev, citizenship: "" } : prev,
    );
  }, [attuidRequiredOn, ctzn, form1.citizenship]);

  /* =========================
           SECURITY ID (masked input + eye)
           ========================= */

  const [showSecurityId, setShowSecurityId] = useState(false);
  const [showConfirmSecurityId, setShowConfirmSecurityId] = useState(false);

  const securityRef = useRef(null);
  const confirmSecurityRef = useRef(null);
  const securityTouchedRef = useRef(false);
  const confirmSecurityTouchedRef = useRef(false);

  // UI: Security ID input + message fixed width (per requirement)
  const SECURITY_INPUT_WIDTH_PX = 180;
  const prevAttuidRequiredOnRef = useRef(null);
  const prevCountryLabelRef = useRef("");

  // Security ID rule (per requirement):
  // Apply US rule ONLY when (Attuid Required = Yes) AND (Country = United States).
  // Otherwise apply Non-US rule.
  const isUS = useMemo(
    () => attuidRequiredOn && isUnitedStatesSelected(form1.country),
    [attuidRequiredOn, form1.country],
  );

  const securityPlaceholder = useMemo(() => templateFor(isUS), [isUS]);
  const securityHintPrimary = useMemo(() => {
    return isUS
      ? "Numbers only. Enter SSN (9 digits)."
      : "Numbers only. Enter Security ID (8 digits).";
  }, [isUS]);

  const securityHintFormat = useMemo(() => {
    return isUS
      ? "Format: xxx-xx-xxxx"
      : "Format: f-mm-dd-xxxx (Birth Month-Date-Last 4 of Security ID).";
  }, [isUS]);

  const securityLabel = useMemo(() => (isUS ? "SSN" : "Security ID"), [isUS]);
  const confirmSecurityLabel = useMemo(
    () => (isUS ? "Confirm SSN" : "Confirm Security ID"),
    [isUS],
  );
  const securityTooltipText = useMemo(() => {
    return isUS
      ? "US + ATTUID Requested: enter SSN (9 digits)."
      : "Non-US or ATTUID not requested: enter Security ID as f-mm-dd-xxxx (Birth Month-Date-Last 4 of Sec ID).";
  }, [isUS]);

  useEffect(() => {
    if (!isSecurityEditContext) return;
    if (lookupSecExistFlag !== "no") return;
    if (securityTouchedRef.current || confirmSecurityTouchedRef.current) return;

    const hasSeededSecurity = Boolean(
      extractDigits(form1.securityId, isUS) ||
      extractDigits(form1.validateSecurity, isUS),
    );
    if (!hasSeededSecurity) return;

    setShowSecurityId(false);
    setShowConfirmSecurityId(false);
    setFieldErrors((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));
    setForm1((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));
  }, [
    isSecurityEditContext,
    lookupSecExistFlag,
    form1.securityId,
    form1.validateSecurity,
    isUS,
  ]);

  // If the Security ID rule changes while editable, revalidate so the user sees the correct
  // 8 vs 9 digit requirement immediately.
  useEffect(() => {
    if (isSecurityReadOnly) return;
    if (form1.securityId) runFieldValidation("securityId", form1.securityId);
    if (form1.validateSecurity)
      runFieldValidation("validateSecurity", form1.validateSecurity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUS, isSecurityReadOnly]);

  // Keep mismatch validation in sync: changing either field should revalidate both.
  useEffect(() => {
    if (isSecurityReadOnly) return;

    const hasSecurity = (form1.securityId || "").length > 0;
    const hasConfirm = (form1.validateSecurity || "").length > 0;
    const touched =
      securityTouchedRef.current || confirmSecurityTouchedRef.current;

    if (hasSecurity || hasConfirm || touched) {
      runFieldValidation("securityId", form1.securityId || "");
      runFieldValidation("validateSecurity", form1.validateSecurity || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form1.securityId, form1.validateSecurity, isUS, isSecurityReadOnly]);

  const handleEnableSecurityEdit = () => {
    setSecurityEditEnabled(true);
    setFieldErrors((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));

    const nextSecurityId = clampDigitsToFormat(
      isUS,
      extractDigits(form1.securityId, isUS),
    );
    const nextValidateSecurity = clampDigitsToFormat(
      isUS,
      extractDigits(form1.validateSecurity, isUS),
    );

    // Only clear if we only have last-4 (or fewer) digits. If the rule changed while locked,
    // keep what we have and clamp 9->8 rather than wiping.
    if (nextSecurityId && nextSecurityId.length <= 4) {
      setForm1((p) => ({ ...p, securityId: "", validateSecurity: "" }));
      setShowSecurityId(false);
      setShowConfirmSecurityId(false);
    } else {
      if (
        nextSecurityId !== form1.securityId ||
        nextValidateSecurity !== form1.validateSecurity
      ) {
        setForm1((p) => ({
          ...p,
          securityId: nextSecurityId,
          validateSecurity: nextValidateSecurity,
        }));
      }
      setShowSecurityId(false);
      setShowConfirmSecurityId(false);
    }

    queueMicrotask(() => {
      try {
        securityRef.current?.focus();
      } catch {}
    });
  };

  const handleDisableSecurityEdit = () => {
    setSecurityEditEnabled(false);
    setShowSecurityId(false);
    setShowConfirmSecurityId(false);
    setFieldErrors((prev) => ({
      ...prev,
      securityId: "",
      validateSecurity: "",
    }));
    // Revert to the originally-loaded digits (may be empty) so we don't accidentally send changes.
    setForm1((p) => ({
      ...p,
      securityId: securityInitialDigits || "",
      validateSecurity: securityInitialDigits || "",
    }));
  };

  const securityDisplayValue = useMemo(() => {
    const d = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
    if (isSecurityReadOnly) {
      const max = maxDigitsFor(isUS);
      if (!d) return "*".repeat(max);
      if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
      return buildUnmaskedFormatted(isUS, d);
    }
    if (!d) return "";
    return showSecurityId
      ? buildUnmaskedFormatted(isUS, d)
      : buildMaskedDisplay(isUS, d);
  }, [isUS, form1.securityId, showSecurityId, isSecurityReadOnly]);

  const confirmDisplayValue = useMemo(() => {
    const d = clampDigitsToFormat(
      isUS,
      extractDigits(form1.validateSecurity, isUS),
    );
    if (isSecurityReadOnly) {
      const max = maxDigitsFor(isUS);
      if (!d) return "*".repeat(max);
      if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
      return buildUnmaskedFormatted(isUS, d);
    }
    if (!d) return "";
    return showConfirmSecurityId
      ? buildUnmaskedFormatted(isUS, d)
      : buildMaskedDisplay(isUS, d);
  }, [isUS, form1.validateSecurity, showConfirmSecurityId, isSecurityReadOnly]);

  /* =========================================================
       Security ID existence check (TB ↔ Hinge Search)
       - Trigger when digits >= 8
       - 2s debounce (user stops typing)
       - Show "already associated to <Name>" when match found
       - Show green "available to use" when no match
       ========================================================= */
  useEffect(() => {
    // Do not validate in locked/edit contexts
    if (isSecurityReadOnly) {
      if (securityIdDebounceRef.current)
        clearTimeout(securityIdDebounceRef.current);
      setSecurityIdExistence({ status: "idle", message: "" });
      return;
    }

    const primaryErr = String(fieldErrors?.securityId || "").trim();
    const confirmErr = String(fieldErrors?.validateSecurity || "").trim();
    const hasPrimaryError = Boolean(primaryErr);
    const hasAnySecurityError = hasPrimaryError || Boolean(confirmErr);

    const digits = extractDigits(form1.securityId || "", isUS);
    if (!digits || digits.length < HINGE_SSN_MIN_DIGITS) {
      if (securityIdDebounceRef.current)
        clearTimeout(securityIdDebounceRef.current);
      setSecurityIdExistence({ status: "idle", message: "" });
      return;
    }

    // If the primary SSN/Security ID field is invalid, do not show availability messaging
    // and do not call Hinge search until it is valid.
    if (hasPrimaryError) {
      if (securityIdDebounceRef.current)
        clearTimeout(securityIdDebounceRef.current);
      setSecurityIdExistence({ status: "idle", message: "" });
      return;
    }

    // If non-US Security ID has an MM/DD error, do not show "available" messaging.
    if (!isUS) {
      const mmddMsg = validateSecurityIdMmdd8(digits);
      if (mmddMsg) {
        if (securityIdDebounceRef.current)
          clearTimeout(securityIdDebounceRef.current);
        setSecurityIdExistence({ status: "idle", message: "" });
        return;
      }
    }

    if (!TB_HINGE_SEARCH_URL) {
      // non-breaking: if env not configured, just don't show any message
      setSecurityIdExistence({ status: "idle", message: "" });
      return;
    }

    if (securityIdDebounceRef.current)
      clearTimeout(securityIdDebounceRef.current);

    const seq = ++securityIdSeqRef.current;
    setSecurityIdExistence({
      status: "checking",
      message: `Checking ${securityLabel}…`,
    });

    securityIdDebounceRef.current = setTimeout(async () => {
      try {
        const searcherRoleName = (
          SS.getItem("tb_role") ||
          SS.getItem("tb_dtvRoleName") ||
          ""
        ).trim();
        const searcherSupplierName = (
          SS.getItem("tb_supplierName") ||
          SS.getItem("tb_vendorId") ||
          ""
        ).trim();
        const searchDtvId = (SS.getItem("tb_dtvid") || "").trim();

        const base = TB_HINGE_SEARCH_URL;
        const sep = base.includes("?") ? "&" : "?";
        /*TODO:pentest*/
        const url =
          `${base}${sep}` +
          `q=${encodeURIComponent(digits)}` +
          `&isActive=true&searchMode=validate` +
          (searcherRoleName
            ? `&searcherRoleName=${encodeURIComponent(searcherRoleName)}`
            : "") +
          (searcherSupplierName
            ? `&searcherSupplierName=${encodeURIComponent(searcherSupplierName)}`
            : "") +
          (searchDtvId
            ? `&searchDtvId=${encodeURIComponent(searchDtvId)}`
            : "");
        // const url =
        //     `${base}${sep}` +
        //     `q=${encodeURIComponent(digits)}` +
        //     `&isActive=true` +
        //     (searcherRoleName ? `&searcherRoleName=${encodeURIComponent(searcherRoleName)}` : "") +
        //     (searcherSupplierName ? `&searcherSupplierName=${encodeURIComponent(searcherSupplierName)}` : "") +
        //     (searchDtvId ? `&searchDtvId=${encodeURIComponent(searchDtvId)}` : "");

        const resp = await tbGetJson(url);
        if (seq !== securityIdSeqRef.current) return;

        const payload =
          resp &&
          typeof resp === "object" &&
          resp.response &&
          typeof resp.response === "object"
            ? resp.response
            : resp;

        const data = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.personList)
            ? payload.personList
            : Array.isArray(payload?.items)
              ? payload.items
              : Array.isArray(payload)
                ? payload
                : [];

        if (Array.isArray(data) && data.length > 0) {
          const p0 = data[0] || {};
          const first = String(p0.firstName || p0.first_name || "").trim();
          const last = String(p0.lastName || p0.last_name || "").trim();
          const fullName = String(
            [first, last].filter(Boolean).join(" ") || "the existing worker",
          ).trim();

          //Validate SSN / Security ID
          const loggedinDtvId = String(getFreshSession().dtvid || "").trim().toLowerCase();
          const foundDtvid = String(
            p0?.dtvAnchorId || p0?.dtvId || p0?.dtv_id || "",
          ).trim().toLowerCase();
          const isloggedinDtvId = Boolean(loggedinDtvId) && loggedinDtvId === foundDtvid;
          const editingDtvId = form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || routeDtvid || "";
          const isEditingDtvId =
            isEditRoute &&
            Boolean(editingDtvId) &&
            String(editingDtvId).trim().toLowerCase() === foundDtvid;

          //For the case when the security ID is already associated with the person being edited
          if (isEditingDtvId) {
            const isEditingLoggedInDtvId = isEditingDtvId && isloggedinDtvId;
            setSecurityIdExistence({
              status: "found",
              message: isEditingLoggedInDtvId? 
                `${securityLabel} is already associated with you.`
                : `${securityLabel} is already associated with the same person which you are trying to update.`
            });
          }
          //For the case when the security ID is associated with logged in user or someone else
          else {
            setSecurityIdExistence({
              status: "found",
              message: isloggedinDtvId
                ? `${securityLabel} is already associated with you.`
                : `${securityLabel} is already associated to ${fullName}.`,
            });
          }
         

        } else {
          // Guard again in case the value changed during debounce
          if (!isUS) {
            const mmddMsg = validateSecurityIdMmdd8(
              extractDigits(form1.securityId || "", isUS),
            );
            if (mmddMsg) {
              setSecurityIdExistence({ status: "idle", message: "" });
              return;
            }
          }

          // If there is any validation error (including confirm mismatch), do not show
          // the green "available" message.
          if (hasAnySecurityError) {
            setSecurityIdExistence({ status: "idle", message: "" });
            return;
          }
          setSecurityIdExistence({
            status: "none",
            message: isUS
              ? "SSN is available to use."
              : "Security ID is available.",
          });
        }
      } catch (e) {
        if (seq !== securityIdSeqRef.current) return;
        // non-blocking: don't show scary error; just clear message
        setSecurityIdExistence({ status: "idle", message: "" });
      }
    }, HINGE_SSN_DEBOUNCE_MS);

    return () => {
      if (securityIdDebounceRef.current)
        clearTimeout(securityIdDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form1.securityId,
    fieldErrors?.securityId,
    fieldErrors?.validateSecurity,
    isUS,
    securityLabel,
    isSecurityReadOnly,
    TB_HINGE_SEARCH_URL,
    HINGE_SSN_MIN_DIGITS,
    HINGE_SSN_DEBOUNCE_MS,
  ]);

  // Do not clear Security ID when Attuid Required toggles.
  // Preserve digits and allow validation rule to update.
  useEffect(() => {
    const prev = prevAttuidRequiredOnRef.current;
    prevAttuidRequiredOnRef.current = attuidRequiredOn;
    if (prev === null) return;
    if (prev !== attuidRequiredOn && !isSecurityReadOnly) {
      setShowSecurityId(false);
      setShowConfirmSecurityId(false);
      setFieldErrors((p) => ({ ...p, securityId: "", validateSecurity: "" }));
    }
  }, [attuidRequiredOn, isSecurityReadOnly]);

  // If we are on the NEW onboarding route (no :id and no external npwId),
  // force-create mode by clearing any previous edit context + cached draft IDs.
  useEffect(() => {
    const isNewRoute = !id && !npwId;
    if (!isNewRoute) return;

    setEditData(null);
    setDraftNpwId(null);
    try {
      SS.removeItem("tb_draft_npw_id");
    } catch {}

    setStep(1);
    setForm1(initialVal);
    setFieldErrors({});

    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setCitizen(null);
    SetSelectedTimeZone(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, npwId]);

  const handleSecKeyDown =
    (which /* "securityId" | "validateSecurity" */) => (e) => {
      if (isSecurityReadOnly) {
        e.preventDefault();
        return;
      }
      const key = e.key;

      // Allow paste (handled in onPaste). Do not preventDefault here;
      if (e.ctrlKey || e.metaKey) {
        if (["v", "a", "c", "x"].includes(String(key || "").toLowerCase()))
          return;
      }

      const curDigits = clampDigitsToFormat(
        isUS,
        extractDigits(form1[which], isUS),
      );
      const max = maxDigitsFor(isUS);
      const pos = digitPosFor(isUS);

      if (key === "Tab") return;

      if (key === "Backspace") {
        e.preventDefault();
        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity")
          confirmSecurityTouchedRef.current = true;
        const next = curDigits.slice(0, -1);
        setForm1((p) => ({ ...p, [which]: next }));
        runFieldValidation(which, next);

        queueMicrotask(() => {
          const el =
            which === "securityId"
              ? securityRef.current
              : confirmSecurityRef.current;
          if (!next.length) setCaretToSlotIndex(el, 0);
          else setCaretToNextSlot(el, isUS, next.length);
        });
        return;
      }

      // SSN (US): 9 digits only; Security ID (non-US): MMDDXXXX (first 4 digits, last 4 alphanumeric)
      const isValidKey = (key, pos, isUS) => {
        if (isUS) {
          // SSN: only digits, max 9
          return /^[0-9]$/.test(key) && pos < 9;
        } else {
          // Security ID: MMDDXXXX
          if (pos < 4) return /^[0-9]$/.test(key); // MMDD
          if (pos >= 4 && pos < 8) return /^[A-Za-z0-9]$/.test(key); // XXXX
          return false;
        }
      };

      if (isValidKey(key, curDigits.length, isUS)) {
        e.preventDefault();
        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity")
          confirmSecurityTouchedRef.current = true;
        if (curDigits.length >= max) return;

        const next = curDigits + key;
        setForm1((p) => ({ ...p, [which]: next }));
        runFieldValidation(which, next);

        queueMicrotask(() => {
          const el =
            which === "securityId"
              ? securityRef.current
              : confirmSecurityRef.current;
          if (el) {
            if (next.length >= max) {
              setCaretToSlotIndex(el, templateFor(isUS).length);
            } else {
              const nextSlot = pos[next.length] ?? templateFor(isUS).length;
              setCaretToSlotIndex(el, nextSlot);
            }
          }
        });
        return;
      }

      if (
        [
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ].includes(key)
      ) {
        e.preventDefault();
        const el =
          which === "securityId"
            ? securityRef.current
            : confirmSecurityRef.current;
        queueMicrotask(() => {
          if (!curDigits.length) setCaretToSlotIndex(el, 0);
          else setCaretToNextSlot(el, isUS, curDigits.length);
        });
        return;
      }

      e.preventDefault();
    };

  const handleSecChange =
    (which /* "securityId" | "validateSecurity" */) => (e) => {
      if (isSecurityReadOnly) return;
      const raw = e?.target?.value ?? "";
      const digits = clampDigitsToFormat(isUS, extractDigits(raw, isUS));
      if (which === "securityId") securityTouchedRef.current = true;
      if (which === "validateSecurity")
        confirmSecurityTouchedRef.current = true;
      setForm1((p) => ({ ...p, [which]: digits }));
      runFieldValidation(which, digits);
    };

  const handleSecPaste = (which) => (e) => {
    if (isSecurityReadOnly) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const clip = e.clipboardData?.getData("text") || "";
    const digits = clampDigitsToFormat(isUS, extractDigits(clip, isUS));
    if (!digits) return;

    if (which === "securityId") securityTouchedRef.current = true;
    if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;

    setForm1((p) => ({ ...p, [which]: digits }));
    runFieldValidation(which, digits);

    queueMicrotask(() => {
      const el =
        which === "securityId"
          ? securityRef.current
          : confirmSecurityRef.current;
      setCaretToSlotIndex(el, templateFor(isUS).length);
    });
  };

  const handleSecFocus = (which) => () => {
    if (isSecurityReadOnly) return;
    const digits = clampDigitsToFormat(isUS, extractDigits(form1[which], isUS));
    const el =
      which === "securityId" ? securityRef.current : confirmSecurityRef.current;

    if (!digits) {
      queueMicrotask(() => setCaretToSlotIndex(el, 0));
      return;
    }
    queueMicrotask(() => setCaretToNextSlot(el, isUS, digits.length));
  };

  // Date constraints
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minStartDate = new Date(today);
  minStartDate.setMonth(minStartDate.getMonth() - 1);
  const maxStartDate = new Date(today);
  maxStartDate.setMonth(maxStartDate.getMonth() + 1);

  const minEndDate = (() => {
    if (form1.startDate) {
      const start = new Date(
        form1.startDate.getFullYear(),
        form1.startDate.getMonth(),
        form1.startDate.getDate(),
      );
      return start > today ? start : today;
    }
    return today;
  })();

  /* ------------------------------------------------------------------ */
  /*  ATT ROLE OPTIONS (from Hinge roleCatalog)                         */
  /* ------------------------------------------------------------------ */

  const attRoleOptions = (() => {
    const map = new Map();
    hingeRoleCatalog.forEach((role) => {
      const dtvRoleId = role.dtvRoleId;
      const dtvRoleName = role.dtvRoleName;
      (role.cinglepointRoles || []).forEach((cp) => {
        const cpRoleId = cp.cpRoleId;
        const cpRoleDesc = cp.cpRoleDesc;
        (cp.cpProfiles || []).forEach((profile) => {
          const cpProfileId = profile.cpProfileId;
          const cpProfileDesc = profile.cpProfileDesc;
          const key = `${cpRoleId}||${cpProfileId}`;
          if (!map.has(key)) {
            map.set(key, {
              key,
              dtvRoleId,
              dtvRoleName,
              cpRoleId,
              cpRoleDesc,
              cpProfileId,
              cpProfileDesc,
            });
          }
        });
      });
    });
    return Array.from(map.values());
  })();

  // Options for searchable ATT Role dropdown
  const attRoleSelectOptions = attRoleOptions.map((o) => ({
    value: o.key,
    label: `${o.cpRoleId} - ${o.cpProfileDesc || ""}`,
    meta: o,
  }));
  const selectedAttRoleOption =
    attRoleSelectOptions.find((o) => o.value === form1.attRoleKey) || null;

  console.log("[ATT ROLE DEBUG] Current attRoleKey:", form1.attRoleKey);

  console.log(
    "[ATT ROLE DEBUG] Selected dropdown option:",
    selectedAttRoleOption,
  );

  console.log(
    "[ATT ROLE DEBUG] Dropdown options:",
    attRoleSelectOptions.map((option) => ({
      value: option.value,
      label: option.label,
      cpRoleId: option.meta?.cpRoleId,
      cpRoleDesc: option.meta?.cpRoleDesc,
      cpProfileId: option.meta?.cpProfileId,
      cpProfileDesc: option.meta?.cpProfileDesc,
    })),
  );

  // ===== Helper: auto-select ATT role mapped to the chosen DTV role =====
  const autoSelectAttRoleForDtvRole = (roleNameOrId) => {
    if (!roleNameOrId) return null;
    try {
      const needle = String(roleNameOrId).trim().toLowerCase();
      // 1) Build a lookup of dtvRoleName(lower) -> dtvRoleId from the catalog
      let dtvIdFromName = null;
      for (const r of hingeRoleCatalog || []) {
        const nm = (r?.dtvRoleName || "").toLowerCase();
        if (nm && nm === needle) {
          dtvIdFromName = r?.dtvRoleId || null;
          break;
        }
      }
      // If the caller already passed an ID, accept that
      const maybeId = /^\d+$/.test(needle) ? needle : null;
      const targetId =
        maybeId || (dtvIdFromName ? String(dtvIdFromName) : null);

      // 2) Find a matching att role by dtvRoleId first, fallback to name match
      let match = null;
      if (targetId) {
        match = attRoleOptions.find(
          (o) =>
            String(o?.dtvRoleId || "").toLowerCase() ==
            String(targetId).toLowerCase(),
        );
      }
      if (!match) {
        match = attRoleOptions.find(
          (o) => (o?.dtvRoleName || "").toLowerCase() === needle,
        );
      }
      if (!match) return null;
      return {
        attRoleKey: `${match.cpRoleId}||${match.cpProfileId}`,
        attRoleDtvRoleId: match.dtvRoleId || "",
        attRoleDtvRoleName: match.dtvRoleName || "",
        attRoleCpRoleId: match.cpRoleId || "",
        attRoleCpRoleDesc: match.cpRoleDesc || "",
        attRoleCpProfileId: match.cpProfileId || "",
        attRoleCpProfileDesc: match.cpProfileDesc || "",
      };
    } catch {
      return null;
    }
  };

  // ===== Auto-select ATT Role on mount/changes (when Attuid Required is ON) =====
  useEffect(() => {
    // Auto-select ATT Role only when ATTUID Required is ON
    if (!attuidRequiredOn) return;

    if (!form1.role) return;
    if (!Array.isArray(attRoleOptions) || attRoleOptions.length === 0) return;

    // If current selection already matches the chosen DTV role, skip
    // if (form1.attRoleKey) {
    //     const current = attRoleOptions.find(
    //         (o) => `${o.cpRoleId}||${o.cpProfileId}` === form1.attRoleKey
    //     );

    //     if (current) {
    //         const r1 = String(current?.dtvRoleName || "").toLowerCase();
    //         const r2 = String(form1.role || "").toLowerCase();
    //         if (r1 === r2) return;
    //     }
    // }
    if (form1.attRoleKey) {
      const current = attRoleOptions.find(
        (o) => `${o.cpRoleId}||${o.cpProfileId}` === form1.attRoleKey,
      );

      // Existing ATT Role is still valid in the catalog.
      if (current) {
        const r1 = String(current?.dtvRoleName || "").toLowerCase();
        const r2 = String(form1.role || "").toLowerCase();

        if (r1 === r2) return;
      }

      // Existing ATT Role is not in the current catalog.
      // Do NOT automatically replace it during edit.
      if (!current) {
        return;
      }
    }

    const auto = autoSelectAttRoleForDtvRole(form1.role);
    if (auto) {
      setForm1((prev) => ({ ...prev, ...auto }));
      setFieldErrors((prev) => ({ ...prev, attRoleKey: "" }));
    }
  }, [attuidRequiredOn, form1.role, attRoleOptions]); // ✅ safer than .length

  /* ------------------------------------------------------------------ */
  /*  EDIT MODE LOAD (from Manage Requests overlay using npwId)         */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!npwId || !TB_SEARCH_URL) return;
    (async () => {
      try {
        setLoading(true);
        const url = `${TB_SEARCH_URL}?npwId=${encodeURIComponent(
          npwId,
        )}&action=editLoadNewOnboard`;
        const rspns = await tbGetJson(url);
        const parsed = typeof rspns === "string" ? JSON.parse(rspns) : rspns;
        if (parsed?.data?.[0]) {
          //   const mapped = mapApiToForm1(parsed.data[0]);
          //   setLookupSecExistFlag(
          //     normalizeLookupSecurityExistence(mapped?.isSecExist),
          //   );
          //   setEditData({ ...mapped, npwId }); // keep npwId
          //   setForm1(mapped);

          const mapped = mapApiToForm1(parsed.data[0]);

          setLookupSecExistFlag(
            normalizeLookupSecurityExistence(mapped?.isSecExist),
          );

          // Validate existing DTV Role against current Role Catalog.
          const existingRole = String(mapped?.role || "").trim();

          const roleExistsInCatalog = hingeRoleCatalog.some(
            (catalogRole) =>
              String(catalogRole?.dtvRoleName || "")
                .trim()
                .toLowerCase() === existingRole.toLowerCase(),
          );

          // If existing role is no longer in the current catalog,
          // do not show it as a selected dropdown value.
          if (existingRole && !roleExistsInCatalog) {
            console.log(
              "[EDIT ROLE VALIDATION] Existing role not found in catalog:",
              existingRole,
            );

            mapped.role = "";
          }

          setEditData({ ...mapped, npwId });
          setForm1(mapped);
        } else {
          navigate(location.pathname, {
            state: { toastMessage: "Item not found" },
            replace: true,
          });
        }
      } catch (e) {
        console.error("editLoadNewOnboard failed", e);
        toast.error("Failed to load item for edit.");
        navigate(location.pathname, {
          state: { toastMessage: "Item not found" },
          replace: true,
        });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwId]);

  const editLoadSearchableDDval = (val, optn, setFn) => {
    const vRaw = String(val || "").trim();
    if (!vRaw) return;
    const v = vRaw.toLowerCase();
    const list = optn || [];

    const match =
      list.find(
        (c) =>
          String(c?.label || "")
            .trim()
            .toLowerCase() === v,
      ) ||
      list.find(
        (c) =>
          String(c?.value || "")
            .trim()
            .toLowerCase() === v,
      ) ||
      list.find((c) =>
        String(c?.label || "")
          .trim()
          .toLowerCase()
          .includes(v),
      ) ||
      list.find((c) =>
        v.includes(
          String(c?.label || "")
            .trim()
            .toLowerCase(),
        ),
      );

    setFn(match || null);
  };

  const geoCountryHydratedKeyRef = useRef(null);
  const geoStateHydratedKeyRef = useRef(null);
  const geoCityHydratedKeyRef = useRef(null);

  useEffect(() => {
    if (editData !== null) {
      const ed = { ...editData };
      ed.validateSecurity = ed.securityId;
      setWorkerType(ed.worker_type || workerType);
      setForm1(ed);

      // Geo dropdown values (country/state/city) are synced in follow-up effects
      // so we don't miss state when selectedCountry is set in the same tick.
      if (ed.citizenship) {
        const citizenshipOpts = citizenshipOptions.length
          ? citizenshipOptions
          : countryOptions;
        const citizenship = resolveCitizenshipDisplay(
          ed.citizenship,
          citizenshipOpts,
        );
        if (citizenship) {
          setCitizen(
            getCitizenshipOption(citizenshipOpts, citizenship) || {
              label: citizenship,
              value: citizenship,
            },
          );
        } else {
          setCitizen(null);
          setForm1((p) => (p.citizenship ? { ...p, citizenship: "" } : p));
        }
      }
      if (ed.timeZone)
        editLoadSearchableDDval(
          ed.timeZone,
          timeZoneOptions,
          SetSelectedTimeZone,
        );

      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  // Geo: ensure Country/State/City show correctly on edit-load (including when options arrive late)
  // and do NOT reapply after user clears/changes (one-time per record key).
  useEffect(() => {
    const countryValue = firstNonEmpty(form1.country, editData?.country);
    if (!countryValue) return;
    if (!Array.isArray(countryOptions) || !countryOptions.length) return;

    const opt = getCountryOption(countryOptions, countryValue);
    if (!opt) return;
    if (selectedCountry?.value === opt.value) return;

    suppressCountryCascadeRef.current = true;
    setSelectedCountry(opt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form1.country,
    editData?.country,
    countryOptions,
    selectedCountry?.value,
  ]);

  useEffect(() => {
    if (!editData) return;
    const key = String(
      editData?.npw_id ||
        editData?.npwId ||
        editData?.client_request_id ||
        "__edit__",
    );
    if (!editData.country) return;
    if (geoCountryHydratedKeyRef.current === key) return;
    const opt = getCountryOption(countryOptions, editData.country);
    if (!opt) return;
    if (selectedCountry?.value === opt.value) {
      geoCountryHydratedKeyRef.current = key;
      return;
    }
    setSelectedCountry(opt);
    geoCountryHydratedKeyRef.current = key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, selectedCountry, countryOptions]);

  useEffect(() => {
    if (!editData) return;
    const key = String(
      editData?.npw_id ||
        editData?.npwId ||
        editData?.client_request_id ||
        "__edit__",
    );
    if (!selectedCountry) return;
    if (selectedState) return;
    if (!editData.state) return;
    if (geoStateHydratedKeyRef.current === key) return;
    editLoadSearchableDDval(editData.state, stateOptions, (opt) => {
      if (!opt) return;
      setSelectedState(opt);
      geoStateHydratedKeyRef.current = key;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, selectedCountry, selectedState, stateOptions]);

  // If we loaded editData before cp-countrylist options finished loading,
  // re-sync the citizenship Select once options arrive.
  useEffect(() => {
    if (!editData) return;
    if (ctzn) return;
    const list =
      citizenshipOptions && citizenshipOptions.length
        ? citizenshipOptions
        : countryOptions;
    if (!Array.isArray(list) || !list.length) return;
    if (!editData.citizenship) return;
    const citizenship = resolveCitizenshipDisplay(editData.citizenship, list);
    if (citizenship) {
      setCitizen(
        getCitizenshipOption(list, citizenship) || {
          label: citizenship,
          value: citizenship,
        },
      );
    } else {
      setCitizen(null);
      setForm1((p) => (p.citizenship ? { ...p, citizenship: "" } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citizenshipOptions, countryOptions, editData]);

  useEffect(() => {
    if (!editData) return;
    const key = String(
      editData?.npw_id ||
        editData?.npwId ||
        editData?.client_request_id ||
        "__edit__",
    );
    if (!selectedCountry || !selectedState || !cityOptions.length) return;
    if (selectedCity) return;
    if (!editData.city) return;
    if (geoCityHydratedKeyRef.current === key) return;
    // Common city-name abbreviations (e.g. API sends "MT DORA", dataset has "Mount Dora").
    const normCity = (vv) =>
      String(vv || "")
        .trim()
        .toLowerCase()
        .replace(/\(.*?\)/g, "")
        .replace(/,/g, " ")
        .replace(/^city and county of\s+/i, "")
        .replace(/^county of\s+/i, "")
        .replace(/^city of\s+/i, "")
        .replace(/\./g, "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .map((word) => CITY_ABBREVIATIONS[word] || word)
        .join(" ");

    const target = normCity(editData.city);
    const match =
      (cityOptions || []).find(
        (c) => normCity(c?.label) === target || normCity(c?.value) === target,
      ) ||
      (cityOptions || []).find(
        (c) =>
          normCity(c?.label).includes(target) ||
          normCity(c?.value).includes(target),
      ) ||
      (cityOptions || []).find(
        (c) =>
          target.includes(normCity(c?.label)) ||
          target.includes(normCity(c?.value)),
      );

    if (match) {
      setSelectedCity(match);
      geoCityHydratedKeyRef.current = key;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, selectedCountry, selectedState, cityOptions, selectedCity]);

  /* ------------------------------------------------------------------ */
  /*  HINGE ROLES (Role dropdown + ATT Role source)                     */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!attuidRequiredOn) {
      // When ATTUID is not requested, Role + ATT Role are not applicable.
      setDd((prev) => ({ ...prev, roles: [] }));
      setHingeRoleCatalog([]);
      setDataLoads((prev) => ({
        ...prev,
        roles: { field: "Role", status: "idle", startedAt: null },
      }));
      return;
    }
    if (!ENABLE_HINGE_ROLE_DROPDOWN || !TB_HINGE_ROLE_LIST_URL) return;

    (async () => {
      try {
        setDataLoads((prev) => ({
          ...prev,
          roles: {
            field: "Role",
            status: "loading",
            startedAt:
              prev?.roles?.status === "loading" && prev?.roles?.startedAt
                ? prev.roles.startedAt
                : Date.now(),
          },
        }));
        const roleResp = await tbGetJson(TB_HINGE_ROLE_LIST_URL);

        // Support multiple shapes:
        // - Hinge direct: { roles: [...] }
        // - TB wrapper:   { requestId, timing, response: { roles: [...] } }
        // - Array:        [ ... ]
        const payload =
          roleResp?.response && typeof roleResp.response === "object"
            ? roleResp.response
            : roleResp;

        let catalog = [];
        let dtvRoleNames = [];

        if (Array.isArray(payload?.roles)) {
          catalog = payload.roles;
          dtvRoleNames = [
            ...new Set(
              payload.roles
                .map(
                  (r) =>
                    r?.dtvRoleName ||
                    r?.dtv_role_name ||
                    r?.roleName ||
                    r?.role_name ||
                    r?.name,
                )
                .filter(Boolean),
            ),
          ];
        } else if (Array.isArray(payload)) {
          catalog = payload;
          dtvRoleNames = [
            ...new Set(
              payload
                .map(
                  (r) =>
                    r?.dtvRoleName ||
                    r?.dtv_role_name ||
                    r?.roleName ||
                    r?.role_name ||
                    r?.name,
                )
                .filter(Boolean),
            ),
          ];
        }

        if (catalog.length) {
          setHingeRoleCatalog(catalog);
        }
        if (dtvRoleNames.length) {
          setDd((prev) => ({
            ...prev,
            roles: dtvRoleNames,
          }));
        }

        setDataLoads((prev) => ({
          ...prev,
          roles: { ...prev.roles, field: "Role", status: "success" },
        }));
      } catch (err) {
        console.warn("Hinge role dropdown fetch failed", err);
        setDataLoads((prev) => ({
          ...prev,
          roles: { ...prev.roles, field: "Role", status: "error" },
        }));
      }
    })();
  }, [attuidRequiredOn]);

  /* ------------------------------------------------------------------ */
  /*  LOAD DRAFT BY /onboarding/new/:id                                 */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!id) return;

    // Route supports two patterns:
    //   1) /onboarding/new/<npw_id>                -> load from Approval Details (draft/edit)
    //   2) /onboarding/new/DTVID=<dtvId> (encoded) -> call Lookup API and prefill the NEW onboarding form
    // Examples:
    //   /onboarding/new/259
    //   /onboarding/new/npw_id%3D259
    //   /onboarding/new/DTVID%3Ddt0115.uat

    const parseRouteToken = (token) => {
      const raw = decodeURIComponent(String(token || "").trim());
      if (!raw) return { mode: null, value: null, raw: "" };

      // Plain numeric => npw_id
      if (/^\d+$/.test(raw)) return { mode: "npw", value: raw, raw };

      // key=value (encoded) => detect key
      const idx = raw.indexOf("=");
      if (idx > 0) {
        const k = raw.slice(0, idx).trim();
        const v = raw.slice(idx + 1).trim();
        const ku = k.toUpperCase();
        if (
          ku === "DTVID" ||
          ku === "DTV_ID" ||
          ku === "DTV" ||
          ku === "DTVID"
        ) {
          return { mode: "dtv", value: v, raw };
        }
        if (
          ku === "NPWID" ||
          ku === "NPW_ID" ||
          ku === "NPW" ||
          ku === "NPWID"
        ) {
          return { mode: "npw", value: v, raw };
        }
        // If unknown key but value is numeric, assume npw_id
        if (/^\d+$/.test(v)) return { mode: "npw", value: v, raw };
        // If unknown key but value looks like dtvId (contains dot / letters), assume dtv
        if (/^[A-Za-z0-9._-]+$/.test(v)) return { mode: "dtv", value: v, raw };
      }

      // Fallback heuristic: if token contains 'dtv' treat as dtvId, else treat as npw_id
      const u = raw.toUpperCase();
      if (u.includes("DTV"))
        return {
          mode: "dtv",
          value: raw.replace(/^DTVID\s*:?/i, "").trim(),
          raw,
        };
      return { mode: "npw", value: raw, raw };
    };

    const { mode, value } = parseRouteToken(id);

    // --- DTV prefill path ---
    const prefillFromDtvId = async (dtvId) => {
      if (!dtvId) return;
      if (!TB_HINGE_DTV_LOOKUP_URL) {
        toast.error("Lookup API is not configured (TB_HINGE_DTV_LOOKUP_URL).", {
          toastId: "dtv-prefill-no-url",
        });
        return;
      }

      // Ensure this stays in NEW mode (do NOT treat this as an existing npw_id draft)
      setEditData(null);
      setDraftNpwId(null);
      try {
        SS.removeItem("tb_draft_npw_id");
      } catch {}

      const pickFirst = (...vals) => {
        for (const v of vals) {
          if (v === 0) return 0;
          if (v === false) return false;
          if (v === null || v === undefined) continue;
          const s = String(v).trim();
          if (s) return s;
        }
        return "";
      };

      const toDigits = (v) => String(v || "").replace(/\D/g, "");
      const toYesNo = (v) => {
        if (
          v === true ||
          String(v).toLowerCase() === "true" ||
          String(v).toLowerCase() === "yes"
        )
          return "Yes";
        if (
          v === false ||
          String(v).toLowerCase() === "false" ||
          String(v).toLowerCase() === "no"
        )
          return "No";
        return "";
      };

      const parseMdy = (s) => {
        const v = String(s || "").trim();
        // Expected: MM/DD/YYYY (Hinge)
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
        if (m) {
          const mm = Number(m[1]);
          const dd = Number(m[2]);
          const yy = Number(m[3]);
          const d = new Date(yy, mm - 1, dd);
          return isNaN(+d) ? null : d;
        }
        const d = v ? new Date(v) : null;
        return d && !isNaN(+d) ? d : null;
      };

      const normWorkerTypeTax = (v) => {
        const s = String(v || "").trim();
        if (!s) return "";
        const lc = s.toLowerCase();
        if (lc === "w-2" || lc === "w2" || lc === "w2 / 1099") return "W-2";
        if (lc === "1099") return "1099";
        if (lc === "international") return "International";
        // unrecognized value: don't pass arbitrary text into the dropdown field
        return "";
      };

      const mergeIfBlank = (prevVal, nextVal) => {
        const p = String(prevVal ?? "").trim();
        const n = String(nextVal ?? "").trim();
        return p ? prevVal : n ? nextVal : prevVal;
      };

      try {
        setLoading(true);
        const base = TB_HINGE_DTV_LOOKUP_URL;
        const sep = base.includes("?") ? "&" : "?";
        const url = `${base}${sep}dtvId=${encodeURIComponent(dtvId)}`;
        const resp = await tbGetJson(url);
        const lookupSecFlag = getLookupSecurityExistenceFromPayload(resp);
        setLookupSecExistFlag(lookupSecFlag);

        // Support multiple response shapes (tb-hinge proxy / direct hinge)
        // Your TB-Hinge lookup returns shape:
        //   { requestId, timing, response: { dtvAnchorId, firstName, ... } }
        // (no personList array). We therefore accept response as the "person" object.
        const unwrapLookupPayload = (r) => {
          let p = r;
          for (let i = 0; i < 3; i++) {
            if (
              p &&
              typeof p === "object" &&
              Object.prototype.hasOwnProperty.call(p, "data") &&
              p.data &&
              typeof p.data === "object"
            ) {
              p = p.data;
              continue;
            }
            if (
              p &&
              typeof p === "object" &&
              Object.prototype.hasOwnProperty.call(p, "response")
            ) {
              const rr = p.response;
              if (typeof rr === "string") {
                try {
                  p = JSON.parse(rr);
                  continue;
                } catch {
                  // ignore
                }
              }
              if (rr && typeof rr === "object") {
                p = rr;
                continue;
              }
            }
            break;
          }
          return p;
        };

        const payload = unwrapLookupPayload(resp);
        const person =
          payload?.personList?.[0] ||
          payload?.person?.[0] ||
          payload?.person ||
          payload?.data?.[0] ||
          payload?.items?.[0] ||
          // If payload itself is the person object (fields like firstName / dtvAnchorId), use it.
          (payload &&
          (payload.firstName ||
            payload.lastName ||
            payload.dtvAnchorId ||
            payload.primaryEmail ||
            payload.attuid ||
            payload.attUid ||
            payload.attUID)
            ? payload
            : null) ||
          (Array.isArray(payload) ? payload[0] : null);

        if (!person) {
          toast.error("Lookup returned no data for the provided DTVID.", {
            toastId: "dtv-prefill-none",
          });
          return;
        }

        // ATTUID from lookup (when present, send it in payload on create/edit)
        const lookupAttuid = pickFirst(
          person.attuid,
          person.attUid,
          person.attUID,
        );
        setLookupAttuid(String(lookupAttuid ?? "").trim());
        // console.log("DTV Lookup person data:", person);
        const firstName = pickFirst(person.firstName, person.first_name);
        const lastName = pickFirst(person.lastName, person.last_name);
        const emailId = pickFirst(
          person.secondaryEmail,
          person.primaryEmail,
          person.email,
          person.primary_email,
        );
        const phone = toDigits(
          pickFirst(person.phone, person.phoneNumber, person.mobile),
        );

        let citizenship = pickFirst(
          person.countryOfCitizenship,
          person.citizenship,
        );

        const startDate = parseMdy(
          pickFirst(
            person.effectiveStartDate,
            person.startDate,
            person.start_date,
          ),
        );
        const endDate = parseMdy(
          pickFirst(person.effectiveEndDate, person.endDate, person.end_date),
        );

        const sponsorId = pickFirst(
          person.employeeSponsor,
          person.sponsor,
          person.manager,
          person.approverDtvId,
        );
        const mgrId = pickFirst(
          person.supervisorDtvAnchorId,
          person.supervisorAttuid,

          person.webphoneManagerId,
          person.webphone_manager,
        );

        const vendorFromHinge = pickFirst(person.supplierName, person.vendor);
        const supplierReference = pickFirst(
          person.supplierReference,
          person.supplier_reference,
        );
        //const supplierReference = pickFirst(person.supplierReference, person.supplier_reference);
        const contractPo = pickFirst(
          person.contractNumber,
          person.contractPo,
          person.contract,
          person.contract_po,
        );
        const poNum = pickFirst(
          person.poNumber,
          person.poNum,
          person.po_number,
        );
        const lineNum = pickFirst(
          person.lineNumber,
          person.lineNum,
          person.line_number,
        );

        const nonBillable = toYesNo(
          pickFirst(person.nonBillableIndicator, person.non_billable),
        );
        const occupancy = String(
          pickFirst(person.occupancyIndicator, person.occupancy),
        )
          .trim()
          .toLowerCase();
        const occupancyCode =
          occupancy === "virtual" || occupancy === "office" ? occupancy : "";

        const roleName = pickFirst(person.dtvRoleName, person.role);
        const workerTypeTax = normWorkerTypeTax(
          pickFirst(
            person.w2_1099,
            person.workerTypeTax,
            person.worker_type_tax,
          ),
        );

        // Worker/NPW type comes from lookup as `personType`.
        const npwTypeFromHinge = String(
          pickFirst(
            person.personType,
            person.employment_type,
            person.npw_type,
          ) || "",
        ).trim();

        // DTV anchor id from lookup payload (used to persist dtvId in the request)
        const anchorId = pickFirst(
          person.dtvAnchorId,
          person.dtvId,
          person.dtv_id,
          dtvId,
        );

        // DTV anchor id from lookup payload (used to persist dtvId in the request)
        //const anchorId = pickFirst(person.dtvAnchorId, person.dtvId, person.dtv_id, dtv);
        let country = normalizeWorkingCountryName(
          pickFirst(
            person.countryOfWorking,
            person.country,
            person.countryOfWork,
            person.country_of_working,
          ),
        );
        const state = pickFirst(
          person.state,
          person.stateCode,
          person.state_code,
        );
        const city = pickFirst(person.city, person.cityName, person.city_name);
        const timeZone = pickFirst(
          person.timeZone,
          person.timezone,
          person.time_zone,
        );
        /*TODO:pentest*/ const npwId = pickFirst(
          person.npwId,
          person.npwid,
          person.npw_id,
        );

        /*Patch: handled country data issue for USA */
        if (
          String(country || "")
            .trim()
            .toUpperCase() === "USA"
        ) {
          country = "";
        }
        if (citizenship) {
          const citizenshipOpts = citizenshipOptions.length
            ? citizenshipOptions
            : countryOptions;
          citizenship = resolveCitizenshipDisplay(citizenship, citizenshipOpts);
        }

        // ATTUID Required (lookup-prefill rule):
        // For Active/Inactive/Terminated lookup results, derive ATTUID Required from
        // cinglepointRoleId / cinglepointProfileId (both empty => No, else Yes).
        // For other statuses, fall back to legacy isAttuidRequested/attuidRequired fields.
        const statusRaw = pickFirst(
          person.personStatus,
          person.status,
          person.employeeStatus,
          person.person_status,
        );
        const statusLc = String(statusRaw || "")
          .trim()
          .toLowerCase();
        const isExistingLookupStatus =
          /(^|\b)(active|inactive|terminated)(\b|$)/.test(statusLc);

        const cleanLookupVal = (v) => {
          const s = String(v ?? "").trim();
          if (!s) return "";
          const lc = s.toLowerCase();
          if (lc === "null" || lc === "undefined") return "";
          return s;
        };

        const spRoleId =
          cleanLookupVal(person.cinglepointRoleId) ||
          cleanLookupVal(person.singlepointRoleId) ||
          cleanLookupVal(person.cinglePointRoleId) ||
          cleanLookupVal(person.singlePointRoleId);
        const spProfileId =
          cleanLookupVal(person.cinglepointProfileId) ||
          cleanLookupVal(person.singlepointProfileId) ||
          cleanLookupVal(person.cinglePointProfileId) ||
          cleanLookupVal(person.singlePointProfileId);

        const attuidFromSinglePoint = Boolean(spRoleId || spProfileId);
        const isAttuidRequested = isExistingLookupStatus
          ? attuidFromSinglePoint
          : person.isAttuidRequested || person.attuidRequired || false;
        const shouldLockSecurityForExisting =
          isExistingLookupStatus && lookupSecFlag === "yes";

        const attuidYesNo = isAttuidRequested ? "Yes" : "No";

        setForm1((prev) => ({
          ...prev,

          // route/identity
          dtvId: anchorId || dtvId,

          attuid: mergeIfBlank(prev.attuid, String(lookupAttuid ?? "").trim()),

          // personal
          firstName: mergeIfBlank(prev.firstName, firstName),
          lastName: mergeIfBlank(prev.lastName, lastName),
          email: mergeIfBlank(prev.email, emailId),
          phone: mergeIfBlank(prev.phone, phone),

          // sponsor / webphone
          sponsor: mergeIfBlank(prev.sponsor, sponsorId),
          webphoneManagerId: mergeIfBlank(prev.webphoneManagerId, mgrId),

          // role + ATT-related fields (only fill if blank)
          role: mergeIfBlank(prev.role, roleName),
          workerTypeTax: mergeIfBlank(prev.workerTypeTax, workerTypeTax),
          nonBillable: mergeIfBlank(prev.nonBillable, nonBillable),
          occupancyCode: mergeIfBlank(prev.occupancyCode, occupancyCode),

          // NPW/Worker type from lookup (personType); override the default placeholder.
          npwType:
            prev.npwType && prev.npwType !== "Non-staff Supp Worker"
              ? prev.npwType
              : npwTypeFromHinge || prev.npwType,
          npwTypeRaw: prev.npwTypeRaw || npwTypeFromHinge || null,

          country: mergeIfBlank(prev.country, country),
          city: mergeIfBlank(prev.city, city),
          state: mergeIfBlank(prev.state, state),
          timeZone: mergeIfBlank(prev.timeZone, timeZone),
          isAttuidRequested: attuidYesNo,
          attuidRequired: attuidYesNo,

          // vendor/contract info
          vendor: mergeIfBlank(prev.vendor, vendorFromHinge),
          supplierReference: mergeIfBlank(
            prev.supplierReference,
            supplierReference,
          ),
          contractPo: mergeIfBlank(prev.contractPo, contractPo),
          poNum: mergeIfBlank(prev.poNum, poNum),
          lineNum: mergeIfBlank(prev.lineNum, lineNum),

          // citizenship + dates
          citizenship: mergeIfBlank(prev.citizenship, citizenship),
          startDate: prev.startDate || startDate || null,
          endDate: prev.endDate || endDate || null,
        }));

        if (citizenship) {
          const citizenshipOpts = citizenshipOptions.length
            ? citizenshipOptions
            : countryOptions;
          setCitizen(
            getCitizenshipOption(citizenshipOpts, citizenship) || {
              label: citizenship,
              value: citizenship,
            },
          );
        }
        if (country) {
          const norm = (v) =>
            String(v || "")
              .trim()
              .toLowerCase();
          const normCity = (v) =>
            norm(v)
              .replace(/\(.*?\)/g, "")
              .replace(/,/g, " ")
              .replace(/^city and county of\s+/i, "")
              .replace(/^county of\s+/i, "")
              .replace(/^city of\s+/i, "")
              .replace(/\./g, "")
              .replace(/\s+/g, " ")
              .trim()
              .split(" ")
              .map((word) => CITY_ABBREVIATIONS[word] || word)
              .join(" ");

          // countryOptions can be stale/empty here if this closure was created before
          // the country-list effect populated it; recompute fresh as a fallback.
          const freshCountryOptions = countryOptions.length
            ? countryOptions
            : Country.getAllCountries().map((c) => ({
                label: c.name,
                value: c.isoCode,
              }));
          const cOpt = getCountryOption(freshCountryOptions, country);
          if (cOpt) {
            suppressCountryCascadeRef.current = true;
            setSelectedCountry(cOpt);

            const allStates = State.getStatesOfCountry(cOpt.value);
            if (state) {
              const targetState = norm(state);
              const sMatch = allStates.find(
                (s) =>
                  norm(s.name) === targetState ||
                  norm(s.isoCode) === targetState,
              );
              if (sMatch) {
                const sOpt = {
                  label: sMatch.name,
                  value: sMatch.isoCode,
                };
                suppressStateCascadeRef.current = true;
                setSelectedState(sOpt);

                if (city) {
                  const allCities = City.getCitiesOfState(
                    cOpt.value,
                    sMatch.isoCode,
                  );
                  const targetCity = normCity(city);
                  const cityMatch =
                    allCities.find((x) => normCity(x.name) === targetCity) ||
                    allCities.find((x) =>
                      normCity(x.name).includes(targetCity),
                    ) ||
                    allCities.find((x) =>
                      targetCity.includes(normCity(x.name)),
                    );
                  if (cityMatch) {
                    setSelectedCity({
                      label: cityMatch.name,
                      value: cityMatch.name,
                    });
                  }
                }
              }
            }
          }
        }
        if (timeZone) {
          const tzOpt = timeZoneOptions.find(
            (z) => z.label.includes(timeZone) || z.value === timeZone,
          );
          if (tzOpt) SetSelectedTimeZone(tzOpt);
        }

        setStep(1);
        toast.info("Loaded Lookup data for DTVID.", {
          toastId: "dtv-prefill-ok",
        });
      } catch (err) {
        console.warn("DTVID lookup prefill failed", err);
        toast.error("Lookup API call failed for the provided DTVID.", {
          toastId: "dtv-prefill-failed",
        });
      } finally {
        setLoading(false);
      }
    };

    // If the route token indicates DTVID, prefill and stop.
    if (mode === "dtv") {
      prefillFromDtvId(String(value || "").trim());
      return;
    }

    // --- NPW draft/edit load path (Approval Details) ---
    (async () => {
      try {
        setLoading(true);
        const resp = await apiFetchOnboardingById(value || id);

        // Track route draft id as current npw_id for future PUT submissions
        try {
          SS.setItem("tb_draft_npw_id", String(value || id));
        } catch {}
        setDraftNpwId(String(value || id));

        const draft =
          resp?.data && typeof resp.data === "object" ? resp.data : resp;

        const mapped = mapApiToForm1(draft || {});
        setLookupSecExistFlag(
          normalizeLookupSecurityExistence(mapped?.isSecExist),
        );
        setForm1(mapped);
        setEditData(mapped);

        // Pre-select country / state / city for edit mode
        if (mapped.country) {
          const allCountries = Country.getAllCountries();
          const cOpt = getCountryOption(countryOptions, mapped.country);
          if (cOpt) {
            setSelectedCountry(cOpt);

            const allStates = State.getStatesOfCountry(cOpt.value);
            if (mapped.state) {
              const sMatch = allStates.find((s) => s.name === mapped.state);
              if (sMatch) {
                const sOpt = {
                  label: sMatch.name,
                  value: sMatch.isoCode,
                };
                setSelectedState(sOpt);

                if (mapped.city) {
                  const allCities = City.getCitiesOfState(
                    cOpt.value,
                    sMatch.isoCode,
                  );
                  const normCity = (v) =>
                    String(v || "")
                      .trim()
                      .toLowerCase()
                      .replace(/\(.*?\)/g, "")
                      .replace(/,/g, " ")
                      .replace(/^city and county of\s+/i, "")
                      .replace(/^county of\s+/i, "")
                      .replace(/^city of\s+/i, "")
                      .replace(/\./g, "")
                      .replace(/\s+/g, " ")
                      .trim()
                      .split(" ")
                      .map((word) => CITY_ABBREVIATIONS[word] || word)
                      .join(" ");
                  const targetCity = normCity(mapped.city);
                  const cityMatch =
                    allCities.find((x) => x.name === mapped.city) ||
                    allCities.find((x) => normCity(x.name) === targetCity) ||
                    allCities.find((x) =>
                      normCity(x.name).includes(targetCity),
                    ) ||
                    allCities.find((x) =>
                      targetCity.includes(normCity(x.name)),
                    );
                  if (cityMatch) {
                    setSelectedCity({
                      label: cityMatch.name,
                      value: cityMatch.name,
                    });
                  }
                }
              }
            }
          }
        }

        if (mapped.citizenship) {
          const citizenshipOpts = citizenshipOptions.length
            ? citizenshipOptions
            : countryOptions;
          const citizenship = resolveCitizenshipDisplay(
            mapped.citizenship,
            citizenshipOpts,
          );
          setCitizen(
            citizenship
              ? getCitizenshipOption(citizenshipOpts, citizenship) || {
                  label: citizenship,
                  value: citizenship,
                }
              : null,
          );
        }
        if (mapped.timeZone) {
          const tzOpt = timeZoneOptions.find(
            (z) =>
              z.label.includes(mapped.timeZone) || z.value === mapped.timeZone,
          );
          if (tzOpt) SetSelectedTimeZone(tzOpt);
        }

        setStep(2);
        toast.info("Loaded draft into review.", {
          toastId: "load-draft",
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load draft.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ------------------------------------------------------------------ */
  /*  COUNTRY / STATE / CITY DROPDOWNS                                 */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const countries = Country.getAllCountries().map((c) => ({
      label: c.name,
      value: c.isoCode,
    }));
    setCountryOptions(countries);
  }, []);

  // ✅ Don't wipe state/city when we're in edit mode and just syncing options
  useEffect(() => {
    if (selectedCountry) {
      const states = State.getStatesOfCountry(selectedCountry.value).map(
        (s) => ({
          label: s.name,
          value: s.isoCode,
        }),
      );
      setStateOptions(states);
    } else {
      setStateOptions([]);
    }

    // Programmatic prefill: keep any state/city we just set.
    if (suppressCountryCascadeRef.current) {
      suppressCountryCascadeRef.current = false;
      return;
    }

    if (!editData && !isEditRoute) {
      // new onboarding: user changed country manually, reset state/city
      setSelectedState(null);
      setCityOptions([]);
      setSelectedCity(null);
      setForm1((p) => ({
        ...p,
        country: selectedCountry?.label || "",
        state: "",
        city: "",
      }));
    } else {
      // edit load: keep state/city as already mapped
      setForm1((p) => ({
        ...p,
        country: selectedCountry?.label || p.country,
      }));
    }

    // If user changes country after initial selection, keep Security ID digits.
    // The formatting/validation rule updates automatically (8 vs 9 digits).
    const nextCountry = selectedCountry?.label || "";
    const prevCountry = prevCountryLabelRef.current;
    prevCountryLabelRef.current = nextCountry;
    if (
      !isSecurityReadOnly &&
      prevCountry &&
      nextCountry &&
      prevCountry !== nextCountry
    ) {
      setShowSecurityId(false);
      setShowConfirmSecurityId(false);
      setFieldErrors((prev) => ({
        ...prev,
        securityId: "",
        validateSecurity: "",
      }));
    }
  }, [selectedCountry, editData]);

  useEffect(() => {
    const phoneVal = String(form1.phone || "").trim();
    if (!phoneVal) {
      setFieldErrors((prev) => ({ ...prev, phone: "" }));
      return;
    }
    runFieldValidation("phone", phoneVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form1.country]);

  useEffect(() => {
    if (selectedCountry && selectedState) {
      const cities = City.getCitiesOfState(
        selectedCountry.value,
        selectedState.value,
      ).map((city) => ({
        label: city.name,
        value: city.name,
      }));
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }

    // Programmatic prefill: keep any city we just set.
    if (suppressStateCascadeRef.current) {
      suppressStateCascadeRef.current = false;
      return;
    }

    if (!editData && !isEditRoute) {
      setSelectedCity(null);
      setForm1((p) => ({
        ...p,
        state: selectedState?.label || "",
        city: "",
      }));
    }
  }, [selectedState, selectedCountry, editData]);

  const handleCityChange = (opt) => {
    setSelectedCity(opt);
    setForm1((prev) => ({
      ...prev,
      city: opt?.label || "",
      state: selectedState?.label || "",
      country: selectedCountry?.label || "",
    }));
    setFieldErrors((prev) => ({
      ...prev,
      city: "",
    }));
  };
  /*  Hinge pre-load by DTV ID (for new onboarding)                     */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!ENABLE_DTV_PREFILL || !TB_HINGE_DTV_LOOKUP_URL) return;
    // Do not prefill if we are editing/loading an existing record (npw_id context)
    if (editData || id || npwId || draftNpwId) return;

    const search = new URLSearchParams(location.search);
    const dtvFromQuery = search.get("dtvId") || search.get("dtvid");
    const dtvFromState = location.state?.dtvId;
    const dtv = (dtvFromQuery || dtvFromState || "").trim();
    if (!dtv) return;

    const pickFirst = (...vals) => {
      for (const v of vals) {
        if (v === 0) return 0;
        if (v === false) return false;
        if (v === null || v === undefined) continue;
        const s = String(v).trim();
        if (s) return s;
      }
      return "";
    };

    const toDigits = (v) => String(v || "").replace(/\D/g, "");
    const toYesNo = (v) => {
      if (
        v === true ||
        String(v).toLowerCase() === "true" ||
        String(v).toLowerCase() === "yes"
      )
        return "Yes";
      if (
        v === false ||
        String(v).toLowerCase() === "false" ||
        String(v).toLowerCase() === "no"
      )
        return "No";
      return "";
    };

    const parseMdy = (s) => {
      const v = String(s || "").trim();
      // Expected: MM/DD/YYYY (Hinge)
      const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
      if (m) {
        const mm = Number(m[1]);
        const dd = Number(m[2]);
        const yy = Number(m[3]);
        const d = new Date(yy, mm - 1, dd);
        return isNaN(+d) ? null : d;
      }
      // fallback: allow ISO-ish
      const d = v ? new Date(v) : null;
      return d && !isNaN(+d) ? d : null;
    };

    const normWorkerTypeTax = (v) => {
      const s = String(v || "").trim();
      if (!s) return "";
      const lc = s.toLowerCase();
      if (lc === "w-2" || lc === "w2" || lc === "w2 / 1099") return "W-2";
      if (lc === "1099") return "1099";
      if (lc === "international") return "International";
      // unrecognized value: don't pass arbitrary text into the dropdown field
      return "";
    };

    const mergeIfBlank = (prevVal, nextVal) => {
      const p = String(prevVal ?? "").trim();
      const n = String(nextVal ?? "").trim();
      return p ? prevVal : n ? nextVal : prevVal;
    };

    (async () => {
      try {
        setLoading(true);
        const base = TB_HINGE_DTV_LOOKUP_URL;
        const sep = base.includes("?") ? "&" : "?";
        const url = `${base}${sep}dtvId=${encodeURIComponent(dtv)}`;
        const resp = await tbGetJson(url);
        const lookupSecFlag = getLookupSecurityExistenceFromPayload(resp);
        setLookupSecExistFlag(lookupSecFlag);

        // Support multiple response shapes (tb-hinge proxy / direct hinge)
        // Your TB-Hinge lookup commonly returns:
        //   { requestId, timing, response: { dtvAnchorId, firstName, ... } }
        // i.e., NOT a personList array. Accept response itself as the person object.
        const unwrapLookupPayload = (r) => {
          let p = r;
          for (let i = 0; i < 3; i++) {
            if (
              p &&
              typeof p === "object" &&
              Object.prototype.hasOwnProperty.call(p, "data") &&
              p.data &&
              typeof p.data === "object"
            ) {
              p = p.data;
              continue;
            }
            if (
              p &&
              typeof p === "object" &&
              Object.prototype.hasOwnProperty.call(p, "response")
            ) {
              const rr = p.response;
              if (typeof rr === "string") {
                try {
                  p = JSON.parse(rr);
                  continue;
                } catch {
                  // ignore
                }
              }
              if (rr && typeof rr === "object") {
                p = rr;
                continue;
              }
            }
            break;
          }
          return p;
        };

        const payload = unwrapLookupPayload(resp);
        const person =
          payload?.personList?.[0] ||
          payload?.person?.[0] ||
          payload?.person ||
          payload?.data?.[0] ||
          payload?.items?.[0] ||
          (payload &&
          (payload.firstName ||
            payload.lastName ||
            payload.dtvAnchorId ||
            payload.primaryEmail ||
            payload.attuid ||
            payload.attUid ||
            payload.attUID)
            ? payload
            : null) ||
          (Array.isArray(payload) ? payload[0] : null);

        if (!person) return;

        // ATTUID from lookup (sometimes missing for certain records/environments).
        const lookupAttuid = pickFirst(
          person.attuid,
          person.attUid,
          person.attUID,
        );
        const hasLookupAttuid = Boolean(String(lookupAttuid ?? "").trim());
        setLookupAttuid(String(lookupAttuid ?? "").trim());

        // Active/Terminated DTV lookup should behave like an edit-only Security ID UI.
        const statusRaw = pickFirst(
          person.personStatus,
          person.status,
          person.employeeStatus,
          person.person_status,
        );
        const statusLc = String(statusRaw || "")
          .trim()
          .toLowerCase();
        const isExistingRecord =
          /(^|\b)(active|inactive|terminated)(\b|$)/.test(statusLc);

        // Rule change:
        // - For FTE Nominator: keep legacy behavior (if ATTUID is missing, prompt for SSN/Security ID like CREATE).
        // - For all other roles: treat Active/Inactive/Terminated as an edit-like context regardless of ATTUID,
        //   so the Security ID/SSN shows Edit/Disable functionality.
        const roleKey = String(getFreshSession().role || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, " ")
          .trim();
        const isFteNominator = roleKey === "fte nominator";
        const lockSecurityForExisting =
          isExistingRecord &&
          lookupSecFlag === "yes" &&
          (hasLookupAttuid || !isFteNominator);

        setDtvPrefillExistingRecord(isExistingRecord);
        setDtvPrefillPersonStatus(statusRaw || "");

        const last4Digits = extractDigits(
          pickFirst(
            person.ssnLast4d,
            person.ssn_last4d,
            person.ssnLast4,
            person.ssn_last4,
          ),
        );
        if (lockSecurityForExisting && !securityInitCapturedRef.current) {
          securityInitCapturedRef.current = true;
          setSecurityInitialDigits(last4Digits || "");

          const editAttuidOn =
            form1.attuidRequired === "Yes" ||
            form1.attuidRequired === "true" ||
            form1.isAttuidRequested === "Yes" ||
            form1.isAttuidRequested === true;
          securityInitialIsUSRef.current =
            editAttuidOn && isUnitedStatesSelected(form1.country);
        }

        const firstName = pickFirst(person.firstName, person.first_name);
        const lastName = pickFirst(person.lastName, person.last_name);
        const emailId = pickFirst(
          person.secondaryEmail,
          person.primaryEmail,
          person.email,
          person.primary_email,
        );
        const phone = toDigits(
          pickFirst(person.phone, person.phoneNumber, person.mobile),
        );

        let citizenship = pickFirst(
          person.countryOfCitizenship,
          person.citizenship,
        );

        const startDate = parseMdy(
          pickFirst(
            person.effectiveStartDate,
            person.startDate,
            person.start_date,
          ),
        );
        const endDate = parseMdy(
          pickFirst(person.effectiveEndDate, person.endDate, person.end_date),
        );

        const sponsorId = pickFirst(
          person.employeeSponsor,
          person.sponsor,
          person.manager,
          person.approverDtvId,
        );
        const mgrId = pickFirst(
          person.supervisorDtvAnchorId,
          person.supervisorAttuid,

          person.webphoneManagerId,
          person.webphone_manager,
        );

        const vendorFromHinge = pickFirst(person.supplierName, person.vendor);
        const supplierReference = pickFirst(
          person.supplierReference,
          person.supplier_reference,
        );
        const contractPo = pickFirst(
          person.contractNumber,
          person.contractPo,
          person.contract,
          person.contract_po,
        );
        const poNum = pickFirst(
          person.poNumber,
          person.poNum,
          person.po_number,
        );
        const lineNum = pickFirst(
          person.lineNumber,
          person.lineNum,
          person.line_number,
        );

        const nonBillable = toYesNo(
          pickFirst(person.nonBillableIndicator, person.non_billable),
        );
        const occupancy = String(
          pickFirst(person.occupancyIndicator, person.occupancy),
        )
          .trim()
          .toLowerCase();
        const occupancyCode =
          occupancy === "virtual" || occupancy === "office" ? occupancy : "";

        const roleName = pickFirst(person.dtvRoleName, person.role);
        const workerTypeTax = normWorkerTypeTax(
          pickFirst(
            person.w2_1099,
            person.workerTypeTax,
            person.worker_type_tax,
          ),
        );

        // Worker/NPW type comes from lookup as `personType`.
        const npwTypeFromHinge = String(
          pickFirst(
            person.personType,
            person.employment_type,
            person.npw_type,
          ) || "",
        ).trim();

        // DTV anchor id from lookup payload (prefer payload value if present)
        const anchorId = pickFirst(
          person.dtvAnchorId,
          person.dtvId,
          person.dtv_id,
          dtv,
        );
        let country = normalizeWorkingCountryName(
          pickFirst(
            person.countryOfWorking,
            person.country,
            person.countryOfWork,
            person.country_of_working,
          ),
        );
        const state = pickFirst(
          person.state,
          person.stateCode,
          person.state_code,
        );
        const city = pickFirst(person.city, person.cityName, person.city_name);
        const timeZone = pickFirst(
          person.timeZone,
          person.timezone,
          person.time_zone,
        );

        if (
          String(country || "")
            .trim()
            .toUpperCase() === "USA"
        ) {
          country = "";
        }
        if (citizenship) {
          const citizenshipOpts = citizenshipOptions.length
            ? citizenshipOptions
            : countryOptions;
          citizenship = resolveCitizenshipDisplay(citizenship, citizenshipOpts);
        }

        setForm1((prev) => ({
          ...prev,

          // route/identity
          dtvId: anchorId || dtv,

          attuid: mergeIfBlank(prev.attuid, String(lookupAttuid ?? "").trim()),

          // personal
          firstName: mergeIfBlank(prev.firstName, firstName),
          lastName: mergeIfBlank(prev.lastName, lastName),
          email: mergeIfBlank(prev.email, emailId),
          phone: mergeIfBlank(prev.phone, phone),

          // sponsor / webphone
          sponsor: mergeIfBlank(prev.sponsor, sponsorId),
          webphoneManagerId: mergeIfBlank(prev.webphoneManagerId, mgrId),

          // role + ATT-related fields (only fill if blank)
          role: mergeIfBlank(prev.role, roleName),
          workerTypeTax: mergeIfBlank(prev.workerTypeTax, workerTypeTax),
          nonBillable: mergeIfBlank(prev.nonBillable, nonBillable),
          occupancyCode: mergeIfBlank(prev.occupancyCode, occupancyCode),
          country: mergeIfBlank(prev.country, country),
          state: mergeIfBlank(prev.state, state),
          city: mergeIfBlank(prev.city, city),
          timeZone: mergeIfBlank(prev.timeZone, timeZone),

          // NPW/Worker type from lookup (personType); override the default placeholder.
          npwType:
            prev.npwType && prev.npwType !== "Non-staff Supp Worker"
              ? prev.npwType
              : npwTypeFromHinge || prev.npwType,
          npwTypeRaw: prev.npwTypeRaw || npwTypeFromHinge || null,

          // vendor/contract info
          vendor: mergeIfBlank(prev.vendor, vendorFromHinge),
          supplierReference: mergeIfBlank(
            prev.supplierReference,
            supplierReference,
          ),
          contractPo: mergeIfBlank(prev.contractPo, contractPo),
          poNum: mergeIfBlank(prev.poNum, poNum),
          lineNum: mergeIfBlank(prev.lineNum, lineNum),

          // citizenship + dates
          citizenship: mergeIfBlank(prev.citizenship, citizenship),
          startDate: prev.startDate || startDate || null,
          endDate: prev.endDate || endDate || null,

          // Security ID: when only last-4 is known (Active/Inactive/Terminated), show masked last-4 in locked mode.
          securityId:
            lockSecurityForExisting &&
            last4Digits &&
            !extractDigits(prev.securityId, isUS)
              ? last4Digits
              : prev.securityId,
          validateSecurity:
            lockSecurityForExisting &&
            last4Digits &&
            !extractDigits(prev.validateSecurity, isUS)
              ? last4Digits
              : prev.validateSecurity,
        }));

        if (lockSecurityForExisting) {
          setShowSecurityId(false);
          setShowConfirmSecurityId(false);
          setSecurityEditEnabled(false);
        }

        // React-Select for citizenship (so the dropdown shows the value)
        if (citizenship) {
          const citizenshipOpts = citizenshipOptions.length
            ? citizenshipOptions
            : countryOptions;
          setCitizen(
            getCitizenshipOption(citizenshipOpts, citizenship) || {
              label: citizenship,
              value: citizenship,
            },
          );
        }
        if (country) {
          const norm = (v) =>
            String(v || "")
              .trim()
              .toLowerCase();
          const normCity = (v) =>
            norm(v)
              .replace(/\(.*?\)/g, "")
              .replace(/,/g, " ")
              .replace(/^city and county of\s+/i, "")
              .replace(/^county of\s+/i, "")
              .replace(/^city of\s+/i, "")
              .replace(/\./g, "")
              .replace(/\s+/g, " ")
              .trim()
              .split(" ")
              .map((word) => CITY_ABBREVIATIONS[word] || word)
              .join(" ");

          // countryOptions can be stale/empty here if this closure was created before
          // the country-list effect populated it; recompute fresh as a fallback.
          const freshCountryOptions = countryOptions.length
            ? countryOptions
            : Country.getAllCountries().map((c) => ({
                label: c.name,
                value: c.isoCode,
              }));
          const cOpt = getCountryOption(freshCountryOptions, country);
          if (cOpt) {
            suppressCountryCascadeRef.current = true;
            setSelectedCountry(cOpt);

            const allStates = State.getStatesOfCountry(cOpt.value);
            if (state) {
              const targetState = norm(state);
              const sMatch = allStates.find(
                (s) =>
                  norm(s.name) === targetState ||
                  norm(s.isoCode) === targetState,
              );
              if (sMatch) {
                const sOpt = {
                  label: sMatch.name,
                  value: sMatch.isoCode,
                };
                suppressStateCascadeRef.current = true;
                setSelectedState(sOpt);

                if (city) {
                  const allCities = City.getCitiesOfState(
                    cOpt.value,
                    sMatch.isoCode,
                  );
                  const targetCity = normCity(city);
                  const cityMatch =
                    allCities.find((x) => normCity(x.name) === targetCity) ||
                    allCities.find((x) =>
                      normCity(x.name).includes(targetCity),
                    ) ||
                    allCities.find((x) =>
                      targetCity.includes(normCity(x.name)),
                    );
                  if (cityMatch) {
                    setSelectedCity({
                      label: cityMatch.name,
                      value: cityMatch.name,
                    });
                  }
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn("Hinge DTV prefill failed", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, editData, id, npwId, draftNpwId]);
  /* ------------------------------------------------------------------ */
  /*  Sponsor + Webphone Hinge validation (new /validation API)         */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!ENABLE_SPONSOR_VALIDATION || !TB_HINGE_SPONSOR_VALIDATE_URL) return;
    const sponsorVal = String(form1.sponsor || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasFrontendSponsorError =
      !sponsorVal ||
      emailRegex.test(sponsorVal) ||
      sponsorVal.length > DTVUID_MAX_LEN;

    if (hasFrontendSponsorError) {
      setSponsorValidation({ status: "idle", message: "" });
      // Clear captured sponsor email when sponsor id is cleared
      setForm1((prev) => ({ ...prev, sponsor_email: "", sponsorEmail: "" }));
      return;
    }

    let cancelled = false;
    setSponsorValidation({ status: "checking", message: "" });

    const handle = setTimeout(async () => {
      try {
        const base = TB_HINGE_SPONSOR_VALIDATE_URL;
        const sep = base.includes("?") ? "&" : "?";
        const url = `${base}${sep}sponsorId=${encodeURIComponent(sponsorVal)}`;
        const resp = await tbGetJson(url);

        const ok =
          getManagerIsValidFromResp(resp) ||
          (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));

        // Capture sponsor email (if provided by validation API)
        const sponsorEmail = (
          resp?.emailId ||
          resp?.email ||
          resp?.email_id ||
          ""
        ).trim();

        if (!cancelled) {
          setSponsorValidation({
            status: ok ? "ok" : "error",
            message: ok ? "" : "Sponsor not found in Hinge",
          });

          // Store email in form (sent to draft/submit/edit)
          setForm1((prev) => ({
            ...prev,
            sponsor_email: ok && sponsorEmail ? sponsorEmail : "",
            sponsorEmail: ok && sponsorEmail ? sponsorEmail : "",
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setSponsorValidation({
            status: "error",
            message: "Sponsor validation failed",
          });
        }
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form1.sponsor]);

  useEffect(() => {
    if (!ENABLE_WEBPHONE_VALIDATION || !TB_HINGE_WEBPHONE_VALIDATE_URL) return;
    const managerVal = String(form1.webphoneManagerId || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasFrontendManagerError =
      !managerVal ||
      emailRegex.test(managerVal) ||
      managerVal.length > DTVUID_MAX_LEN;

    if (hasFrontendManagerError) {
      setWebphoneValidation({ status: "idle", message: "" });
      // Clear captured manager email when manager id is cleared
      setForm1((prev) => ({
        ...prev,
        webphone_manager_email: "",
        webphoneManagerEmail: "",
        webphone_manager_attuid: "",
      }));
      return;
    }

    const cached = readWebphoneManagerCache(managerVal);
    if (cached?.response) {
      const resp = cached.response;
      const ok =
        getManagerIsValidFromResp(resp) ||
        (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
      const mgrEmail = (
        resp?.emailId ||
        resp?.email ||
        resp?.email_id ||
        ""
      ).trim();
      const mgrAttuid = getManagerAttuidFromResp(resp);
      const attuidMissing =
        attuidRequiredOn && !isManagerAttuidActiveForAttuidFlow(resp);
      setWebphoneValidation({
        status: ok ? "ok" : "error",
        message: ok ? "" : "Webphone Manager not found in Hinge",
      });
      setFieldErrors((prev) => ({
        ...prev,
        webphoneManagerId: attuidMissing
          ? WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG
          : [
                WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG,
                "webphone manager doesn't have attuid",
              ].includes(prev?.webphoneManagerId)
            ? ""
            : prev?.webphoneManagerId || "",
      }));
      setForm1((prev) => ({
        ...prev,
        webphone_manager_email: ok && mgrEmail ? mgrEmail : "",
        webphoneManagerEmail: ok && mgrEmail ? mgrEmail : "",
        webphone_manager_attuid: mgrAttuid,
      }));
      return;
    }

    let cancelled = false;
    setWebphoneValidation({ status: "checking", message: "" });

    const handle = setTimeout(async () => {
      try {
        const base = TB_HINGE_WEBPHONE_VALIDATE_URL;
        const sep = base.includes("?") ? "&" : "?";
        const url = `${base}${sep}mgrDtvId=${encodeURIComponent(managerVal)}`;
        const resp = await tbGetJson(url);
        writeWebphoneManagerCache(managerVal, resp);

        const ok =
          getManagerIsValidFromResp(resp) ||
          (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));

        // Capture webphone manager email (if provided by validation API)
        const mgrEmail = (
          resp?.emailId ||
          resp?.email ||
          resp?.email_id ||
          ""
        ).trim();
        const mgrAttuid = getManagerAttuidFromResp(resp);

        if (!cancelled) {
          const attuidMissing =
            attuidRequiredOn && !isManagerAttuidActiveForAttuidFlow(resp);
          setWebphoneValidation({
            status: ok ? "ok" : "error",
            message: ok ? "" : "Webphone Manager not found in Hinge",
          });
          setFieldErrors((prev) => ({
            ...(prev || {}),
            webphoneManagerId: attuidMissing
              ? WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG
              : [
                    WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG,
                    "webphone manager doesn't have attuid",
                  ].includes(prev?.webphoneManagerId)
                ? ""
                : prev?.webphoneManagerId || "",
          }));

          // Store email in form (sent to draft/submit/edit)
          setForm1((prev) => ({
            ...prev,
            webphone_manager_email: ok && mgrEmail ? mgrEmail : "",
            webphoneManagerEmail: ok && mgrEmail ? mgrEmail : "",
            webphone_manager_attuid: mgrAttuid,
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setWebphoneValidation({
            status: "error",
            message: "Webphone validation failed",
          });
        }
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form1.webphoneManagerId, attuidRequiredOn]);

  // ===== S2P CONTRACT VALIDATION (optional / GET / debounced) =====
  useEffect(() => {
    if (!ENV.S2P_CONTRACT_VALIDATION_URL) return;
    const vendor = (form1.vendor || "").trim();
    const contract = (form1.contractPo || "").trim();
    if (!contract) {
      setContractValidation({ status: "idle", message: "" });
      return;
    }
    let cancelled = false;
    setContractValidation({ status: "checking", message: "" });
    const handle = setTimeout(async () => {
      try {
        const payload = {
          supplier: vendor || undefined,
          contractNumber: contract,
        };
        const qs = new URLSearchParams(
          Object.fromEntries(
            Object.entries(payload).filter(([_, v]) => v !== undefined),
          ),
        ).toString();
        const resp = await getJson(`${ENV.S2P_CONTRACT_VALIDATION_URL}?${qs}`);
        const ok =
          getManagerIsValidFromResp(resp) ||
          (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
        if (!cancelled)
          setContractValidation({
            status: ok ? "ok" : "error",
            message: ok ? "" : "Contract not valid",
          });
      } catch (e) {
        if (!cancelled)
          setContractValidation({
            status: "error",
            message: "Contract validation failed",
          });
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form1.contractPo, form1.vendor]);

  // ===== S2P PURCHASE ORDER VALIDATION (optional / GET / debounced) =====
  useEffect(() => {
    if (!ENV.S2P_PURCHASEORDER_VALIDATION_URL) return;
    const vendor = (form1.vendor || "").trim();
    const po = (form1.poNum || "").trim();
    if (!po) {
      setPoValidation({ status: "idle", message: "" });
      return;
    }
    let cancelled = false;
    setPoValidation({ status: "checking", message: "" });
    const handle = setTimeout(async () => {
      try {
        const payload = { supplier: vendor || undefined, orderNumber: po };
        const qs = new URLSearchParams(
          Object.fromEntries(
            Object.entries(payload).filter(([_, v]) => v !== undefined),
          ),
        ).toString();
        const resp = await getJson(
          `${ENV.S2P_PURCHASEORDER_VALIDATION_URL}?${qs}`,
        );
        const ok =
          getManagerIsValidFromResp(resp) ||
          (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
        if (!cancelled)
          setPoValidation({
            status: ok ? "ok" : "error",
            message: ok ? "" : "Purchase Order not valid",
          });
      } catch (e) {
        if (!cancelled)
          setPoValidation({
            status: "error",
            message: "Purchase Order validation failed",
          });
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form1.poNum, form1.vendor]);

  // ===== S2P PO LINE VALIDATION (optional / GET / debounced) =====
  useEffect(() => {
    if (!ENV.S2P_POLINES_VALIDATION_URL) return;
    const vendor = (form1.vendor || "").trim();
    const po = (form1.poNum || "").trim();
    const line = (form1.lineNum || "").trim();
    if (!line) {
      setLineValidation({ status: "idle", message: "" });
      return;
    }
    let cancelled = false;
    setLineValidation({ status: "checking", message: "" });
    const handle = setTimeout(async () => {
      try {
        const payload = {
          orderNumber: po || undefined,
          vendor: vendor || undefined,
          lineNumber: line,
        };
        const qs = new URLSearchParams(
          Object.fromEntries(
            Object.entries(payload).filter(([_, v]) => v !== undefined),
          ),
        ).toString();
        const resp = await getJson(`${ENV.S2P_POLINES_VALIDATION_URL}?${qs}`);
        const ok =
          getManagerIsValidFromResp(resp) ||
          (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
        if (!cancelled)
          setLineValidation({
            status: ok ? "ok" : "error",
            message: ok ? "" : "PO Line not valid",
          });
      } catch (e) {
        if (!cancelled)
          setLineValidation({
            status: "error",
            message: "PO Line validation failed",
          });
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [form1.lineNum, form1.vendor, form1.poNum]);

  /* ------------------------------------------------------------------ */
  /*  VENDOR LIVE SEARCH (S2P)                                         */
  /* ------------------------------------------------------------------ */

  const fetchVendors = async (inputValue) => {
    const query = (inputValue || "").trim();
    setVendorInput(query);

    if (!query) {
      setVendorOptions([]);
      setDataLoads((prev) => ({
        ...prev,
        vendorSupplier: {
          ...prev.vendorSupplier,
          field: "Vendor/Supplier",
          status: "idle",
        },
      }));
      return;
    }

    if (query.length < 3) {
      setVendorOptions([]);
      setDataLoads((prev) => ({
        ...prev,
        vendorSupplier: {
          ...prev.vendorSupplier,
          field: "Vendor/Supplier",
          status: "idle",
        },
      }));
      toast.warn("Please enter at least 3 characters to search vendors", {
        toastId: "vendor-min-3",
      });
      return;
    }

    if (!SUPPLIER_VALIDATION_URL) {
      setDataLoads((prev) => ({
        ...prev,
        vendorSupplier: {
          ...prev.vendorSupplier,
          field: "Vendor/Supplier",
          status: "error",
        },
      }));
      toast.error("Supplier validation endpoint not configured.");
      return;
    }

    try {
      setVendorLoading(true);
      setDataLoads((prev) => ({
        ...prev,
        vendorSupplier: {
          field: "Vendor/Supplier",
          status: "loading",
          startedAt:
            prev?.vendorSupplier?.status === "loading" &&
            prev?.vendorSupplier?.startedAt
              ? prev.vendorSupplier.startedAt
              : Date.now(),
        },
      }));
      const base = SUPPLIER_VALIDATION_URL;
      const sep = base.includes("?") ? "&" : "?";
      const url = `${base}${sep}SupplierName=${encodeURIComponent(query)}`;
      const resp = await getJson(url);
      const items = resp?.items || [];
      if (resp?.ok && items.length > 0) {
        const opts = items.map((it) => ({
          label: `${it.supplierName} (${it.supplierNumber})`,
          value: it.supplierNumber,
          meta: {
            supplierName: it.supplierName,
            supplierNumber: it.supplierNumber,
          },
        }));
        setVendorOptions(opts);
        setDataLoads((prev) => ({
          ...prev,
          vendorSupplier: {
            ...prev.vendorSupplier,
            field: "Vendor/Supplier",
            status: "success",
          },
        }));
      } else {
        setVendorOptions([]);
        toast.info("No vendors found for the entered text.", {
          toastId: "vendor-none",
        });
        setDataLoads((prev) => ({
          ...prev,
          vendorSupplier: {
            ...prev.vendorSupplier,
            field: "Vendor/Supplier",
            status: "success",
          },
        }));
      }
    } catch (err) {
      console.error("Vendor search failed", err);
      toast.error("Failed to search vendors.");
      setVendorOptions([]);
      setDataLoads((prev) => ({
        ...prev,
        vendorSupplier: {
          ...prev.vendorSupplier,
          field: "Vendor/Supplier",
          status: "error",
        },
      }));
    } finally {
      setVendorLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  VALIDATION HELPERS                                                */
  /* ------------------------------------------------------------------ */

  const runFieldValidation = (name, value) => {
    if (
      isSecurityReadOnly &&
      (name === "securityId" || name === "validateSecurity")
    ) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
      return "";
    }
    let msg = "";
    switch (name) {
      case "firstName": {
        const v = String(value || "").trim();
        if (!v) {
          msg = "";
          break;
        }
        if (!FIRST_LAST_NAME_REGEX.test(v)) {
          msg =
            "First Name must be 2-50 characters, start with a letter, and only contain letters, spaces, apostrophe ('), hyphen (-), or period (.)";
        }
        break;
      }
      case "lastName": {
        const v = String(value || "").trim();
        if (!v) {
          msg = "";
          break;
        }
        if (!FIRST_LAST_NAME_REGEX.test(v)) {
          msg =
            "Last Name must be 2-50 characters, start with a letter, and only contain letters, spaces, apostrophe ('), hyphen (-), or period (.)";
        }
        break;
      }
      case "email":
        if (value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            msg = "Enter a valid email (e.g. name@domain.com)";
          }
        }
        break;
      case "phone": {
        const v = String(value || "").trim();
        if (!v) {
          msg = "";
          break;
        }

        const isUsPhone = isUnitedStatesSelected(form1.country);
        if (isUsPhone) {
          if (v.length >= 1 && !/^[2-9]/.test(v)) {
            msg = "Invalid phone. 1st digit must be 2-9.";
            break;
          }
          if (v.length >= 4 && !/^[2-9][0-9]{2}[2-9]/.test(v)) {
            msg = "Invalid phone. 4th digit must be 2-9.";
            break;
          }
          if (v.length !== 10) {
            msg = "Phone must be 10 digits";
            break;
          }
          if (!/^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(v))
            msg = "Invalid phone. 1st & 4th digits must be 2-9.";
          break;
        }

        if (!/^\d+$/.test(v)) {
          msg =
            "Phone must contain digits only (no spaces or special characters).";
          break;
        }
        if (v.length < 10) {
          msg = "Phone must be at least 10 digits.";
          break;
        }
        if (v.length > 15) {
          msg = "Phone must be 15 digits or less";
        }
        break;
      }
      case "sponsor":
      case "webphoneManagerId": {
        const idVal = String(value || "").trim();
        if (!idVal) {
          msg = "";
          break;
        }
        if (idVal.length > DTVUID_MAX_LEN) {
          msg = "Must be 10 characters or less.";
          break;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(idVal)) {
          msg = `Email not allowed.`;
          break;
        }
        // Webphone Manager cannot be the edited record's own DTVID (all roles except FTE Nominator).
        if (name === "webphoneManagerId" && isEditRoute) {
          // const isFteNominator = String(sessionStorage.getItem("tb_role") || "")
          //     .trim()
          //     .toLowerCase() === "fte nominator";
          const editedRecordDtvid = String(
            editData?.dtvId || editData?.dtv_id || form1.dtvId || "",
          ).trim();
          if (
            // !isFteNominator &&
            editedRecordDtvid &&
            idVal.toLowerCase() === editedRecordDtvid.toLowerCase()
          ) {
            msg =
              "Webphone Manager DTVUID cannot be the same as the record's DTVID.";
          }
        }
        break;
      }
      case "contractPo":
        if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
          msg = "Contract # must be 50 characters or less.";
          break;
        }
        //if (value && !/^[A-Za-z0-9]+$/.test(value)) {
        if (value && !/^[A-Za-z0-9\-_.\\/#&@+]+$/.test(value)) {
          //msg = "Contract # can contain only letters and numbers";
          msg =
            "Contract # can contain only letters, numbers, and common special characters (- _ . / # & @ +)";
        }
        break;
      case "poNum":
        if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
          msg = "PO # must be 50 characters or less.";
          break;
        }
        //if (value && !/^[A-Za-z0-9]+$/.test(value)) {
        if (value && !/^[A-Za-z0-9\-_.\\/#&@+]+$/.test(value)) {
          //msg = "PO # can contain only letters and numbers";
          msg =
            "PO # can contain only letters, numbers, and common special characters (- _ . / # & @ +)";
        }
        break;
      case "lineNum":
        if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
          msg = "Line # must be 50 characters or less.";
          break;
        }
        if (value && !/^[A-Za-z0-9\-_.\\/#&@+]+$/.test(value)) {
          msg =
            "Line # can contain only letters, numbers, and common special characters (- _ . / # & @ +)";
        }
        break;
      case "workerTypeTax":
        // Worker Type is only required when ATTUID Required is ON.
        if (attuidRequiredOn) {
          if (!value) {
            msg = requiredError("workerTypeTax");
          } else if (!dd.workerTypeTax.includes(value)) {
            msg = invalidDropdownValueError("workerTypeTax");
          }
        }
        break;
      case "nonBillable":
        if (!value) {
          msg = requiredError("nonBillable");
        }
        break;
      //   case "attRoleKey":
      //     if (attuidRequiredOn && !value) {
      //       msg = requiredError("attRoleKey");
      //     }
      //     break;

      //   case "attRoleKey": {
      //     if (attuidRequiredOn) {
      //       const selectedKey = String(value || "").trim();

      //       const isValidDropdownValue = attRoleOptions.some(
      //         (option) => String(option?.key || "").trim() === selectedKey,
      //       );

      //       if (!selectedKey) {
      //         msg = "Please select a valid ATT Role from the dropdown.";
      //       } else if (!isValidDropdownValue) {
      //         msg = "Please select a valid ATT Role from the dropdown.";
      //       }
      //     }
      //     break;
      //   }

      case "role": {
        if (attuidRequiredOn) {
          const selectedRole = String(value || "").trim();
          if (selectedRole) {
            const isValidCatalogRole = (dd.roles || []).some(
              (r) => String(r || "").trim().toLowerCase() === selectedRole.toLowerCase(),
            );
            if (!isValidCatalogRole) {
              msg = "Selected Role is not available in the Role Catalog. Please select a valid Role.";
            }
          }
        }
        break;
      }

      case "attRoleKey": {
        if (attuidRequiredOn) {
          const selectedKey = String(value || "").trim();

          const isValidDropdownValue = attRoleOptions.some(
            (option) => String(option?.key || "").trim() === selectedKey,
          );

          console.log("[ATT ROLE VALIDATION] selectedKey:", selectedKey);
          console.log(
            "[ATT ROLE VALIDATION] isValidDropdownValue:",
            isValidDropdownValue,
          );
          console.log(
            "[ATT ROLE VALIDATION] Available keys:",
            attRoleOptions.map((option) => option.key),
          );

          if (!selectedKey) {
            msg = "Please select a valid ATT Role from the dropdown.";
          } else if (!isValidDropdownValue) {
            msg = "Please select a valid ATT Role from the dropdown.";
          }
        }
        break;
      }

      case "vendor": {
        const v = String(value || "").trim();
        if (!v) msg = requiredError("vendor");
        break;
      }

      case "securityId": {
        const _isUS = attuidRequiredOn && isUnitedStatesSelected(form1.country);
        const digits = clampDigitsToFormat(_isUS, extractDigits(value, _isUS));
        if (!digits)
          msg = _isUS ? "SSN is required" : "Security ID is required";
        else if (digits.length !== maxDigitsFor(_isUS))
          msg = _isUS ? "SSN must be 9 digits" : "Security ID must be 8 digits";
        else if (!_isUS) {
          const mmddMsg = validateSecurityIdMmdd8(digits);
          if (mmddMsg) msg = mmddMsg;
        }
        break;
      }
      case "validateSecurity": {
        const _isUS = attuidRequiredOn && isUnitedStatesSelected(form1.country);
        const d1 = clampDigitsToFormat(
          _isUS,
          extractDigits(form1.securityId, _isUS),
        );
        const d2 = clampDigitsToFormat(_isUS, extractDigits(value, _isUS));
        if (!d2)
          msg = _isUS
            ? "Confirm SSN is required"
            : "Confirm Security ID is required";
        else if (d2.length !== maxDigitsFor(_isUS))
          msg = _isUS
            ? "Confirm SSN must be 9 digits"
            : "Confirm Security ID must be 8 digits";
        else {
          if (!_isUS) {
            const mmddMsg = validateSecurityIdMmdd8(d2);
            if (mmddMsg) {
              msg = mmddMsg;
              break;
            }
          }
          if (d1 && d2 !== d1)
            msg = _isUS
              ? "Confirm SSN must match SSN"
              : "Confirm Security ID must match Security ID";
        }
        break;
      }

      default:
        break;
    }
    setFieldErrors((prev) => ({
      ...prev,
      [name]: msg,
    }));
    return msg;
  };

  const validateAll = () => {
    if (
      /^\d+$/.test(String(effectiveNpwId || "").trim()) &&
      !lookupSecExistFlag &&
      String(
        editData?.dtvId || editData?.dtv_id || form1.dtvId || routeDtvid || "",
      ).trim()
    ) {
      toast.error("Loading Security ID details. Please wait a moment.", {
        toastId: "onb-security-lookup-pending",
      });
      return false;
    }

    const required = [
      "firstName",
      "lastName",
      "email",
      "sponsor",
      "webphoneManagerId",
      "country",
      "vendor",
      "occupancyCode",
      "startDate",
      "endDate",
      "nonBillable",
    ];
    if (isStateRequired) required.push("state");
    if (isCityRequired) required.push("city");
    // In edit/prefill flows, Security ID can be locked and should not be required/validated
    if (isSecurityFieldRequired) {
      required.push("securityId", "validateSecurity");
    }
    if (attuidRequiredOn) {
      required.push("role");
      //   if (!isEditRoute) {
      //     required.push("attRoleKey");
      //   }
      required.push("attRoleKey");
      // When ATTUID Required is ON, show and require both Country of Citizenship + W2/1099.
      required.push("citizenship");
      required.push("workerTypeTax");
    }
    const newErrors = {};
    const getValue = (field) => {
      if (field === "startDate" || field === "endDate") return form1[field];
      if (field === "state") return selectedState?.label || "";
      if (field === "citizenship") {
        return ctzn?.label || ctzn?.value || form1.citizenship || "";
      }
      return form1[field] || "";
    };
    // required.forEach((f) => {
    //   const value = getValue(f);
    //   if (!value) {
    //     newErrors[f] = requiredError(f);
    //   } else {
    //     newErrors[f] = runFieldValidation(f, value);
    //   }
    // });

    required.forEach((f) => {
      const value = getValue(f);

      if (f === "attRoleKey") {
        newErrors[f] = runFieldValidation(f, value);
      } else if (!value) {
        newErrors[f] = requiredError(f);
      } else {
        newErrors[f] = runFieldValidation(f, value);
      }
    });

    // Vendor is selected from the supplier lookup, so validate both fields
    // explicitly instead of relying only on the generic required-field loop.
    if (!String(form1.vendor || "").trim()) {
      newErrors.vendor = "Please enter Vendor Name";
    } else if (!String(form1.supplierReference || "").trim()) {
      newErrors.vendor = "Please enter Vendor Name";
    }

    const phoneVal = String(form1.phone || "").trim();
    if (phoneVal) {
      newErrors.phone = runFieldValidation("phone", phoneVal);
    }

    ["contractPo", "poNum"].forEach((f) => {
      const v = form1[f];
      if (v) {
        newErrors[f] = runFieldValidation(f, v);
      }
    });
    // Line validation is advisory: keep its message visible, but do not block submission.
    const lineValidationMessage = form1.lineNum
      ? runFieldValidation("lineNum", form1.lineNum)
      : "";
    const hasError = Object.values(newErrors).some((m) => m);
    setFieldErrors({ ...newErrors, lineNum: lineValidationMessage });
    if (hasError) {
      toast.error("Please fix the highlighted errors before continuing.", {
        toastId: "onb-fix-errors",
      });
    }
    return !hasError;
  };

  const ensureHingeValidationOk = () => {
    if (ENABLE_SPONSOR_VALIDATION && form1.sponsor) {
      if (sponsorValidation.status === "idle") {
        toast.error(
          "Sponsor validation not completed yet. Please wait a few seconds.",
        );
        return false;
      }
      if (sponsorValidation.status === "error") {
        toast.error("Sponsor is invalid per Hinge. Please correct it.");
        return false;
      }
      if (sponsorValidation.status === "checking") {
        toast.error("Sponsor validation is in progress. Please wait a moment.");
        return false;
      }
    }
    if (ENABLE_WEBPHONE_VALIDATION && form1.webphoneManagerId) {
      if (webphoneValidation.status === "idle") {
        toast.error(
          "Webphone Manager validation not completed yet. Please wait a few seconds.",
        );
        return false;
      }
      if (webphoneValidation.status === "error") {
        toast.error(
          "Webphone Manager is invalid per Hinge. Please correct it.",
        );
        return false;
      }
      if (webphoneValidation.status === "checking") {
        toast.error(
          "Webphone Manager validation is in progress. Please wait a moment.",
        );
        return false;
      }
    }
    if (attuidRequiredOn) {
      const cached = readWebphoneManagerCache(form1.webphoneManagerId);
      const managerValidationResp = cached?.response || null;
      const hasActiveManagerAttuid =
        isManagerAttuidActiveForAttuidFlow(managerValidationResp) ||
        (!managerValidationResp &&
          Boolean(String(form1.webphone_manager_attuid || "").trim()) &&
          !["false", "null", "undefined"].includes(
            String(form1.webphone_manager_attuid || "")
              .trim()
              .toLowerCase(),
          ));
      if (!hasActiveManagerAttuid) {
        setFieldErrors((prev) => ({
          ...(prev || {}),
          webphoneManagerId: WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG,
        }));
        toast.error(WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG);
        return false;
      }
    }
    return true;
  };

  /* ------------------------------------------------------------------ */
  /*  SAVE / SUBMIT                                                     */
  /* ------------------------------------------------------------------ */

  // IMPORTANT:
  // Backend expects npw_id to be a bigint (numeric).
  // In DTVID-prefill routes (e.g., "/onboarding/new/DTVID=pt3665.uat"),
  // we must NOT send that token as npw_id.
  const isNumericNpwId = (v) => /^\d+$/.test(String(v ?? "").trim());

  const pickNumericNpwId = (...vals) => {
    for (const v of vals) {
      const s = String(v ?? "").trim();
      if (!s) continue;
      if (isNumericNpwId(s)) return s;
    }
    return null;
  };

  const buildCommonPayload = (statusLabel, buttonLabel) => {
    const finalStatus = statusLabel;

    const { dtvid } = getFreshSession();
    const finalButton =
      buttonLabel ||
      (editData && editData.status ? editData.status : statusLabel);

    const rawNpwCandidate =
      draftNpwId ||
      (isEditRoute ? id || npwId : null) ||
      editData?.npwId ||
      editData?.npw_id ||
      undefined;

    // Only use numeric npw_id values; if the candidate is non-numeric (e.g., "DTVID=..."),
    // send npw_id as null so backend does not attempt bigint casts.
    const numericNpwId = pickNumericNpwId(rawNpwCandidate);

    // Serialize dates to YYYY-MM-DD to avoid timezone shifts
    const secDigits = clampDigitsToFormat(
      isUS,
      extractDigits(form1.securityId, isUS),
    );

    // If lookup API returned ATTUID, include it in payload (create + edit).
    const attuidPayload = String(
      lookupAttuid ||
        form1?.attuid ||
        editData?.attuid ||
        editData?.attUid ||
        editData?.attUID ||
        editData?.att_uid ||
        "",
    ).trim();
    const cachedManager = readWebphoneManagerCache(form1.webphoneManagerId);
    const webphoneManagerAttuid = String(
      form1?.webphone_manager_attuid ||
        editData?.webphone_manager_attuid ||
        editData?.webphoneManagerAttuid ||
        getManagerAttuidFromResp(cachedManager?.response),
    ).trim();

    const safeForm1 = {
      ...form1,
      validateSecurity: undefined, // UI only
      startDate: serializeDate(form1.startDate),
      endDate: serializeDate(form1.endDate),
    };
    if (webphoneManagerAttuid)
      safeForm1.webphone_manager_attuid = webphoneManagerAttuid;
    // Only include securityId when we intend to submit it.
    if (secDigits) safeForm1.securityId = secDigits;

    // Only send Security ID/SSN when user is actually editing it, and only if it changed.
    const securityUnchanged = secDigits === securityInitialDigits;
    const shouldOmitSecurityId =
      (isSecurityEditRecord || isSecurityOptionalEditMode) &&
      (!securityEditEnabled || securityUnchanged);
    if (shouldOmitSecurityId) {
      try {
        delete safeForm1.securityId;
      } catch {}
    }

    /*TODO:pentest*/
    // body v = hashed-roleSess
    const dtvRaw = sessionStorage.getItem("tb_dtvid") || "";
    const ts = Date.now();
    const vRaw = `${nominatorRole}_*v*_${ts}`;
    const dRaw = `${dtvRaw}_*v*_${ts}`;
    const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
    const dParam = typeof btoa === "function" ? btoa(dRaw) : window.btoa(dRaw);

    return {
      workerType,
      form1: safeForm1,
      status: finalStatus,
      buttonAction: finalButton,
      ...(isReactiveAttuidFlow && finalStatus === "Submitted"
        ? { reactive_attuid: true }
        : {}),

      ...(attuidPayload ? { attuid: attuidPayload } : {}),
      ...(webphoneManagerAttuid
        ? { webphone_manager_attuid: webphoneManagerAttuid }
        : {}),

      submitter: email || undefined,
      approver: editData ? editData.approver_id || approver : approver,
      submitter_name: name || undefined,
      submitterDtvid: dtvid || undefined,
      ...(rawNpwCandidate !== undefined && rawNpwCandidate !== null
        ? { npw_id: numericNpwId || null, npwId: numericNpwId || null }
        : {}),
      nominatorRole, // ✅ original role field
      loggedInUserRole: nominatorRole, // ✅ extra field with logged-in user role
      /*TODO:pentest*/
      v: vParam,
      d: dParam,
    };
  };

  const saveDraft = async () => {
    if (supplierGate.blocked) {
      toast.error(
        "Cannot save Draft: Supplier Reference is not loaded. Please fix Vendor/Supplier selection.",
      );
      return;
    }

    // Draft rule: require Security ID entry when Security is editable (NEW flow).
    // If Security is locked (editing existing NPW / loaded by npw_id or DTVID-prefill), we do NOT block Draft.
    if (isSecurityFieldRequired) {
      const _isUS =
        attuidRequiredOn &&
        isUnitedStatesSelected(selectedCountry?.label || form1.country || "");
      const _secDigits = clampDigitsToFormat(
        _isUS,
        extractDigits(form1.securityId, _isUS),
      );
      const _need = maxDigitsFor(_isUS);

      if (!_secDigits || _secDigits.length !== _need) {
        setFieldErrors((prev) => ({
          ...(prev || {}),
          securityId: `${_isUS ? "SSN" : "Security ID"} must be ${_need} digits`,
        }));
        toast.error(
          `Please enter a valid ${_need}-digit ${_isUS ? "SSN" : "Security ID"} before saving Draft.`,
        );
        try {
          securityRef?.current?.focus();
        } catch {}
        return;
      }

      // Extra validation for non-US Security ID: MMDDXXXX where MM/DD are valid.
      if (!_isUS) {
        const mmddMsg = validateSecurityIdMmdd8(_secDigits);
        if (mmddMsg) {
          setFieldErrors((prev) => ({
            ...(prev || {}),
            securityId: mmddMsg,
            validateSecurity: "",
          }));
          toast.error("Please enter a valid Security ID (MMDDXXXX).", {
            toastId: "onb-securityid-mmdd",
          });
          try {
            securityRef?.current?.focus();
          } catch {}
          return;
        }
      }
    }
    // Draft should not block on field or Hinge validations
    const payload = buildCommonPayload("Draft", "Draft");
    try {
      setLoading(true);
      const hasExistingNpw =
        isNumericNpwId(draftNpwId) ||
        isNumericNpwId(id) ||
        isNumericNpwId(npwId) ||
        isNumericNpwId(editData?.npwId) ||
        isNumericNpwId(editData?.npw_id);
      const resp = await apiSaveDraft(payload, hasExistingNpw);

      // If backend returned npw_id for a new draft, persist it so subsequent submits use EDIT
      const returnedNpw =
        resp?.npw_id ||
        resp?.npwId ||
        resp?.data?.npw_id ||
        resp?.data?.npwId ||
        resp?.data?.id ||
        resp?.data?.npwID;
      if (returnedNpw) {
        const v = String(returnedNpw);
        if (isEditRoute) {
          try {
            SS.setItem("tb_draft_npw_id", v);
          } catch {}
          setDraftNpwId(v);
        }
        // Only treat as edit when URL has :id or an external npwId prop exists.
        // If we're editing, keep editData in sync so the editData hydration effect
        // doesn't overwrite the form with stale values.
        if (isEditRoute) {
          setEditData((prev) => ({
            ...(prev || {}),
            ...form1,
            worker_type: workerType,
            validateSecurity: form1.securityId,
            npwId: v,
            npw_id: v,
            status: "Draft",
          }));
        }
      } else if (isEditRoute) {
        setEditData((prev) => ({
          ...(prev || {}),
          ...form1,
          worker_type: workerType,
          validateSecurity: form1.securityId,
          status: "Draft",
        }));
      }

      if (isEditRoute)
        navigate(location.pathname, {
          state: { toastMessage: "Item updated successfully!" },
          replace: true,
        });

      toast.success(
        isEditRoute ? "Updated successfully" : "Draft submitted successfully!",
      );

      // On edit routes, keep the current values visible.
      if (!isEditRoute) {
        // Important: clear cached draft IDs so the next Draft/Submit creates a NEW record
        // without requiring a page reload.
        setDraftNpwId(null);
        try {
          SS.removeItem("tb_draft_npw_id");
        } catch {}

        setForm1(initialVal);
        setFieldErrors({});
        setStep(1);
        setSelectedState(null);
        setSelectedCity(null);
        setSelectedCountry(null);
        setCitizen(null);
        SetSelectedTimeZone(null);
        setVendorOptions([]);
        setVendorInput("");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to save draft.");
    } finally {
      setLoading(false);
    }
  };

  const submitAll = async () => {
    if (supplierGate.blocked) {
      toast.error(
        "Cannot submit: Supplier Reference is not loaded. Please fix Vendor/Supplier selection.",
      );
      return;
    }

    if (!validateAll()) {
      toast.error("Please fix validation errors before submitting.");
      return;
    }
    if (!ensureHingeValidationOk()) return;
    if (requiresCpValidateBeforeSubmit && cpValidation.status !== "passed") {
      toast.error(
        "Please click Validate and resolve all CP errors before submitting.",
      );
      return;
    }

    const payload = buildCommonPayload("Submitted", null);
    try {
      setLoading(true);
      setSubmitting(true);
      // Treat as EDIT only when we have a numeric npw_id.
      // (Routes like DTVID=pt3665.uat must remain NEW and should not force PUT.)
      const hasExistingNpw =
        isNumericNpwId(draftNpwId) ||
        isNumericNpwId(id) ||
        isNumericNpwId(npwId) ||
        isNumericNpwId(editData?.npwId) ||
        isNumericNpwId(editData?.npw_id);
      const resp = await apiSubmit(payload, hasExistingNpw);
      // Clear any stored draft npw_id after final submit success
      try {
        SS.removeItem("tb_draft_npw_id");
      } catch {}
      setDraftNpwId(null);

      const msg = hasExistingNpw
        ? "Request updated successfully"
        : "Request submitted successfully!";
      // Show toast before redirect, and persist it for the dashboard.
      // (Immediate navigation can otherwise hide the toast.)
      toastThenDashboard(msg, "success");

      // After a successful submit on the NEW route, clear edit state too.
      if (!isEditRoute) {
        setEditData(null);
      }
      // Navigation handled by toastThenDashboard.
      return;
    } catch (e) {
      console.error(e);
      const messages = getCpErrorMessages(e, { max: 12 });
      if (messages.length) {
        messages.forEach((msg) => toast.error(msg));
      } else {
        toast.error("Failed to submit.");
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const validateBeforeSubmit = async () => {
    if (!requiresCpValidateBeforeSubmit) {
      setCpValidation({ status: "passed", message: "Validation not required" });
      toast.success("Validation not required for this onboarding flow.");
      return;
    }
    if (supplierGate.blocked) {
      toast.error(
        "Cannot validate: Supplier Reference is not loaded. Please fix Vendor/Supplier selection.",
      );
      return;
    }
    if (!validateAll()) {
      return;
    }
    if (!ensureHingeValidationOk()) return;

    const validateNpwIdRaw =
      draftNpwId || id || npwId || editData?.npwId || editData?.npw_id || "";
    const validateNpwId = /^\d+$/.test(String(validateNpwIdRaw).trim())
      ? String(validateNpwIdRaw).trim()
      : "";
    const pick = (...vals) => {
      for (const val of vals) {
        const s = String(val ?? "").trim();
        if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined")
          return s;
      }
      return "";
    };
    const attRoleKeyForValidate = pick(
      form1?.attRoleKey,
      form1?.att_role_key,
      editData?.attRoleKey,
      editData?.att_role_key,
    );
    const profileFromAttRoleKey = (() => {
      const idx = String(attRoleKeyForValidate || "").indexOf("||");
      return idx >= 0
        ? String(attRoleKeyForValidate)
            .slice(idx + 2)
            .trim()
        : "";
    })();
    const roleFromAttRoleKey = (() => {
      const idx = String(attRoleKeyForValidate || "").indexOf("||");
      return idx >= 0 ? String(attRoleKeyForValidate).slice(0, idx).trim() : "";
    })();
    const derivedProfileFromCatalog = (() => {
      const cpRoleId = pick(
        form1?.attRoleCpRoleId,
        form1?.att_role_cp_role_id,
        roleFromAttRoleKey,
        form1?.attRoleKey,
      );
      if (!cpRoleId) return "";
      for (const role of hingeRoleCatalog || []) {
        for (const cp of role?.cinglepointRoles || []) {
          if (String(cp?.cpRoleId || "").trim() !== cpRoleId) continue;
          for (const profile of cp?.cpProfiles || []) {
            const cpProfileId = String(profile?.cpProfileId || "").trim();
            if (cpProfileId) return cpProfileId;
          }
        }
      }
      return "";
    })();
    const cpProfileId = pick(
      form1?.attRoleCpProfileId,
      form1?.att_role_cp_profile_id,
      form1?.cinglepointProfileId,
      form1?.singlepointProfileId,
      editData?.attRoleCpProfileId,
      editData?.att_role_cp_profile_id,
      editData?.cinglepointProfileId,
      editData?.singlepointProfileId,
      profileFromAttRoleKey,
      derivedProfileFromCatalog,
    );
    if (!cpProfileId) {
      const msg = "CP Profile ID is required for validation.";
      setCpValidation({ status: "failed", message: msg });
      toast.error(msg);
      return;
    }
    const ssnSecId = extractDigits(
      pick(
        form1?.securityId,
        form1?.validateSecurity,
        form1?.confirmSecurityId,
        form1?.security_id_plaintext,
        form1?.securityIdPlaintext,
      ),
      isUS,
    );

    const requestDetail = {
      cp_approver_attuid: pick(
        form1?.sponsor,
        form1?.webphone_manager_attuid,
        sessionStorage.getItem("tb_attuid"),
      ),
      first_name: pick(form1?.firstName, editData?.first_name),
      last_name: pick(form1?.lastName, editData?.last_name),
      email: pick(form1?.email, editData?.email),
      phone: pick(form1?.phone, editData?.phone, editData?.phone_number),
      phone_number: pick(form1?.phone, editData?.phone, editData?.phone_number),
      country: pick(
        form1?.citizenship,
        editData?.citizenship,
        form1?.country,
        editData?.country,
      ),
      countryOfWorking: pick(form1?.country, editData?.country),
      attuid: pick(form1?.attuid, editData?.attuid),
      dtvid: pick(form1?.dtvId, editData?.dtv_id) || generateTbFallbackDtvid(),
      worker_type_tax: pick(form1?.workerTypeTax, editData?.w2_1099),
      webphone_manager_attuid: pick(
        form1?.webphone_manager_attuid,
        form1?.webphoneManagerId,
        editData?.webphone_manager_attuid,
      ),
      cp_role_id: pick(
        form1?.attRoleCpRoleId,
        form1?.att_role_cp_role_id,
        editData?.att_role_cp_role_id,
      ),
      role: pick(form1?.role, editData?.role),
      cp_profile_id: cpProfileId,
      sow: cpProfileId,
      security_id_hash:
        pick(form1?.security_id_hash, form1?.securityIdHash) || null,
      security_id_plaintext: null,
      securityId: null,
      security_id: null,
      ssn: null,
      ssn_plain: null,
      secid: null,
      sec_id: null,
      start_date:
        serializeDate(form1?.startDate) ||
        pick(editData?.start_date, new Date().toISOString().slice(0, 10)),
      end_date:
        serializeDate(form1?.endDate) || pick(editData?.end_date) || null,
      comments: "Validation from UI no-npw flow",
      fte_nominated: false,
      offboard_by_attuid: false,
      ...(ssnSecId ? { ssnSecId } : {}),
    };

    // if(!requestDetail.dtvid || requestDetail.dtvid === "null" || requestDetail.dtvid === "undefined" || requestDetail.dtvid === '') {
    //     requestDetail.dtvid = generateTbFallbackDtvid();
    // }

    const validateStatusKey = String(
      editData?.personStatus ??
        editData?.person_status ??
        editData?.status ??
        "",
    )
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    const shouldSendFullValidatePayload =
      !validateNpwId || validateStatusKey === "draft";

    const payload = {
      req_type: "action",
      action: "VALIDATE",
      client_request_id: `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      request_detail: requestDetail,
      ...(!shouldSendFullValidatePayload && validateNpwId
        ? { npw_id: Number(validateNpwId) }
        : {}),
    };
    try {
      setLoading(true);
      setCpValidation({
        status: "checking",
        message: "Validating with CP API...",
      });

      const resp = await apiValidateCp(payload);
      const failed = hasApiValidationFailure(resp);
      const messages = getCpErrorMessages(resp, { max: 12 });

      if (failed) {
        if (messages.length) {
          messages.forEach((msg) => toast.error(msg));
        } else {
          toast.error(
            "CP validation failed. Please review and correct the data.",
          );
        }
        setCpValidation({
          status: "failed",
          message: messages[0] || "Validation failed",
        });
        return;
      }

      setCpValidation({ status: "passed", message: "Validation successful" });
      toast.success("Validation successful. You can submit now.");
    } catch (e) {
      const messages = getCpErrorMessages(e, { max: 12 });
      const msg =
        messages[0] || String(e?.message || e || "Validation failed").trim();
      setCpValidation({ status: "failed", message: msg });
      if (messages.length) {
        messages.forEach((item) => toast.error(item));
      } else {
        toast.error(msg || "CP validation failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAttuidToggle = () => {
    const currentOn =
      form1.attuidRequired === "Yes" ||
      form1.attuidRequired === "true" ||
      form1.isAttuidRequested === "Yes";
    const nextOn = !currentOn;

    setForm1((prev) => {
      let next = {
        ...prev,
        attuidRequired: nextOn ? "Yes" : "No",
        isAttuidRequested: nextOn ? "Yes" : "No",
        ...(nextOn
          ? {}
          : {
              role: "",
              attRoleKey: "",
              attRoleDtvRoleId: "",
              attRoleDtvRoleName: "",
              attRoleCpRoleId: "",
              attRoleCpRoleDesc: "",
              attRoleCpProfileId: "",
              attRoleCpProfileDesc: "",
            }),
      };

      // If turning OFF, hide + clear dependent fields.
      if (!nextOn) {
        next = {
          ...next,
          citizenship: "",
          workerTypeTax: "",
        };
      }
      return next;
    });

    if (!nextOn) {
      // Clear react-select value when hiding the field.
      setCitizen(null);
    }

    // Clear related errors when toggling.
    setFieldErrors((prev) => ({
      ...prev,
      citizenship: "",
      workerTypeTax: "",
      role: "",
      attRoleKey: "",
    }));
  };

  const handleContinue = () => {
    console.log("[CONTINUE] handleContinue() CALLED");
    console.log("[CONTINUE] Before validateAll()");

    const validationResult = validateAll();

    console.log("[CONTINUE] validateAll() result:", validationResult);

    if (!validationResult) {
      console.log("[CONTINUE] STOPPED by validateAll()");
      return;
    }

    console.log("[CONTINUE] Before ensureHingeValidationOk()");

    const hingeValidationResult = ensureHingeValidationOk();

    console.log(
      "[CONTINUE] ensureHingeValidationOk() result:",
      hingeValidationResult,
    );

    if (!hingeValidationResult) {
      console.log("[CONTINUE] STOPPED by ensureHingeValidationOk()");
      return;
    }

    console.log("[CONTINUE] PASSED all validations");
    if (supplierGate.blocked) {
      toast.error(
        "Cannot continue: Supplier validation is incomplete. Please fix Vendor/Supplier selection.",
      );
      return;
    }
    const blocks = blockingLoadsForContinue(dataLoads, { timeoutMs: 20000 });
    if (blocks.length) {
      blocks.forEach((b) => {
        const toastOpts = { toastId: dataLoadToastId(b.status, b.field) };
        if (b.status === "error")
          toast.error(loadFailMessage(b.field), toastOpts);
        else if (b.status === "timeout")
          toast.warn(loadTimeoutMessage(b.field), toastOpts);
        else toast.info(loadOngoingMessage(b.field), toastOpts);
      });
      return;
    }
    if (!validateAll()) return;

    console.log("[CONTINUE] Supplier gate:", supplierGate);
    console.log("[CONTINUE] Vendor:", form1.vendor);
    console.log("[CONTINUE] Supplier Reference:", form1.supplierReference);

    if (!ensureHingeValidationOk()) return;
    setStep(step + 1);
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */
  const handleCancel = () => {
    try {
      if (typeof backToMain === "function") {
        backToMain();
        return;
      }
    } catch {
      /* ignore */
    }
    navigate("/dashboard");
  };

  return (
    <AppShell>
      <ToastContainer />
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
            Loading...
          </div>
        </div>
      )}

      <div className="ap-page-wrap page-scroll-scope">
        <div className="onb-wrap compact">
          <h2 className="onb-title">
            {isReactiveAttuidFlow
              ? "Reactivate ATTUID"
              : dtvPrefillHeadingOverride ||
                editHeadingOverride ||
                (isEditRoute
                  ? `Edit Onboarding Details (${roleLabelForTitle})`
                  : `New Onboarding (${roleLabelForTitle})`)}
          </h2>

          {/* Worker Type + Vendor Name (side-by-side, aligned) */}
          <div
            className="row-between"
            style={{
              marginBottom: 24,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              columnGap: 48,
              paddingLeft: 0,
            }}
          >
            <div className="worker-type" style={{ width: 260, minWidth: 360 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: "#000" }}>*</span>
                <span>Vendor Name</span>
              </span>
              {fieldErrors.vendor && (
                <div
                  style={{
                    color: "red",
                    fontSize: 12,
                    display: "inline-block",
                    marginLeft: 8,
                    marginTop: 4,
                  }}
                >
                  {fieldErrors.vendor}
                </div>
              )}
              <div className="select like-input">
                {/* react-select rendered inside, but sized similar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Select
                    placeholder={
                      vendorInput?.length >= 3
                        ? "Select Vendor"
                        : "Type at least 3 characters..."
                    }
                    isClearable
                    isSearchable
                    isLoading={vendorLoading}
                    options={vendorOptions}
                    value={
                      form1.vendor && form1.supplierReference
                        ? {
                            label: `${form1.vendor} (${form1.supplierReference})`,
                            value: form1.supplierReference,
                          }
                        : null
                    }
                    onInputChange={(input, { action }) => {
                      if (action === "input-change") {
                        fetchVendors(input);
                      }
                    }}
                    onChange={(opt) => {
                      const supplierName = opt?.meta?.supplierName || "";
                      const supplierNumber = opt?.meta?.supplierNumber || "";
                      setForm1((prev) => ({
                        ...prev,
                        vendor: supplierName,
                        supplierReference: supplierNumber,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        vendor: "",
                      }));
                    }}
                    styles={{
                      control: (b) => ({
                        ...b,
                        minHeight: 32,
                        height: 32,
                        fontSize: 14,
                        border: "none",
                        boxShadow: "none",
                      }),
                      valueContainer: (b) => ({
                        ...b,
                        padding: "0 6px",
                      }),
                      dropdownIndicator: (b) => ({
                        ...b,
                        padding: 4,
                      }),
                      clearIndicator: (b) => ({
                        ...b,
                        padding: 4,
                      }),
                      indicatorsContainer: (b) => ({
                        ...b,
                        height: 32,
                      }),
                      input: (b) => ({
                        ...b,
                        margin: 0,
                        padding: 0,
                      }),
                      menu: (b) => ({
                        ...b,
                        fontSize: 14,
                      }),
                    }}
                  />
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 4,
                  display: "block",
                }}
              ></span>
            </div>
          </div>

          {/* Stepper */}
          <div className="stepper" style={{ marginBottom: 24 }}>
            <StepDot
              n={1}
              active={step === 1}
              done={step > 1}
              label="Enter Details"
            />
            <StepBar done={step > 1} />
            <StepDot
              n={2}
              active={step === 2}
              done={step > 2}
              label="Review & Submit"
            />
          </div>

          {step === 1 && (
            <div
              className="grid-3"
              style={{
                gridGap: "32px",
                padding: "24px 40px 40px",
                alignItems: "flex-start",
              }}
            >
              <div>
                <Section title="Personal Details:">
                  <Row
                    NonMandatory={NonMandatory.includes("firstName")}
                    label="First Name"
                  >
                    <input
                      value={form1.firstName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm1({
                          ...form1,
                          firstName: value,
                        });
                        runFieldValidation("firstName", value);
                      }}
                    />
                    {fieldErrors.firstName && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.firstName}
                      </div>
                    )}
                  </Row>
                  <Row
                    NonMandatory={NonMandatory.includes("lastName")}
                    label="Last Name"
                  >
                    <input
                      value={form1.lastName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm1({
                          ...form1,
                          lastName: value,
                        });
                        runFieldValidation("lastName", value);
                      }}
                    />
                    {fieldErrors.lastName && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.lastName}
                      </div>
                    )}
                  </Row>
                  <Row label="Email">
                    <input
                      value={form1.email || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm1({
                          ...form1,
                          email: value,
                        });
                        runFieldValidation("email", value);
                      }}
                    />
                    {fieldErrors.email && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.email}
                      </div>
                    )}
                  </Row>
                  <Row label="Phone" NonMandatory={true}>
                    <input
                      type="tel"
                      placeholder={
                        isUnitedStatesSelected(form1.country)
                          ? "1st & 4th digits between 2-9"
                          : ""
                      }
                      value={form1.phone || ""}
                      onChange={(e) => {
                        const maxPhoneLen = isUnitedStatesSelected(
                          form1.country,
                        )
                          ? 10
                          : 15;
                        const digitsOnly = String(e.target.value || "")
                          .replace(/\D/g, "")
                          .slice(0, maxPhoneLen);
                        setForm1({
                          ...form1,
                          phone: digitsOnly,
                        });
                        runFieldValidation("phone", digitsOnly);
                      }}
                    />
                    {fieldErrors.phone && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.phone}
                      </div>
                    )}
                  </Row>
                  {showDtvidAttuidPrefill && (
                    <>
                      {Boolean(
                        String(
                          form1?.dtvId ||
                            editData?.dtvId ||
                            editData?.dtv_id ||
                            editData?.dtvAnchorId ||
                            routeDtvid ||
                            "",
                        ).trim(),
                      ) && (
                        <TextRow
                          label="DTVID"
                          inline
                          rdOnly
                          NonMandatory
                          v={String(
                            form1?.dtvId ||
                              editData?.dtvId ||
                              editData?.dtv_id ||
                              editData?.dtvAnchorId ||
                              routeDtvid ||
                              "",
                          ).trim()}
                          onChange={() => {}}
                        />
                      )}
                      {Boolean(
                        String(
                          form1?.attuid ||
                            editData?.attuid ||
                            editData?.attUid ||
                            editData?.attUID ||
                            editData?.att_uid ||
                            "",
                        ).trim(),
                      ) && (
                        <TextRow
                          label="ATTUID"
                          inline
                          rdOnly
                          NonMandatory
                          v={String(
                            form1?.attuid ||
                              editData?.attuid ||
                              editData?.attUid ||
                              editData?.attUID ||
                              editData?.att_uid ||
                              "",
                          ).trim()}
                          onChange={() => {}}
                        />
                      )}
                    </>
                  )}
                  {attuidRequiredOn && (
                    <Row label="Country of Citizenship">
                      <Select
                        options={
                          citizenshipOptions && citizenshipOptions.length
                            ? citizenshipOptions
                            : countryOptions
                        }
                        value={ctzn}
                        onChange={(e) => {
                          setCitizen(e);
                          const val = e?.label || "";
                          setForm1((prev) => ({
                            ...prev,
                            citizenship: val,
                          }));
                          runFieldValidation("citizenship", val);
                        }}
                        placeholder="Select"
                        isSearchable
                        styles={{
                          control: (b) => ({
                            ...b,
                            minHeight: 20,
                            fontSize: 14,
                          }),
                          dropdownIndicator: (b) => ({
                            ...b,
                            padding: 2,
                          }),
                          clearIndicator: (b) => ({
                            ...b,
                            padding: 2,
                          }),
                          valueContainer: (b) => ({
                            ...b,
                            padding: "0 6px",
                          }),
                          input: (b) => ({
                            ...b,
                            margin: 0,
                            padding: 0,
                          }),
                        }}
                      />
                      {fieldErrors.citizenship && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            display: "inline-block",
                            marginLeft: 8,
                            marginTop: 4,
                          }}
                        >
                          {fieldErrors.citizenship}
                        </div>
                      )}
                    </Row>
                  )}
                </Section>

                {/* Project Details block is intentionally removed for Employee Supplier Admin */}
              </div>

              <div>
                <Section title="Sponsorship & Role:">
                  <TextRow
                    label="DTV Sponsor DTVUID (Owns portal record)"
                    v={form1.sponsor}
                    maxLength={DTVUID_MAX_LEN}
                    onChange={(v) => {
                      const next = String(v || "").slice(0, DTVUID_MAX_LEN);
                      setForm1({
                        ...form1,
                        sponsor: next,
                        sponsor_email: "",
                        sponsorEmail: "",
                      });
                      runFieldValidation("sponsor", next);
                    }}
                    error={
                      !!fieldErrors.sponsor ||
                      sponsorValidation.status === "error"
                    }
                    errorLabel={
                      fieldErrors.sponsor && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            display: "inline-block",
                            marginLeft: 8,
                            marginTop: 4,
                          }}
                        >
                          {fieldErrors.sponsor}
                        </div>
                      )
                    }
                  />
                  {ENABLE_SPONSOR_VALIDATION &&
                    sponsorValidation.status !== "idle" && (
                      <div
                        className="field-hint"
                        style={{
                          fontSize: 12,
                          marginTop: 4,
                          color:
                            sponsorValidation.status === "error"
                              ? "red"
                              : sponsorValidation.status === "ok"
                                ? "green"
                                : "#555",
                        }}
                      >
                        {sponsorValidation.status === "checking" &&
                          "Validating sponsor..."}
                        {sponsorValidation.status === "ok" &&
                          "Sponsor verified."}
                        {sponsorValidation.status === "error" &&
                          sponsorValidation.message}
                      </div>
                    )}

                  <TextRow
                    label="Webphone Manager DTVUID (HR line manager)"
                    v={form1.webphoneManagerId}
                    NonMandatory={false}
                    maxLength={DTVUID_MAX_LEN}
                    onChange={(v) => {
                      const next = String(v || "").slice(0, DTVUID_MAX_LEN);
                      setForm1({
                        ...form1,
                        webphoneManagerId: next,
                        webphone_manager_attuid: "",
                        webphone_manager_email: "",
                        webphoneManagerEmail: "",
                      });
                      runFieldValidation("webphoneManagerId", next);
                    }}
                    error={
                      !!fieldErrors.webphoneManagerId ||
                      webphoneValidation.status === "error"
                    }
                    errorLabel={null}
                  />
                  {(fieldErrors.webphoneManagerId ||
                    (ENABLE_WEBPHONE_VALIDATION &&
                      webphoneValidation.status !== "idle")) && (
                    <div
                      className="field-hint"
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        color: fieldErrors.webphoneManagerId
                          ? "red"
                          : webphoneValidation.status === "error"
                            ? "red"
                            : webphoneValidation.status === "ok"
                              ? "green"
                              : "#555",
                      }}
                    >
                      {fieldErrors.webphoneManagerId ||
                        (webphoneValidation.status === "checking"
                          ? "Validating Webphone Manager..."
                          : webphoneValidation.status === "ok"
                            ? "Webphone Manager verified."
                            : webphoneValidation.status === "error"
                              ? webphoneValidation.message
                              : "")}
                    </div>
                  )}

                  {/* SEARCHABLE Role (Title) dropdown */}
                  <Row label="Country">
                    <Select
                      options={countryOptions}
                      value={selectedCountry}
                      onChange={(opt) => {
                        setSelectedCountry(opt);
                        setSelectedState(null);
                        setSelectedCity(null);
                        setCityOptions([]);
                        const label = opt?.label || "";
                        setForm1((p) => ({
                          ...p,
                          country: label,
                          state: "",
                          city: "",
                        }));
                        setFieldErrors((prev) => ({
                          ...prev,
                          country: "",
                          state: "",
                          city: "",
                        }));
                      }}
                      placeholder="Select"
                      isClearable
                      styles={{
                        control: (b) => ({
                          ...b,
                          minHeight: 20,
                          fontSize: 14,
                        }),
                        dropdownIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        clearIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        valueContainer: (b) => ({
                          ...b,
                          padding: "0 6px",
                        }),
                        input: (b) => ({
                          ...b,
                          margin: 0,
                          padding: 0,
                        }),
                      }}
                    />
                    {fieldErrors.country && (
                      <div
                        style={{
                          display: "inline-block",
                          marginLeft: 12,
                          color: "red",
                          fontSize: 12,
                          maxWidth: 220,
                          verticalAlign: "middle",
                        }}
                      >
                        {fieldErrors.country}
                      </div>
                    )}
                  </Row>

                  <Row label="State" NonMandatory={!isStateRequired}>
                    <Select
                      options={stateOptions}
                      value={selectedState}
                      onChange={(opt) => {
                        setSelectedState(opt);
                        setSelectedCity(null);
                        if (!opt) setCityOptions([]);
                        const label = opt?.label || "";
                        setForm1((p) => ({
                          ...p,
                          state: label,
                          city: "",
                        }));
                        setFieldErrors((prev) => ({
                          ...prev,
                          state: "",
                          city: "",
                        }));
                      }}
                      placeholder="Select"
                      isClearable
                      isDisabled={!selectedCountry}
                      noOptionsMessage={() =>
                        selectedCountry
                          ? "No states found"
                          : "Select a country first"
                      }
                      styles={{
                        control: (b) => ({
                          ...b,
                          minHeight: 20,
                          fontSize: 14,
                        }),
                        dropdownIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        clearIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        valueContainer: (b) => ({
                          ...b,
                          padding: "0 6px",
                        }),
                        input: (b) => ({
                          ...b,
                          margin: 0,
                          padding: 0,
                        }),
                      }}
                    />
                    {fieldErrors.state && (
                      <div
                        style={{
                          display: "inline-block",
                          marginLeft: 12,
                          color: "red",
                          fontSize: 12,
                          maxWidth: 220,
                          verticalAlign: "middle",
                        }}
                      >
                        {fieldErrors.state}
                      </div>
                    )}
                  </Row>

                  <Row label="City" NonMandatory={!isCityRequired}>
                    <Select
                      options={cityOptions}
                      value={selectedCity}
                      onChange={handleCityChange}
                      placeholder="Select"
                      isClearable
                      isDisabled={!selectedState}
                      noOptionsMessage={() =>
                        selectedState
                          ? "No cities found"
                          : "Select a state first"
                      }
                      styles={{
                        control: (b) => ({
                          ...b,
                          minHeight: 20,
                          fontSize: 14,
                        }),
                        dropdownIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        clearIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        valueContainer: (b) => ({
                          ...b,
                          padding: "0 6px",
                        }),
                        input: (b) => ({
                          ...b,
                          margin: 0,
                          padding: 0,
                        }),
                      }}
                    />
                    {fieldErrors.city && (
                      <div
                        style={{
                          display: "inline-block",
                          marginLeft: 12,
                          color: "red",
                          fontSize: 12,
                          maxWidth: 220,
                          verticalAlign: "middle",
                        }}
                      >
                        {fieldErrors.city}
                      </div>
                    )}
                  </Row>

                  <Row label="Time Zone" NonMandatory={true}>
                    <Select
                      options={timeZoneOptions}
                      value={selectedTimeZone}
                      onChange={(e) => {
                        const val = e?.label || "";
                        setForm1({
                          ...form1,
                          timeZone: val,
                        });
                        SetSelectedTimeZone(e);
                        runFieldValidation("timeZone", val);
                      }}
                      placeholder="Search"
                      isSearchable
                      styles={{
                        control: (b) => ({
                          ...b,
                          minHeight: 20,
                          fontSize: 14,
                        }),
                        dropdownIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        clearIndicator: (b) => ({
                          ...b,
                          padding: 2,
                        }),
                        valueContainer: (b) => ({
                          ...b,
                          padding: "0 6px",
                        }),
                        input: (b) => ({
                          ...b,
                          margin: 0,
                          padding: 0,
                        }),
                      }}
                    />
                    {fieldErrors.timeZone && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.timeZone}
                      </div>
                    )}
                  </Row>

                  <Row label="Occupancy (Virtual/Office)">
                    <select
                      style={{ width: "108%" }}
                      value={form1.occupancyCode}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm1({
                          ...form1,
                          occupancyCode: v,
                        });
                        runFieldValidation("occupancyCode", v);
                      }}
                    >
                      <option value="">Select</option>
                      {dd.occupancyCode.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.occupancyCode && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.occupancyCode}
                      </div>
                    )}
                  </Row>

                  {/* Attuid toggle row forced into one line */}
                  <div
                    className="row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "12px",
                      flexWrap: "nowrap",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <label
                      style={{
                        marginBottom: 0,
                        marginRight: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Attuid Required?
                    </label>
                    <div className="toggle-wrap">
                      <button
                        type="button"
                        className={`toggle-switch ${
                          attuidRequiredOn ? "on" : "off"
                        }`}
                        onClick={handleAttuidToggle}
                        aria-pressed={attuidRequiredOn}
                      >
                        <span className="toggle-knob" />
                      </button>
                      <span className="toggle-label">
                        {attuidRequiredOn ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>

                  {attuidRequiredOn && (
                    <Row
                      label="Role (Title)"
                      NonMandatory={NonMandatory.includes("role")}
                    >
                      <Select
                        options={dd.roles.map((r) => ({
                          label: r,
                          value: r,
                        }))}
                        value={
                          form1.role
                            ? {
                                label: form1.role,
                                value: form1.role,
                              }
                            : null
                        }
                        onChange={(opt) => {
                          setForm1({
                            ...form1,
                            role: opt?.value || "",
                          });
                          setFieldErrors((prev) => ({
                            ...prev,
                            role: "",
                          }));
                        }}
                        placeholder="Select"
                        isClearable
                        isSearchable
                        styles={{
                          control: (b) => ({
                            ...b,
                            minHeight: 20,
                            fontSize: 14,
                          }),
                          dropdownIndicator: (b) => ({
                            ...b,
                            padding: 2,
                          }),
                          clearIndicator: (b) => ({
                            ...b,
                            padding: 2,
                          }),
                          valueContainer: (b) => ({
                            ...b,
                            padding: "0 6px",
                          }),
                          input: (b) => ({
                            ...b,
                            margin: 0,
                            padding: 0,
                          }),
                        }}
                      />
                      {fieldErrors.role && (
                        <div
                          style={{
                            display: "inline-block",
                            marginLeft: 12,
                            color: "red",
                            fontSize: 12,
                            maxWidth: 220,
                            verticalAlign: "middle",
                          }}
                        >
                          {fieldErrors.role}
                        </div>
                      )}
                    </Row>
                  )}

                  {attuidRequiredOn && (
                    <>
                      <Row
                        label="W2 / 1099 or International"
                        NonMandatory={false}
                      >
                        <select
                          style={{ width: "108%" }}
                          value={form1.workerTypeTax}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm1({
                              ...form1,
                              workerTypeTax: v,
                            });
                            runFieldValidation("workerTypeTax", v);
                          }}
                          disabled={false}
                        >
                          <option value="">Select</option>
                          {dd.workerTypeTax.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </Row>
                      {fieldErrors.workerTypeTax && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            display: "inline-block",
                            marginLeft: 8,
                            marginTop: 4,
                          }}
                        >
                          {fieldErrors.workerTypeTax}
                        </div>
                      )}
                    </>
                  )}
                </Section>
              </div>

              <div>
                <Section title="Contact & Assignment Details:">
                  <TextRow
                    label="Contract #"
                    NonMandatory={true}
                    v={form1.contractPo}
                    maxLength={CONTRACT_FIELDS_MAX_LEN}
                    onChange={(v) => {
                      const next = String(v || "").slice(
                        0,
                        CONTRACT_FIELDS_MAX_LEN,
                      );
                      setForm1({
                        ...form1,
                        contractPo: next,
                      });
                      runFieldValidation("contractPo", next);
                    }}
                  />
                  {contractValidation.status !== "idle" && (
                    <div
                      className="field-hint"
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        color:
                          contractValidation.status === "error"
                            ? "#b45309"
                            : contractValidation.status === "ok"
                              ? "green"
                              : "#555",
                      }}
                    >
                      {contractValidation.status === "checking" &&
                        "Validating contract..."}
                      {contractValidation.status === "ok" &&
                        "Contract verified."}
                      {contractValidation.status === "error" &&
                        contractValidation.message}
                    </div>
                  )}
                  {fieldErrors.contractPo && (
                    <div
                      style={{
                        color: "#b45309",
                        fontSize: 12,
                        display: "inline-block",
                        marginLeft: 8,
                        marginTop: 4,
                      }}
                    >
                      {fieldErrors.contractPo}
                    </div>
                  )}
                  <TextRow
                    label="PO #"
                    NonMandatory={true}
                    v={form1.poNum}
                    maxLength={CONTRACT_FIELDS_MAX_LEN}
                    onChange={(v) => {
                      const next = String(v || "").slice(
                        0,
                        CONTRACT_FIELDS_MAX_LEN,
                      );
                      setForm1({
                        ...form1,
                        poNum: next,
                      });
                      runFieldValidation("poNum", next);
                    }}
                  />
                  {poValidation.status !== "idle" && (
                    <div
                      className="field-hint"
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        color:
                          poValidation.status === "error"
                            ? "#b45309"
                            : poValidation.status === "ok"
                              ? "green"
                              : "#555",
                      }}
                    >
                      {poValidation.status === "checking" &&
                        "Validating purchase order..."}
                      {poValidation.status === "ok" &&
                        "Purchase order verified."}
                      {poValidation.status === "error" && poValidation.message}
                    </div>
                  )}
                  {fieldErrors.poNum && (
                    <div
                      style={{
                        color: "#b45309",
                        fontSize: 12,
                        display: "inline-block",
                        marginLeft: 8,
                        marginTop: 4,
                      }}
                    >
                      {fieldErrors.poNum}
                    </div>
                  )}
                  <TextRow
                    label="Line #"
                    NonMandatory={true}
                    v={form1.lineNum}
                    maxLength={CONTRACT_FIELDS_MAX_LEN}
                    onChange={(v) => {
                      const next = String(v || "").slice(
                        0,
                        CONTRACT_FIELDS_MAX_LEN,
                      );
                      setForm1({
                        ...form1,
                        lineNum: next,
                      });
                      runFieldValidation("lineNum", next);
                    }}
                  />
                  {lineValidation.status !== "idle" && (
                    <div
                      className="field-hint"
                      style={{
                        fontSize: 12,
                        marginTop: 4,
                        color:
                          lineValidation.status === "error"
                            ? "#b45309"
                            : lineValidation.status === "ok"
                              ? "green"
                              : "#555",
                      }}
                    >
                      {lineValidation.status === "checking" &&
                        "Validating PO line..."}
                      {lineValidation.status === "ok" && "PO line verified."}
                      {lineValidation.status === "error" &&
                        lineValidation.message}
                    </div>
                  )}
                  {fieldErrors.lineNum && (
                    <div
                      style={{
                        color: "#b45309",
                        fontSize: 12,
                        display: "inline-block",
                        marginLeft: 8,
                        marginTop: 4,
                      }}
                    >
                      {fieldErrors.lineNum}
                    </div>
                  )}

                  <Row label="Start Date">
                    <DatePicker
                      className="input-styles"
                      selected={form1.startDate}
                      onChange={(date) => {
                        setForm1((prev) => ({
                          ...prev,
                          startDate: date,
                          endDate:
                            prev.endDate && date && prev.endDate < date
                              ? null
                              : prev.endDate,
                        }));
                        runFieldValidation("startDate", date);
                      }}
                      showYearDropdown
                      scrollableYearDropdown
                      dateFormat="MM/dd/yyyy"
                      minDate={minStartDate}
                      maxDate={maxStartDate}
                      popperPlacement="bottom-start"
                      portalId="root"
                      calendarClassName="custom-calendar"
                      popperClassName="tb-datepicker-popper"
                      popperModifiers={[
                        offset(8),
                        shift({ padding: 8 }),
                        flip({
                          fallbackPlacements: [
                            "top-start",
                            "bottom-start",
                            "top-end",
                            "bottom-end",
                          ],
                        }),
                      ]}
                    />
                    {fieldErrors.startDate && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.startDate}
                      </div>
                    )}
                  </Row>

                  <Row label="End Date">
                    <DatePicker
                      className="input-styles"
                      selected={form1.endDate}
                      onChange={(date) => {
                        setForm1({
                          ...form1,
                          endDate: date,
                        });
                        runFieldValidation("endDate", date);
                      }}
                      showYearDropdown
                      scrollableYearDropdown
                      dateFormat="MM/dd/yyyy"
                      disabled={!form1.startDate}
                      minDate={minEndDate}
                      popperPlacement="bottom-start"
                      portalId="root"
                      calendarClassName="custom-calendar"
                      popperClassName="tb-datepicker-popper"
                      popperModifiers={[
                        offset(8),
                        shift({ padding: 8 }),
                        flip({
                          fallbackPlacements: [
                            "top-start",
                            "bottom-start",
                            "top-end",
                            "bottom-end",
                          ],
                        }),
                      ]}
                    />
                    {fieldErrors.endDate && (
                      <div
                        style={{
                          color: "red",
                          fontSize: 12,
                          display: "inline-block",
                          marginLeft: 8,
                          marginTop: 4,
                        }}
                      >
                        {fieldErrors.endDate}
                      </div>
                    )}
                  </Row>

                  {/* SECURITY ID (masked + eye inside input) */}
                  <Row
                    NonMandatory={
                      isSecurityLocked || isSecurityOptionalEditMode
                    }
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span>{securityLabel}</span>
                          <button
                            type="button"
                            title={securityTooltipText}
                            onClick={(e) => e.preventDefault()}
                            style={{
                              border: "1px solid #d97706",
                              background: "#fffbeb",
                              color: "#92400e",
                              borderRadius: 999,
                              width: 18,
                              height: 18,
                              lineHeight: "16px",
                              fontSize: 12,
                              cursor: "help",
                              padding: 0,
                            }}
                          >
                            i
                          </button>
                        </span>
                        {showSecurityEditButtonUi && (
                          <button
                            type="button"
                            onClick={handleEnableSecurityEdit}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: "#fff",
                              color: "#0f172a",
                              padding: "2px 10px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 12,
                              lineHeight: "20px",
                            }}
                          >
                            Edit
                          </button>
                        )}
                        {showSecurityDisableEditButtonUi && (
                          <button
                            type="button"
                            onClick={handleDisableSecurityEdit}
                            style={{
                              border: "1px solid #cbd5e1",
                              background: "#fff",
                              color: "#0f172a",
                              padding: "2px 10px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 12,
                              lineHeight: "20px",
                            }}
                          >
                            Disable
                          </button>
                        )}
                      </span>
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        width: SECURITY_INPUT_WIDTH_PX,
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: SECURITY_INPUT_WIDTH_PX,
                        }}
                      >
                        <input
                          ref={securityRef}
                          value={securityDisplayValue}
                          placeholder={securityPlaceholder}
                          readOnly={isSecurityReadOnly}
                          title={
                            isSecurityReadOnly
                              ? "Click Edit to update Security ID"
                              : ""
                          }
                          inputMode="numeric"
                          autoComplete="off"
                          onChange={handleSecChange("securityId")}
                          onKeyDown={handleSecKeyDown("securityId")}
                          onPaste={handleSecPaste("securityId")}
                          onFocus={handleSecFocus("securityId")}
                          onBlur={() => {
                            if (!isSecurityReadOnly)
                              runFieldValidation(
                                "securityId",
                                form1.securityId,
                              );
                          }}
                          style={{
                            width: SECURITY_INPUT_WIDTH_PX,
                            paddingRight: 42,
                            boxSizing: "border-box",
                            backgroundColor: isSecurityReadOnly
                              ? "#e5e7eb"
                              : undefined,
                            cursor: isSecurityReadOnly
                              ? "not-allowed"
                              : undefined,
                            opacity: isSecurityReadOnly ? 0.9 : 1,
                          }}
                        />
                        <button
                          type="button"
                          aria-label={
                            showSecurityId
                              ? "Hide Security ID"
                              : "Show Security ID"
                          }
                          title={showSecurityId ? "Hide" : "Show"}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setShowSecurityId((p) => !p);
                            queueMicrotask(() => securityRef.current?.focus());
                          }}
                          style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            height: 26,
                            width: 30,
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#334155",
                            padding: 0,
                          }}
                        >
                          <EyeIcon />
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          width: SECURITY_INPUT_WIDTH_PX,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {securityHintPrimary}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          width: SECURITY_INPUT_WIDTH_PX,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {securityHintFormat}
                      </div>
                      {!isSecurityReadOnly &&
                        securityIdExistence?.status === "checking" && (
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: 12,
                              display: "inline-block",
                              marginTop: 4,
                              width: SECURITY_INPUT_WIDTH_PX,
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {securityIdExistence.message}
                          </div>
                        )}
                      {!isSecurityReadOnly &&
                        securityIdExistence?.status === "found" && (
                          <div
                            style={{
                              color: "#b45309",
                              fontSize: 12,
                              display: "inline-block",
                              marginTop: 4,
                              width: SECURITY_INPUT_WIDTH_PX,
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {securityIdExistence.message}
                          </div>
                        )}
                      {!isSecurityReadOnly &&
                        securityIdExistence?.status === "none" && (
                          <div
                            style={{
                              color: "#16a34a",
                              fontSize: 12,
                              display: "inline-block",
                              marginTop: 4,
                              width: SECURITY_INPUT_WIDTH_PX,
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {securityIdExistence.message}
                          </div>
                        )}
                      {!isSecurityReadOnly && fieldErrors.securityId && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            display: "inline-block",
                            marginTop: 4,
                            width: SECURITY_INPUT_WIDTH_PX,
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {fieldErrors.securityId}
                        </div>
                      )}
                    </div>
                  </Row>

                  {/* CONFIRM SECURITY ID (masked + eye inside input) */}
                  <Row
                    NonMandatory={
                      isSecurityLocked || isSecurityOptionalEditMode
                    }
                    label={confirmSecurityLabel}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        width: SECURITY_INPUT_WIDTH_PX,
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: SECURITY_INPUT_WIDTH_PX,
                        }}
                      >
                        <input
                          ref={confirmSecurityRef}
                          value={confirmDisplayValue}
                          placeholder={securityPlaceholder}
                          readOnly={isSecurityReadOnly}
                          title={
                            isSecurityReadOnly
                              ? "Click Edit to update Security ID"
                              : ""
                          }
                          inputMode="numeric"
                          autoComplete="off"
                          onChange={handleSecChange("validateSecurity")}
                          onKeyDown={handleSecKeyDown("validateSecurity")}
                          onPaste={handleSecPaste("validateSecurity")}
                          onFocus={handleSecFocus("validateSecurity")}
                          onBlur={() => {
                            if (!isSecurityReadOnly)
                              runFieldValidation(
                                "validateSecurity",
                                form1.validateSecurity,
                              );
                          }}
                          style={{
                            width: SECURITY_INPUT_WIDTH_PX,
                            paddingRight: 42,
                            boxSizing: "border-box",
                            backgroundColor: isSecurityReadOnly
                              ? "#e5e7eb"
                              : undefined,
                            cursor: isSecurityReadOnly
                              ? "not-allowed"
                              : undefined,
                            opacity: isSecurityReadOnly ? 0.9 : 1,
                          }}
                        />
                        <button
                          type="button"
                          aria-label={
                            showConfirmSecurityId
                              ? "Hide Confirm Security ID"
                              : "Show Confirm Security ID"
                          }
                          title={showConfirmSecurityId ? "Hide" : "Show"}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setShowConfirmSecurityId((p) => !p);
                            queueMicrotask(() =>
                              confirmSecurityRef.current?.focus(),
                            );
                          }}
                          style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            height: 26,
                            width: 30,
                            border: 0,
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#334155",
                            padding: 0,
                          }}
                        >
                          <EyeIcon />
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          width: SECURITY_INPUT_WIDTH_PX,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {securityHintPrimary}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#64748b",
                          width: SECURITY_INPUT_WIDTH_PX,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {securityHintFormat}
                      </div>
                      {!isSecurityReadOnly && fieldErrors.validateSecurity && (
                        <div
                          style={{
                            color: "red",
                            fontSize: 12,
                            display: "inline-block",
                            marginTop: 4,
                            width: SECURITY_INPUT_WIDTH_PX,
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {fieldErrors.validateSecurity}
                        </div>
                      )}
                    </div>
                  </Row>

                  <Row label="Billable (Yes / No)" NonMandatory={false}>
                    <select
                      style={{ width: "108%" }}
                      value={form1.nonBillable}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm1({
                          ...form1,
                          nonBillable: v,
                        });
                        runFieldValidation("nonBillable", v);
                      }}
                    >
                      <option value="">Select</option>
                      {dd.nonBillable.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Row>
                  {fieldErrors.nonBillable && (
                    <div
                      style={{
                        color: "red",
                        fontSize: 12,
                        display: "inline-block",
                        marginLeft: 8,
                        marginTop: 4,
                      }}
                    >
                      {fieldErrors.nonBillable}
                    </div>
                  )}
                </Section>
              </div>
            </div>
          )}

          {step === 2 && (
            <div
              className="review"
              style={{
                padding: "32px 40px 40px",
                width: "100%",
                maxWidth: "1800px",
                margin: "0 auto",
              }}
            >
              <div
                className="grid-3"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  columnGap: "25px",
                  rowGap: "18px",
                  alignItems: "flex-start",
                }}
              >
                {/* COLUMN 1: Personal + Vendor (STACKED VERTICAL) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                  }}
                >
                  <Overview
                    setStep={setStep}
                    title="Personal Details:"
                    form={1}
                    items={{
                      "First Name": form1.firstName,
                      "Last Name": form1.lastName,
                      Email: form1.email,
                      Phone: form1.phone,
                      ...(showDtvidAttuidPrefill
                        ? {
                            ...(String(
                              form1?.dtvId ||
                                editData?.dtvId ||
                                editData?.dtv_id ||
                                editData?.dtvAnchorId ||
                                routeDtvid ||
                                "",
                            ).trim()
                              ? {
                                  DTVID: String(
                                    form1?.dtvId ||
                                      editData?.dtvId ||
                                      editData?.dtv_id ||
                                      editData?.dtvAnchorId ||
                                      routeDtvid ||
                                      "",
                                  ).trim(),
                                }
                              : {}),
                            ...(String(
                              form1?.attuid ||
                                editData?.attuid ||
                                editData?.attUid ||
                                editData?.attUID ||
                                editData?.att_uid ||
                                "",
                            ).trim()
                              ? {
                                  ATTUID: String(
                                    form1?.attuid ||
                                      editData?.attuid ||
                                      editData?.attUid ||
                                      editData?.attUID ||
                                      editData?.att_uid ||
                                      "",
                                  ).trim(),
                                }
                              : {}),
                          }
                        : {}),
                      ...(attuidRequiredOn
                        ? {
                            "Country of Citizenship": form1.citizenship,
                          }
                        : {}),
                    }}
                  />

                  <Overview
                    setStep={setStep}
                    title="Vendor / Project Details:"
                    form={1}
                    items={{
                      "Vendor Name": form1.vendor,
                      "Supplier Reference": form1.supplierReference,
                    }}
                  />
                </div>

                {/* COLUMN 2: Sponsorship & Role */}
                <Overview
                  setStep={setStep}
                  title="Sponsorship & Role:"
                  form={1}
                  items={{
                    //  Sponsor: form1.sponsor,
                    // [FIELD_LABELS.sponsor]: form1.sponsor,
                    "DTV Sponsor DTVUID": form1.sponsor,
                    // "Webphone Manager":  form1.webphoneManagerId,
                    "Webphone Manager DTVUID": form1.webphoneManagerId,
                    Role: form1.role,
                    Country: form1.country,
                    State: form1.state,
                    City: form1.city,
                    "Time Zone": form1.timeZone,
                    Occupancy: form1.occupancyCode,
                    "Attuid Required": form1.attuidRequired,
                    ...(attuidRequiredOn
                      ? {
                          "W2 / 1099 or International": form1.workerTypeTax,
                        }
                      : {}),
                    ...(attuidRequiredOn && form1.attRoleCpRoleId
                      ? {
                          "ATT Role": `${form1.attRoleCpRoleId} - ${
                            form1.attRoleCpProfileDesc || ""
                          }`,
                        }
                      : {}),
                  }}
                />

                {/* COLUMN 3: Contact & Assignment */}
                <Overview
                  setStep={setStep}
                  title="Contact & Assignment Details:"
                  form={1}
                  items={{
                    "Contract #": form1.contractPo,
                    "PO #": form1.poNum,
                    "Line #": form1.lineNum,
                    "Start Date": form1.startDate && fmtDate(form1.startDate),
                    "End Date": form1.endDate && fmtDate(form1.endDate),
                    [securityLabel]: form1.securityId
                      ? buildMaskedLast4(
                          isUS,
                          clampDigitsToFormat(
                            isUS,
                            extractDigits(form1.securityId, isUS),
                          ),
                        )
                      : "-",
                    // [confirmSecurityLabel]: form1.validateSecurity
                    //     ? buildMaskedLast4(isUS, clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS))))
                    //     : "-",

                    Billable: form1.nonBillable,
                  }}
                />
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div
            className="onb-actions"
            style={{ paddingRight: "100px" }} // move buttons a bit left
          >
            <div
              className="right"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <button className="button back" onClick={handleCancel}>
                Cancel
              </button>
              {step > 1 ? (
                <button
                  className="button back"
                  onClick={() => {
                    if (isEditRoute && hasExternalCallerTarget) {
                      handleBackToCaller();
                      return;
                    }
                    setStep(step - 1);
                  }}
                >
                  ≪ Back
                </button>
              ) : (
                <>
                  <span />
                  {showDtvidAttuidPrefill && (
                    <button
                      className="button back"
                      onClick={handleBackToCaller}
                    >
                      <span style={{ color: "grey" }}> «</span> Back
                    </button>
                  )}
                </>
              )}
              {(editData === null || editData?.status === "Draft") && (
                <button
                  className="button back"
                  onClick={saveDraft}
                  disabled={loading}
                >
                  Save Draft
                </button>
              )}
              {step < 2 ? (
                <button
                  className="btn primary"
                  onClick={() => {
                    console.log("[CONTINUE BUTTON] CLICKED");
                    console.log("[CONTINUE BUTTON] Calling handleContinue()");

                    handleContinue();

                    console.log("[CONTINUE BUTTON] handleContinue() RETURNED");
                  }}
                >
                  Continue ≫
                </button>
              ) : (
                <>
                  {requiresCpValidateBeforeSubmit && (
                    <button
                      className="button back"
                      onClick={validateBeforeSubmit}
                      disabled={
                        loading ||
                        submitting ||
                        cpValidation.status === "checking"
                      }
                    >
                      {cpValidation.status === "checking"
                        ? "Validating..."
                        : "Validate"}
                    </button>
                  )}
                  <button
                    className="btn primary"
                    onClick={submitAll}
                    disabled={
                      submitting ||
                      loading ||
                      (requiresCpValidateBeforeSubmit &&
                        cpValidation.status !== "passed")
                    }
                    title={
                      requiresCpValidateBeforeSubmit &&
                      cpValidation.status !== "passed"
                        ? "Run Validate first"
                        : undefined
                    }
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* ================================================================== */
/*  SMALL HELPER COMPONENTS                                           */
/* ================================================================== */

function Section({ title, children, style = false }) {
  return (
    <div style={style !== false ? style : {}} className="onb-section">
      <div className="onb-section-title">
        {title}
        <hr style={{ border: "none", borderTop: "1px solid #ddd" }} />
      </div>
      <div className="onb-section-body">{children}</div>
    </div>
  );
}
function Row({
  label,
  children,
  rdOnly,
  NonMandatory = false,
  inline = false,
}) {
  return (
    <div className={`row ${inline ? "row-inline" : ""}`}>
      <label>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {!rdOnly && !NonMandatory && (
            <span style={{ marginRight: 4 }}>*</span>
          )}
          <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
        </span>
      </label>
      {!rdOnly && !inline && <br />}
      {children}
    </div>
  );
}
function TextRow({
  label,
  v,
  onChange,
  rdOnly = false,
  NonMandatory = false,
  disabled = false,
  inline = false,
  errorLabel = null,
  maxLength = undefined,
}) {
  // tighter label/value gap for inline usage (if needed)
  const inlineInputStyle = inline ? { marginLeft: "-40px", width: "45%" } : {};
  const inlineTextStyle = inline ? { marginLeft: "-100px" } : {};

  return (
    <Row
      label={label}
      NonMandatory={NonMandatory}
      rdOnly={rdOnly}
      inline={inline}
    >
      {rdOnly ? (
        <b style={inlineTextStyle}>{v}</b>
      ) : (
        <input
          readOnly={rdOnly}
          value={v || ""}
          disabled={disabled}
          maxLength={maxLength}
          style={inlineInputStyle}
          onChange={(e) => onChange(rdOnly ? v : e.target.value)}
        />
      )}
      {errorLabel || null}
    </Row>
  );
}

function Overview({ title, items, setStep, form, style = false }) {
  // NOTE: Per requirement, label is driven only by ATTUID Required + Country.
  // Keep this logic inside Overview so onboarding form logic stays unchanged.
  const getItemValue = (obj, keyRegex) => {
    if (!obj || typeof obj !== "object") return undefined;
    for (const [k, v] of Object.entries(obj)) {
      if (keyRegex.test(String(k || ""))) return v;
    }
    return undefined;
  };
  const isTruthyYes = (v) => {
    if (v === true) return true;
    const s = String(v ?? "")
      .trim()
      .toLowerCase();
    return s === "yes" || s === "true" || s === "1" || s === "y";
  };
  const isUnitedStatesish = (v) => {
    const s0 = String(v ?? "")
      .trim()
      .toLowerCase();
    if (!s0) return false;
    const s = s0.replace(/[.()]/g, "").replace(/\s+/g, " ").trim();
    if (s === "us" || s === "u s" || s === "usa") return true;
    return s.includes("united states");
  };

  if (!window.__npwOverviewCtx)
    window.__npwOverviewCtx = { attuidOn: false, isUS: false };
  try {
    const attuidVal = getItemValue(items, /^\s*attuid\s*required\s*\??\s*$/i);
    const countryVal = getItemValue(items, /^\s*country\s*$/i);
    if (attuidVal !== undefined)
      window.__npwOverviewCtx.attuidOn = isTruthyYes(attuidVal);
    if (countryVal !== undefined)
      window.__npwOverviewCtx.isUS = isUnitedStatesish(countryVal);
  } catch {
    /* ignore */
  }

  const baseSecurityLabel =
    window.__npwOverviewCtx?.attuidOn && window.__npwOverviewCtx?.isUS
      ? "SSN #"
      : "Security ID #";
  const normalizeOverviewLabel = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return s;
    if (/^confirm\s+(?:security\s*id|ssn)\b/i.test(s))
      return `Confirm ${baseSecurityLabel}`;
    if (/^(?:security\s*id|ssn)\b/i.test(s)) return baseSecurityLabel;
    return s;
  };

  const normalizeOverviewValue = (rawKey, rawValue) => {
    const raw = String(rawKey || "");
    const normalizedKey = normalizeOverviewLabel(raw);

    const isSecurityKey =
      /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(raw) ||
      /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(normalizedKey);

    const isRoleKey = /\brole\b/i.test(raw) || /\brole\b/i.test(normalizedKey);
    const attuidOn = Boolean(window.__npwOverviewCtx?.attuidOn);

    if (isRoleKey) {
      if (!attuidOn) return null;
      const v = rawValue == null ? "" : String(rawValue).trim();
      if (!v || v === "-") return null;
      if (/^fte\s*nominator$/i.test(v)) return null;
      return v;
    }

    if (!isSecurityKey) {
      if (rawValue == null) return null;
      const s = String(rawValue).trim();
      if (!s || s === "-") return null;
      return s;
    }

    const s = rawValue == null ? "" : String(rawValue).trim();
    if (!s || s === "-" || /^[*]+$/.test(s)) return "********";
    return s;
  };

  // ==== CUSTOMIZABLE SETTINGS ====
  const LABEL_FONT_SIZE = 12; // Label text size
  const VALUE_FONT_SIZE = 12; // Value text size
  const GRID_GAP = 10; // Spacing between rows
  const CARD_PADDING = "20px 20px"; // Card padding
  const CARD_WIDTH = "100%"; // Card width
  const LABEL_COLOR = "#333"; // Label color
  const VALUE_COLOR = "#000"; // Value color
  const LENGTH_THRESHOLD = 30; // label+value length for wrap
  // Keys that ALWAYS show value on next line
  const FORCE_NEXT_LINE_KEYS = [
    "Email",
    "Sponsor",
    "Webphone Manager",
    "Time Zone",
  ];
  // ================================

  const cardStyle = {
    padding: CARD_PADDING,
    borderRadius: "10px",
    border: "1px solid #e3e3e3",
    background: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    width: CARD_WIDTH,
  };

  return (
    <div
      className="onb-section"
      style={style !== false ? { ...cardStyle, ...style } : cardStyle}
    >
      {/* Header Row */}
      <div
        className="onb-section-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          fontSize: LABEL_FONT_SIZE + 2,
        }}
      >
        <div>{title}</div>
        <span className="edit-pointer" onClick={() => setStep(form)}>
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
          </svg>
        </span>
      </div>

      <hr
        style={{
          border: "none",
          borderTop: "1px solid #ddd",
          margin: "0 0 12px",
        }}
      />

      {/* Main grid rows */}
      <div
        className="overview-list"
        style={{
          display: "grid",
          rowGap: GRID_GAP,
        }}
      >
        {Object.entries(items).map(([k, v]) => {
          const rawLabel = k || "";
          const label = normalizeOverviewLabel(rawLabel);
          const displayValue = normalizeOverviewValue(rawLabel, v);
          if (displayValue === null) return null;

          const valueStr = String(displayValue);
          const combinedLength = label.length + valueStr.length;

          const forceNextLine = FORCE_NEXT_LINE_KEYS.includes(label);
          const valueOnNextLine =
            forceNextLine || combinedLength > LENGTH_THRESHOLD;

          return (
            <div
              className="ov-row"
              key={rawLabel}
              style={{
                // display: "grid",
                // gridTemplateColumns: valueOnNextLine
                //     ? "1fr"
                //     : "max-content 1fr",
                display: "flex",
                flexWrap: "wrap",
                columnGap: 10,
                alignItems: "start",
              }}
            >
              {/* LABEL */}
              <span
                style={{
                  color: LABEL_COLOR,
                  fontWeight: 400, // normal
                  fontSize: LABEL_FONT_SIZE,
                  whiteSpace: "nowrap",
                }}
              >
                {label}:
              </span>

              {/* VALUE */}
              <span
                style={{
                  color: VALUE_COLOR,
                  fontWeight: 700, // bold
                  fontSize: VALUE_FONT_SIZE,
                  whiteSpace: valueOnNextLine ? "nowrap" : "normal",
                  // whiteSpace: "normal",
                  // wordBreak: "break-word",
                  // overflowWrap: "anywhere",
                  // display: valueOnNextLine ? "block" : "inline"
                }}
              >
                {valueStr}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepDot({ n, label, active, done }) {
  return (
    <div className="step-dot">
      <div className={`dot ${active ? "active" : ""} ${done ? "done" : ""}`}>
        {n}
      </div>
      <div className="dot-label">{label}</div>
    </div>
  );
}
function StepBar({ done }) {
  return <div className={`step-bar ${done ? "done" : ""}`} />;
}
